
import React, { useState, useEffect, useRef, useMemo } from 'react';
import { formatQuantity, formatUnitLabel } from '../utils/unitDisplay';
import http from '../services/httpClient';
import { postUndo } from '../utils/ccsUndo';
import SettingsService from '../services/settingsService';
import StockInService from '../services/stockInService';
import { useTranslate } from '../translations/useTranslate';
import { Check, Utensils, X } from 'lucide-react';
import revertErrorMessage from '../utils/revertErrorMessage';
import WastageRevertButton from './WastageRevertButton';
import { banquetBadgeLabel, banquetRevertNote } from '../utils/banquetWastageText';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faAngleDown, faLock, faUnlock, faSpinner } from '@fortawesome/free-solid-svg-icons';
import { StandardSearch } from '../styles/standardStyles';
import Toast from './Toast';
import ExportReportModal from './ExportReportModal';
import OutletWiseSales from './outletSale/OutletWiseSales';
import NotInMenuWorklist from './notInMenu/NotInMenuWorklist';
import { useNotInMenuCount } from './notInMenu/useNotInMenuCount';
import NoDataPlaceholder from './NoDataPlaceholder';
// PIECE GUARD §11.1 — the edit screen is a stock-write door and gets the same popups
// the submit form does, or it becomes the back door the whole feature exists to close.
import { PiecePopupFor } from './PiecePopups';
// M6 — the edit door can DEFINE a piece, not merely be told one is missing.
import PieceProfileModal from './PieceProfileModal';
// P3.4 — the one shared interpretation of what the profile modal returned.
import { applyPieceProfileResult } from '../utils/pieceProfileResult';
import './SalesReport.css';
import {
    sectionTargetId, normalizeSummary, isSalesCogsDiscount, profitSplit, weekStartMonday, contributionTarget, contributionLabel, bepBasisDate,
    donutSegments,
} from '../utils/salesReportHelpers';
import { salesPdfSections, salesEnhancedExportQuery, buildSalesPdfData } from '../utils/salesReportPdfData';
import { FixedExpenseHeader, FixedExpenseBody, filterShare } from './salesReport/FixedExpenseTab';
import { fixedShareQuery, saveReturnPoint, peekReturnPoint, clearReturnPoint } from '../utils/fixedExpenseShare';
import { buildEditorialSalesPdfBlob } from '../utils/salesReportEditorialPdf';
import { getPreparedFor } from '../utils/businessIdentity';
import {
    ResponsiveContainer,
    LineChart,
    Line,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    Legend,
    PieChart,
    Pie,
    Cell,
    Sector,
    BarChart,
    Bar
} from 'recharts';

/**
 * The sections the report screen loads independently.
 *
 * Each name is one unit of "has this arrived / did it fail". They exist because the
 * screen no longer waits for every request before rendering anything: a section still in
 * flight must be able to say "loading" rather than show its empty state, and a section
 * that failed must be able to say so rather than print a confident zero.
 */
const REPORT_SECTIONS = ['summary', 'trend', 'topProfit', 'bep', 'expenses', 'wastage', 'breakdown'];

const REPORT_SECTION_LABELS = {
    summary: 'Sales summary',
    trend: 'Sales trend',
    topProfit: 'Top profit items',
    bep: 'Break-even target',
    expenses: 'Expenses',
    wastage: 'Wastage',
    breakdown: 'Daily breakdown'
};

/**
 * How long a single report request may run before the screen stops waiting for it.
 *
 * `http.get` has no timeout of its own, so before this a request that never came back
 * left "Loading sales data..." on screen indefinitely — there was no path from a stalled
 * request to any visible outcome. 45s is deliberately generous: it is a backstop against
 * a hung request, not a performance budget. A range that legitimately needs longer than
 * this is a backend problem (see the cost-pass batching phase of the speed plan), and the
 * reader is now told that instead of being shown a spinner forever.
 */
const REPORT_REQUEST_TIMEOUT_MS = 45000;

/**
 * GET a report endpoint, with a timeout, that REJECTS on failure.
 *
 * The screen used to call `http.get(...).catch(() => 0 / [] / null)` on every endpoint,
 * so a timed-out request and a genuinely empty period produced the same thing: zeros.
 * Rejecting here forces each caller to decide what a failure means for its own section,
 * which is what makes "couldn't load" distinguishable from "nothing sold".
 */
const reportGet = (endpoint) => {
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), REPORT_REQUEST_TIMEOUT_MS);

    return http.get(endpoint, { signal: controller.signal })
        .catch((error) => {
            if (error?.name === 'AbortError') {
                const timeout = new Error(
                    `Request timed out after ${Math.round(REPORT_REQUEST_TIMEOUT_MS / 1000)}s: ${endpoint}`
                );
                // Flagged, because a timeout and a failure need different words. The
                // server keeps computing after we stop waiting, and that work warms the
                // per-item cost cache — so "try again in a moment" is literally true for
                // a timeout and would be misleading for anything else.
                timeout.isTimeout = true;
                throw timeout;
            }
            throw error;
        })
        .finally(() => clearTimeout(timer));
};

const isPlaceholderImage = (url) => {
    if (!url) return true;
    const str = String(url).toLowerCase();
    return str.includes('default.png') || str.includes('placeholder') || str === 'null' || str === 'undefined' || str === '';
};
// Pie chart colors - Blue shades palette
const PIE_COLORS = [
    '#1e40af', '#2563eb', '#3b82f6', '#60a5fa', '#93c5fd', '#bfdbfe', '#1e3a8a', '#1d4ed8', '#2563eb', '#3b82f6', '#60a5fa', '#93c5fd'
];

const RedArrowDownIcon = () => (
    <svg width="17" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" style={{ color: '#ff3b30' }}>
        <path d="M12 5v14M19 12l-7 7-7-7" />
    </svg>
);

const GreenArrowUpIcon = () => (
    <svg width="17" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" style={{ color: '#34c759' }}>
        <path d="M12 19V5M5 12l7-7 7 7" />
    </svg>
);

const ArrowLeftIcon = () => (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M19 12H5M12 19l-7-7 7-7" />
    </svg>
);

// useScrollToSection hook removed (unused)

const CalendarIcon = () => (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect>
        <line x1="16" y1="2" x2="16" y2="6"></line>
        <line x1="8" y1="2" x2="8" y2="6"></line>
        <line x1="3" y1="10" x2="21" y2="10"></line>
    </svg>
);

// Payment icons removed (payment section removed)

const ExportIcon = () => (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4M7 10l5 5 5-5M12 15V3"></path>
    </svg>
);

const ShareIcon = () => (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="18" cy="5" r="3"></circle>
        <circle cx="6" cy="12" r="3"></circle>
        <circle cx="18" cy="19" r="3"></circle>
        <line x1="8.59" y1="13.51" x2="15.42" y2="17.49"></line>
        <line x1="15.41" y1="6.51" x2="8.59" y2="10.49"></line>
    </svg>
);



// Combined Date Picker Component - Shows Week, Month, and Date selection with tabs
const DatePickerCombined = ({ isOpen, onClose, onWeekSelect, onMonthSelect }) => {
    const [pickerMode, setPickerMode] = useState('dates'); // 'dates', 'week', or 'month'
    const [dateMode, setDateMode] = useState('single'); // 'single' or 'range' for Dates tab
    const [viewMonth, setViewMonth] = useState(new Date());
    const [viewYear, setViewYear] = useState(new Date().getFullYear());
    const [selectedStartDate, setSelectedStartDate] = useState(null);
    const [selectedEndDate, setSelectedEndDate] = useState(null);
    const monthNames = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];
    const dayNames = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

    const currentToday = new Date();
    const currentYear = currentToday.getFullYear();
    const currentMonth = currentToday.getMonth();
    const isFutureMonthNav = viewMonth.getFullYear() > currentYear || 
        (viewMonth.getFullYear() === currentYear && viewMonth.getMonth() >= currentMonth);

    // Helper functions defined inside DatePickerCombined
    const formatDate = (d) => {
        const y = d.getFullYear();
        const m = String(d.getMonth() + 1).padStart(2, '0');
        const day = String(d.getDate()).padStart(2, '0');
        return `${y}-${m}-${day}`;
    };

    const getWeekStart = (date) => {
        const d = new Date(date);
        const day = d.getDay();
        const diff = day === 0 ? 6 : day - 1; // Monday as start of week
        d.setDate(d.getDate() - diff);
        d.setHours(0, 0, 0, 0);
        return d;
    };

    const getWeekEnd = (date) => {
        const d = new Date(date);
        const day = d.getDay();
        const diff = day === 0 ? 0 : 7 - day; // Sunday as end of week
        d.setDate(d.getDate() + diff);
        d.setHours(23, 59, 59, 999);
        return d;
    };

    const formatWeekDisplay = (week) => {
        const start = new Date(week.start);
        const end = new Date(week.end);
        const toDMY = (d) => `${String(d.getDate()).padStart(2,'0')}/${String(d.getMonth()+1).padStart(2,'0')}/${d.getFullYear()}`;
        return `${toDMY(start)} - ${toDMY(end)}`;
    };

    const navigateMonth = (direction) => {
        const newMonth = new Date(viewMonth);
        newMonth.setMonth(viewMonth.getMonth() + direction);
        setViewMonth(newMonth);
    };

    const navigateYear = (direction) => {
        setViewYear(prevYear => prevYear + direction);
    };

    // Week-related functions
    const getWeeksInMonth = () => {
        const year = viewMonth.getFullYear();
        const month = viewMonth.getMonth();
        const firstDay = new Date(year, month, 1);
        const lastDay = new Date(year, month + 1, 0);
        
        const weeks = [];
        let currentWeekStart = getWeekStart(firstDay);
        
        // Generate all weeks that overlap with this month
        while (currentWeekStart <= lastDay) {
            const weekEnd = getWeekEnd(currentWeekStart);
            const weekStartDate = new Date(currentWeekStart);
            const weekEndDate = new Date(weekEnd);
            
            // Only include if week overlaps with current month
            if (weekEndDate >= firstDay && weekStartDate <= lastDay) {
                weeks.push({
                    start: new Date(weekStartDate),
                    end: new Date(weekEndDate)
                });
            }
            
            // Move to next week
            currentWeekStart = new Date(currentWeekStart);
            currentWeekStart.setDate(currentWeekStart.getDate() + 7);
        }
        
        return weeks;
    };

    const handleWeekClick = (week) => {
        onWeekSelect({
            start: week.start,
            end: week.end,
            isWeekSelection: true
        });
        onClose();
    };

    const handleMonthClick = (monthIndex) => {
        onMonthSelect({ month: monthIndex, year: viewYear, isMonthSelection: true });
        onClose();
    };

    // Date selection functions - UPDATED: Mode-based selection (single or range)
    const handleDateClick = (date) => {
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        
        if (date > today) return; // Prevent future date selection
        
        // SINGLE-DATE MODE: Instant report on click
        if (dateMode === 'single') {
            const dateStart = new Date(date);
            dateStart.setHours(0, 0, 0, 0);
            const dateEnd = new Date(date);
            dateEnd.setHours(23, 59, 59, 999);
            
            onWeekSelect({
                start: dateStart,
                end: dateEnd,
                isWeekSelection: false,
                isCustomRange: true,
                isSingleDay: true
            });
            onClose();
            return;
        }
        
        // RANGE MODE: Select start and end dates
        if (dateMode === 'range') {
            if (!selectedStartDate || (selectedStartDate && selectedEndDate)) {
                // Start new range selection
                setSelectedStartDate(date);
                setSelectedEndDate(null);
            } else if (selectedStartDate && !selectedEndDate) {
                // Complete the range
                if (date < selectedStartDate) {
                    setSelectedEndDate(selectedStartDate);
                    setSelectedStartDate(date);
                } else {
                    setSelectedEndDate(date);
                }
            }
        }
    };

    const applyDateRange = () => {
        if (selectedStartDate && selectedEndDate) {
            onWeekSelect({
                start: selectedStartDate,
                end: new Date(selectedEndDate.getTime() + 86400000 - 1), // End of day
                isWeekSelection: false,
                isCustomRange: true
            });
            setSelectedStartDate(null);
            setSelectedEndDate(null);
            onClose();
        }
    };

    const getDaysInMonth = () => {
        const year = viewMonth.getFullYear();
        const month = viewMonth.getMonth();
        const firstDay = new Date(year, month, 1);
        const lastDay = new Date(year, month + 1, 0);
        const daysInMonth = lastDay.getDate();
        const startingDayOfWeek = firstDay.getDay();
        const adjustedStart = startingDayOfWeek === 0 ? 6 : startingDayOfWeek - 1; // Monday = 0
        
        const days = [];
        
        // Add empty cells for days before the first day of month
        for (let i = 0; i < adjustedStart; i++) {
            days.push(null);
        }
        
        // Add actual days
        for (let day = 1; day <= daysInMonth; day++) {
            days.push(new Date(year, month, day));
        }
        
        return days;
    };

    const isDateInRange = (date) => {
        if (!selectedStartDate || !date) return false;
        if (!selectedEndDate) return date.getTime() === selectedStartDate.getTime();
        return date >= selectedStartDate && date <= selectedEndDate;
    };

    const isDateRangeStart = (date) => {
        return selectedStartDate && date && date.getTime() === selectedStartDate.getTime();
    };

    const isDateRangeEnd = (date) => {
        return selectedEndDate && date && date.getTime() === selectedEndDate.getTime();
    };

    const renderWeekGrid = () => {
        const today = new Date();
        const thisWeekStart = getWeekStart(today);
        const weeks = getWeeksInMonth();
        
        return (
            <div style={{
                display: 'flex',
                flexDirection: 'column',
                gap: '10px',
                marginTop: '15px',
                maxHeight: '400px',
                overflowY: 'auto'
            }}>
                {weeks.map((week, index) => {
                    const weekStartTime = week.start.getTime();
                    const thisWeekTime = thisWeekStart.getTime();
                    const isThisWeek = weekStartTime === thisWeekTime;
                    const isFutureWeek = week.start > today;
                    
                    const weekLabel = formatWeekDisplay(week);
                    
                    return (
                        <div
                            key={index}
                            className={`week-cell ${isThisWeek ? 'current-week' : ''} ${isFutureWeek ? 'disabled' : ''}`}
                            onClick={!isFutureWeek ? () => handleWeekClick(week) : undefined}
                            style={{
                                padding: '15px 20px',
                                textAlign: 'center',
                                cursor: isFutureWeek ? 'not-allowed' : 'pointer',
                                borderRadius: '8px',
                                border: isThisWeek ? '2px solid #000' : '1px solid #e0e0e0',
                                backgroundColor: isFutureWeek ? '#f5f5f5' : '#fff',
                                color: isFutureWeek ? '#ccc' : '#333',
                                fontWeight: isThisWeek ? 'bold' : '500',
                                fontSize: '14px',
                                transition: 'all 0.2s',
                                opacity: isFutureWeek ? 0.5 : 1,
                                pointerEvents: isFutureWeek ? 'none' : 'auto',
                                boxShadow: 'none'
                            }}
                            onMouseEnter={(e) => {
                                if (!isFutureWeek && !isThisWeek) {
                                    e.target.style.backgroundColor = '#f5f5f5';
                                    e.target.style.transform = 'translateY(-2px)';
                                    e.target.style.boxShadow = '0 4px 8px rgba(0,0,0,0.15)';
                                }
                            }}
                            onMouseLeave={(e) => {
                                if (!isFutureWeek && !isThisWeek) {
                                    e.target.style.backgroundColor = '#fff';
                                    e.target.style.transform = 'translateY(0)';
                                    e.target.style.boxShadow = 'none';
                                }
                            }}
                        >
                            {isThisWeek && '✓ '}
                            {weekLabel}
                        </div>
                    );
                })}
            </div>
        );
    };

    const renderMonthGrid = () => {
        const currentMonth = new Date().getMonth();
        const currentYear = new Date().getFullYear();
        const months = [];

        for (let i = 0; i < 12; i++) {
            const isCurrentMonth = (i === currentMonth && viewYear === currentYear);
            const isFutureMonth = (viewYear > currentYear) || (viewYear === currentYear && i > currentMonth);
            
            months.push(
                <div
                    key={i}
                    className={`month-cell ${isCurrentMonth ? 'current' : ''} ${isFutureMonth ? 'future' : ''}`}
                    onClick={() => !isFutureMonth && handleMonthClick(i)}
                    style={{
                        padding: '20px',
                        textAlign: 'center',
                        cursor: isFutureMonth ? 'not-allowed' : 'pointer',
                        borderRadius: '8px',
                        border: isCurrentMonth ? '2px solid #000' : '1px solid #e0e0e0',
                        backgroundColor: isFutureMonth ? '#f5f5f5' : '#fff',
                        color: isFutureMonth ? '#ccc' : '#333',
                        fontWeight: isCurrentMonth ? 'bold' : 'normal',
                        transition: 'all 0.2s',
                        opacity: isFutureMonth ? 0.5 : 1
                    }}
                    onMouseEnter={(e) => {
                        if (!isFutureMonth && !isCurrentMonth) {
                            e.target.style.backgroundColor = '#f5f5f5';
                            e.target.style.transform = 'scale(1.05)';
                        }
                    }}
                    onMouseLeave={(e) => {
                        if (!isFutureMonth && !isCurrentMonth) {
                            e.target.style.backgroundColor = '#fff';
                            e.target.style.transform = 'scale(1)';
                        }
                    }}
                >
                    {monthNames[i].substring(0, 3)}
                </div>
            );
        }

        return (
            <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(3, 1fr)',
                gap: '12px',
                marginTop: '20px'
            }}>
                {months}
            </div>
        );
    };

    const renderDateCalendar = () => {
        const days = getDaysInMonth();
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        
        return (
            <div style={{ marginTop: '15px' }}>
                {/* Month/Year header with Range toggle button */}
                <div style={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '12px',
                    marginBottom: '15px',
                    padding: '0 5px'
                }}>
                    <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: '15px'
                    }}>
                        <button onClick={() => {
                            const newDate = new Date(viewMonth);
                            newDate.setMonth(newDate.getMonth() - 1);
                            setViewMonth(newDate);
                        }} style={{
                            padding: '6px 12px',
                            fontSize: '16px',
                            fontWeight: 'bold',
                            border: 'none',
                            background: '#f5f5f5',
                            borderRadius: '6px',
                            cursor: 'pointer'
                        }}>‹</button>
                        <span style={{ 
                            fontWeight: '600', 
                            fontSize: '15px',
                            minWidth: '140px',
                            textAlign: 'center'
                        }}>
                            {monthNames[viewMonth.getMonth()]} {viewMonth.getFullYear()}
                        </span>
                        <button onClick={() => {
                            const newDate = new Date(viewMonth);
                            newDate.setMonth(newDate.getMonth() + 1);
                            setViewMonth(newDate);
                        }} 
                        disabled={isFutureMonthNav}
                        style={{
                            padding: '6px 12px',
                            fontSize: '16px',
                            fontWeight: 'bold',
                            border: 'none',
                            background: '#f5f5f5',
                            borderRadius: '6px',
                            cursor: isFutureMonthNav ? 'not-allowed' : 'pointer',
                            color: isFutureMonthNav ? '#ccc' : '#333',
                            opacity: isFutureMonthNav ? 0.5 : 1
                        }}>›</button>
                    </div>
                    
                    {/* Range Mode Toggle Button */}
                    <button 
                        onClick={() => {
                            setDateMode(dateMode === 'single' ? 'range' : 'single');
                            // Reset selection when switching modes
                            setSelectedStartDate(null);
                            setSelectedEndDate(null);
                        }}
                        style={{
                            padding: '8px 16px',
                            fontSize: '13px',
                            fontWeight: '600',
                            border: dateMode === 'range' ? '2px solid #000' : '2px solid #e0e0e0',
                            background: dateMode === 'range' ? '#f5f5f5' : '#fff',
                            color: dateMode === 'range' ? '#000' : '#666',
                            borderRadius: '8px',
                            cursor: 'pointer',
                            transition: 'all 0.2s',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: '6px',
                            whiteSpace: 'nowrap',
                            width: '100%'
                        }}
                        onMouseEnter={(e) => {
                            if (dateMode === 'single') {
                                e.target.style.borderColor = '#000';
                                e.target.style.background = '#f9f9f9';
                            }
                        }}
                        onMouseLeave={(e) => {
                            if (dateMode === 'single') {
                                e.target.style.borderColor = '#e0e0e0';
                                e.target.style.background = '#fff';
                            }
                        }}
                    >
                        {dateMode === 'range' ? 'Range Mode' : 'Range Mode'}
                    </button>
                </div>

                {/* Day names header */}
                <div style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(7, 1fr)',
                    gap: '5px',
                    marginBottom: '10px',
                    padding: '10px 0',
                    borderBottom: '1px solid #e0e0e0'
                }}>
                    {dayNames.map(day => (
                        <div key={day} style={{
                            textAlign: 'center',
                            fontWeight: 'bold',
                            fontSize: '12px',
                            color: '#666'
                        }}>
                            {day}
                        </div>
                    ))}
                </div>
                
                {/* Calendar days */}
                <div style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(7, 1fr)',
                    gap: '5px',
                    scrollbarWidth: 'none',
                    msOverflowStyle: 'none'
                }} className="calendar-days-grid">
                    {days.map((date, index) => {
                        if (!date) {
                            return <div key={`empty-${index}`} />;
                        }
                        
                        const isToday = date.getTime() === today.getTime();
                        // In range mode we suppress the automatic today highlight until user selects a start
                        const highlightToday = dateMode === 'single' && isToday;
                        const isFuture = date > today;
                        const inRange = isDateInRange(date);
                        const isStart = isDateRangeStart(date);
                        const isEnd = isDateRangeEnd(date);
                        
                        return (
                            <div
                                key={index}
                                onClick={() => handleDateClick(date)}
                                style={{
                                    padding: '10px',
                                    textAlign: 'center',
                                    cursor: isFuture ? 'not-allowed' : 'pointer',
                                    borderRadius: '4px',
                                    border: inRange ? '2px solid #000' : highlightToday ? '2px solid #000' : 'none',
                                    backgroundColor: isFuture ? '#f9f9f9' : '#fff',
                                    color: isFuture ? '#ccc' : '#333',
                                    fontWeight: (highlightToday || inRange) ? 'bold' : 'normal',
                                    fontSize: '13px',
                                    opacity: isFuture ? 0.4 : 1,
                                    pointerEvents: isFuture ? 'none' : 'auto',
                                    transition: 'all 0.2s',
                                    position: 'relative'
                                }}
                                onMouseEnter={(e) => {
                                    if (!isFuture && !inRange) {
                                        e.target.style.backgroundColor = '#f5f5f5';
                                        e.target.style.transform = 'scale(1.1)';
                                    }
                                }}
                                onMouseLeave={(e) => {
                                    if (!isFuture && !inRange) {
                                        e.target.style.backgroundColor = '#fff';
                                        e.target.style.transform = 'scale(1)';
                                    }
                                }}
                            >
                                {date.getDate()}
                            </div>
                        );
                    })}
                </div>
                
                {/* Apply button for date range */}
                {selectedStartDate && selectedEndDate && (
                    <div style={{ marginTop: '15px', textAlign: 'center' }}>
                        <button
                            onClick={applyDateRange}
                            style={{
                                padding: '10px 30px',
                                backgroundColor: '#000',
                                color: '#fff',
                                border: 'none',
                                borderRadius: '8px',
                                fontSize: '14px',
                                fontWeight: 'bold',
                                cursor: 'pointer',
                                boxShadow: '0 2px 4px rgba(0,0,0,0.2)'
                            }}
                        >
                            Apply Date Range
                        </button>
                    </div>
                )}
            </div>
        );
    };

    if (!isOpen) return null;

    return (
        <div className="sales-date-picker-overlay" onClick={onClose}>
            <div className="sales-date-picker-popup" onClick={e => e.stopPropagation()}>
                {/* Tabs for Dates/Week/Month Selection */}
                <div style={{
                    display: 'flex',
                    borderBottom: '2px solid #e0e0e0',
                    marginBottom: '15px'
                }}>
                    <button
                        onClick={() => setPickerMode('dates')}
                        style={{
                            flex: 1,
                            padding: '12px',
                            border: 'none',
                            background: 'transparent',
                            color: pickerMode === 'dates' ? '#000' : '#666',
                            fontWeight: pickerMode === 'dates' ? 'bold' : 'normal',
                            fontSize: '1rem',
                            cursor: 'pointer',
                            borderBottom: pickerMode === 'dates' ? '3px solid #000' : 'none',
                            marginBottom: '-2px',
                            transition: 'all 0.2s'
                        }}
                    >
                        Dates
                    </button>
                    <button
                        onClick={() => setPickerMode('week')}
                        style={{
                            flex: 1,
                            padding: '12px',
                            border: 'none',
                            background: 'transparent',
                            color: pickerMode === 'week' ? '#000' : '#666',
                            fontWeight: pickerMode === 'week' ? 'bold' : 'normal',
                            fontSize: '1rem',
                            cursor: 'pointer',
                            borderBottom: pickerMode === 'week' ? '3px solid #000' : 'none',
                            marginBottom: '-2px',
                            transition: 'all 0.2s'
                        }}
                    >
                        Week
                    </button>
                    <button
                        onClick={() => setPickerMode('month')}
                        style={{
                            flex: 1,
                            padding: '12px',
                            border: 'none',
                            background: 'transparent',
                            color: pickerMode === 'month' ? '#000' : '#666',
                            fontWeight: pickerMode === 'month' ? 'bold' : 'normal',
                            fontSize: '1rem',
                            cursor: 'pointer',
                            borderBottom: pickerMode === 'month' ? '3px solid #000' : 'none',
                            marginBottom: '-2px',
                            transition: 'all 0.2s'
                        }}
                    >
                        Month
                    </button>
                </div>

                {/* Header - changes based on mode */}
                {pickerMode === 'month' && (
                    <div className="calendar-header">
                        <button onClick={() => navigateYear(-1)} style={{
                            padding: '8px 12px',
                            fontSize: '18px',
                            fontWeight: 'bold',
                            border: 'none',
                            background: '#f5f5f5',
                            borderRadius: '6px',
                            cursor: 'pointer'
                        }}>‹</button>
                        <h3 style={{ margin: 0, fontSize: '16px', fontWeight: '600' }}>
                            {viewYear}
                        </h3>
                        <button 
                            onClick={() => navigateYear(1)} 
                            disabled={viewYear >= currentYear}
                            style={{
                                padding: '8px 12px',
                                fontSize: '18px',
                                fontWeight: 'bold',
                                border: 'none',
                                background: '#f5f5f5',
                                borderRadius: '6px',
                                cursor: viewYear >= currentYear ? 'not-allowed' : 'pointer',
                                color: viewYear >= currentYear ? '#ccc' : '#333',
                                opacity: viewYear >= currentYear ? 0.5 : 1
                            }}>›</button>
                    </div>
                )}
                
                {pickerMode === 'week' && (
                    <div className="calendar-header">
                        <button onClick={() => navigateMonth(-1)} style={{
                            padding: '8px 12px',
                            fontSize: '18px',
                            fontWeight: 'bold',
                            border: 'none',
                            background: '#f5f5f5',
                            borderRadius: '6px',
                            cursor: 'pointer'
                        }}>‹</button>
                        <h3 style={{ margin: 0, fontSize: '16px', fontWeight: '600' }}>
                            {monthNames[viewMonth.getMonth()]} {viewMonth.getFullYear()}
                        </h3>
                        <button 
                            onClick={() => navigateMonth(1)} 
                            disabled={isFutureMonthNav}
                            style={{
                                padding: '8px 12px',
                                fontSize: '18px',
                                fontWeight: 'bold',
                                border: 'none',
                                background: '#f5f5f5',
                                borderRadius: '6px',
                                cursor: isFutureMonthNav ? 'not-allowed' : 'pointer',
                                color: isFutureMonthNav ? '#ccc' : '#333',
                                opacity: isFutureMonthNav ? 0.5 : 1
                            }}>›</button>
                    </div>
                )}

                {/* Render grid based on mode */}
                {pickerMode === 'week' ? renderWeekGrid() : 
                 pickerMode === 'month' ? renderMonthGrid() :
                 renderDateCalendar()}
            </div>
        </div>
    );
};

// Week Navigator Component - Shows Previous/This/Next Week buttons
const WeekNavigator = ({ selectedWeek, navigateWeek, goToThisWeek, getWeekStart, formatWeekDisplay }) => {
    if (!selectedWeek || !selectedWeek.isWeekSelection) return null;

    const today = new Date();
    const thisWeekStart = getWeekStart(today);
    const selectedWeekStart = new Date(selectedWeek.start);
    const isThisWeek = selectedWeekStart.getTime() === thisWeekStart.getTime();
    const isFutureWeek = selectedWeekStart > thisWeekStart;

    return (
        <div className="week-navigator-container" style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            gap: '15px',
            margin: '20px 0'
        }}>
            <button
                onClick={() => navigateWeek(-1)}
                style={{
                    padding: '10px 20px',
                    backgroundColor: '#fff',
                    border: '1px solid #ddd',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    fontSize: '14px',
                    fontWeight: '500',
                    boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
                }}
            >
                ‹ Previous Week
            </button>
            
            <button
                onClick={goToThisWeek}
                disabled={isThisWeek}
                style={{
                    padding: '10px 25px',
                    backgroundColor: isThisWeek ? '#4CAF50' : '#fff',
                    color: isThisWeek ? '#fff' : '#333',
                    border: isThisWeek ? 'none' : '1px solid #ddd',
                    borderRadius: '8px',
                    cursor: isThisWeek ? 'default' : 'pointer',
                    fontSize: '14px',
                    fontWeight: '600',
                    boxShadow: isThisWeek ? '0 2px 8px rgba(76, 175, 80, 0.3)' : '0 1px 3px rgba(0,0,0,0.1)',
                    opacity: isThisWeek ? 0.9 : 1
                }}
            >
                {isThisWeek ? '✓ This Week' : 'This Week'}
            </button>
            
            <button
                onClick={() => navigateWeek(1)}
                disabled={isFutureWeek}
                style={{
                    padding: '10px 20px',
                    backgroundColor: isFutureWeek ? '#f5f5f5' : '#fff',
                    border: '1px solid #ddd',
                    borderRadius: '8px',
                    cursor: isFutureWeek ? 'not-allowed' : 'pointer',
                    fontSize: '14px',
                    fontWeight: '500',
                    boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
                    opacity: isFutureWeek ? 0.5 : 1
                }}
            >
                Next Week ›
            </button>
        </div>
    );
};



// Helper to format currency numbers in Indian comma notation while preserving decimals
const formatIndian = (val) => {
    if (val === undefined || val === null || isNaN(val)) return '0';
    const num = Number(val);
    const str = String(val);
    const dotIndex = str.indexOf('.');
    let decimals = 0;
    if (dotIndex !== -1) {
        decimals = str.length - dotIndex - 1;
    }
    const maxDec = Math.min(3, Math.max(0, decimals));
    return num.toLocaleString('en-IN', {
        minimumFractionDigits: maxDec,
        maximumFractionDigits: maxDec
    });
};

// Helper to convert values to words in Indian notation
const numberToIndianWords = (val) => {
    if (val === undefined || val === null || isNaN(val)) return '';
    const num = Number(val);
    if (num === 0) return 'Zero';

    const isNegative = num < 0;
    const absNum = Math.abs(num);
    
    // Split into integer and decimal parts
    const parts = String(absNum).split('.');
    const integerPart = Math.floor(absNum);
    const decimalStr = parts[1];

    // Convert integer part
    let temp = integerPart;
    const crores = Math.floor(temp / 10000000);
    temp %= 10000000;
    const lakhs = Math.floor(temp / 100000);
    temp %= 100000;
    const thousands = Math.floor(temp / 1000);
    temp %= 1000;
    const hundreds = Math.floor(temp / 100);
    const remaining = temp % 100;

    const wordParts = [];
    if (crores > 0) {
        wordParts.push(`${crores} Crore`);
    }
    if (lakhs > 0) {
        wordParts.push(`${lakhs} Lakh${lakhs > 1 ? 's' : ''}`);
    }
    if (thousands > 0) {
        wordParts.push(`${thousands} Thousand`);
    }
    
    if (hundreds > 0) {
        if (remaining > 0) {
            wordParts.push(`${hundreds} Hundred ${remaining}`);
        } else {
            wordParts.push(`${hundreds} Hundred`);
        }
    } else if (remaining > 0 || (wordParts.length === 0 && integerPart > 0)) {
        wordParts.push(`${remaining}`);
    }

    let result = wordParts.join(', ');
    if (!result && integerPart === 0) {
        result = '0';
    }

    if (decimalStr && Number(decimalStr) > 0) {
        result += ` point ${decimalStr}`;
    }

    return (isNegative ? 'Minus ' : '') + result;
};

// ✅ keep all your icons and DatePicker component as-is (no change from your code)

const SalesReport = ({ goTo }) => {
    // Get language and translation function from custom translation hook
    const { language, t } = useTranslate();
    
    const [hoverIndex, setHoverIndex] = useState(null);
    const [activeSection, setActiveSection] = useState(null);
    const reportRef = useRef(null);
    const [showExportToast, setShowExportToast] = useState(false);
    const [showExportModal, setShowExportModal] = useState(false);
    const [showExportPicker, setShowExportPicker] = useState(false);
    const [windowWidth, setWindowWidth] = useState(typeof window !== 'undefined' ? window.innerWidth : 1024);

    // Responsive hook
    useEffect(() => {
        const handleResize = () => {
            setWindowWidth(window.innerWidth);
        };

        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    // Effect to handle scrolling to section and highlighting
    useEffect(() => {
        // The Reports cards name a section; sectionTargetId maps it to an element
        // that exists on this page (most of the names never did).
        const section = sectionTargetId(sessionStorage.getItem('highlightSection'));
        if (section) {
            setActiveSection(section);

            // Function to attempt scrolling
            const attemptScroll = (retries = 0) => {
                const element = document.getElementById(section);
                if (element) {
                    const executeScroll = () => {
                        const headerOffset = 100;
                        const elementRect = element.getBoundingClientRect();
                        const absoluteElementTop = elementRect.top + window.pageYOffset;

                        // Scroll to element
                        window.scrollTo({
                            top: Math.max(0, absoluteElementTop - headerOffset),
                            behavior: 'smooth'
                        });

                        // Visual feedback
                        element.style.transition = 'all 0.3s ease';
                        element.style.boxShadow = '0 0 20px rgba(66, 153, 225, 0.6)';

                        setTimeout(() => {
                            element.style.boxShadow = 'none';
                            // The highlight is a moment, not a state: left set, it kept a
                            // permanent border on the section after the user moved on.
                            setActiveSection(null);
                        }, 1500);
                    };

                    // Ensure the element is actually in the document
                    if (element.offsetParent !== null) {
                        executeScroll();
                        return true;
                    }
                }

                // If element not found or not in document and we haven't exceeded retries
                if (retries < 5) {
                    setTimeout(() => attemptScroll(retries + 1), 300);
                    return false;
                }
                return false;
            };

            // Start the first attempt after a short delay
            setTimeout(() => attemptScroll(), 300);

            // Clear the section from sessionStorage after handling
            sessionStorage.removeItem('highlightSection');
        }
    }, []);

    // Summary comparison data
    const [summaryComparison, setSummaryComparison] = useState(null);
    // Pie chart data for today or selected date
    // `pieData` / `pieDate` were removed with the /reports/items-by-date call that fed
    // them: no render site in this file ever read either one.
    // Back from the Net Profit Analyzer (Fixed Expense tab -> Open in Expenses): the period, the
    // Expense Details window and the tab the person left from. Read once, cleared on mount.
    const [returnPoint] = useState(() => peekReturnPoint());
    const [selectedPeriod, setSelectedPeriod] = useState(() => (returnPoint ? returnPoint.selectedPeriod : 'Today'));
    const [periodOpen, setPeriodOpen] = useState(false);
    const periodContainerRef = useRef(null);
    const [showDatePicker, setShowDatePicker] = useState(false);
    const [selectedDate, setSelectedDate] = useState(() => (returnPoint ? returnPoint.selectedDate : null));
    const [selectedWeek, setSelectedWeek] = useState(() => (returnPoint ? returnPoint.selectedWeek : null)); // For week navigation
    const [displayText, setDisplayText] = useState(() => (returnPoint ? returnPoint.displayText : 'Today'));
    // `isLoading` gates the WHOLE page, so it must track only the headline summary.
    // It used to be held until all eleven parallel report calls had settled, which is
    // why a multi-day Range Mode selection showed nothing but "Loading sales data..."
    // for minutes: the screen was hostage to the slowest call on the screen.
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    // A failure that leaves NOTHING renderable (no business context). Distinct from
    // `error`, which is a failed summary request — that one still leaves a usable report,
    // so it must not blank the sections that loaded.
    const [fatalError, setFatalError] = useState(false);
    // Sections that are still in flight after the summary has landed. A section listed
    // here renders a quiet "loading" state instead of its empty state — otherwise
    // progressive rendering would tell the reader "no data" about data still on its way.
    const [pendingSections, setPendingSections] = useState([]);
    // Sections whose request failed. Every one of these used to be swallowed by a
    // `.catch(() => 0 / [] / null)`, which printed a confident ₹0 over a request that
    // had actually timed out. Slowness and failure must read as slowness and failure.
    const [sectionErrors, setSectionErrors] = useState([]);
    const fetchRequestIdRef = useRef(0);

    const EMPTY_EXPENSES = {
        total_expenses: 0,
        total_records: 0,
        manual_entries: 0,
        scanned_entries: 0,
        records_with_bills: 0
    };
    const EMPTY_WASTAGE_EXPENSES = {
        total_expenses: 0,
        total_records: 0
    };

    const resolveBusinessId = () => {
        const fromStorage = Number(localStorage.getItem('businessId'));

        try {
            const userStr = localStorage.getItem('user');
            if (!userStr) return Number.isFinite(fromStorage) && fromStorage > 0 ? fromStorage : null;

            const userObj = JSON.parse(userStr);
            const fromUser = Number(userObj?.businessId || userObj?.business_id || userObj?.businessid);

            if (Number.isFinite(fromUser) && fromUser > 0) {
                if (String(fromStorage) !== String(fromUser)) {
                    localStorage.setItem('businessId', String(fromUser));
                }
                return fromUser;
            }
        } catch (e) {
            console.warn('Failed to resolve business id from user payload:', e);
        }

        return Number.isFinite(fromStorage) && fromStorage > 0 ? fromStorage : null;
    };

    // Helper functions for week navigation
    const formatDate = (d) => {
        const y = d.getFullYear();
        const m = String(d.getMonth() + 1).padStart(2, '0');
        const day = String(d.getDate()).padStart(2, '0');
        return `${y}-${m}-${day}`;
    };

    const getWeekStart = (date) => {
        const d = new Date(date);
        const day = d.getDay();
        const diff = day === 0 ? 6 : day - 1; // Monday as week start
        d.setDate(d.getDate() - diff);
        d.setHours(0, 0, 0, 0);
        return d;
    };

    const getWeekEnd = (date) => {
        const d = getWeekStart(date);
        d.setDate(d.getDate() + 6); // Sunday as week end
        d.setHours(23, 59, 59, 999);
        return d;
    };

    const navigateWeek = (direction) => {
        const baseDate = selectedWeek ? new Date(selectedWeek.start) : new Date();
        const newDate = new Date(baseDate);
        newDate.setDate(baseDate.getDate() + (direction * 7));
        
        const weekStart = getWeekStart(newDate);
        const weekEnd = getWeekEnd(newDate);
        
        setSelectedWeek({
            start: weekStart,
            end: weekEnd,
            isWeekSelection: true
        });
        setSelectedPeriod('This Week'); // Update period
        setSelectedDate(null); // Clear month selection
    };

    const goToThisWeek = () => {
        const today = new Date();
        const weekStart = getWeekStart(today);
        const weekEnd = getWeekEnd(today);
        
        setSelectedWeek({
            start: weekStart,
            end: weekEnd,
            isWeekSelection: true
        });
        setSelectedPeriod('This Week');
        setSelectedDate(null);
    };

    const formatWeekDisplay = (week) => {
        if (!week) return '';
        const startDate = new Date(week.start);
        const endDate = new Date(week.end);
        const toDMY = (d) => `${String(d.getDate()).padStart(2,'0')}/${String(d.getMonth()+1).padStart(2,'0')}/${d.getFullYear()}`;
        return `${toDMY(startDate)} - ${toDMY(endDate)}`;
    };

    const getSelectedDateRange = () => {
        if (selectedWeek && selectedWeek.isWeekSelection) {
            return { startDate: formatDate(selectedWeek.start), endDate: formatDate(selectedWeek.end) };
        }
        if (selectedWeek && selectedWeek.isCustomRange) {
            return { startDate: formatDate(selectedWeek.start), endDate: formatDate(selectedWeek.end) };
        }
        if (selectedDate && selectedDate.isMonthSelection) {
            const firstDay = new Date(selectedDate.year, selectedDate.month, 1);
            const lastDay = new Date(selectedDate.year, selectedDate.month + 1, 0);
            return { startDate: formatDate(firstDay), endDate: formatDate(lastDay) };
        }
        if (selectedDate) {
            const dateStr = formatDate(selectedDate);
            return { startDate: dateStr, endDate: dateStr };
        }
        if (selectedPeriod === 'Today') {
            const today = new Date();
            const dateStr = formatDate(today);
            return { startDate: dateStr, endDate: dateStr };
        }
        if (selectedPeriod === 'This Week') {
            const weekStart = getWeekStart(new Date());
            const weekEnd = getWeekEnd(new Date());
            return { startDate: formatDate(weekStart), endDate: formatDate(weekEnd) };
        }
        if (selectedPeriod === 'This Month') {
            const now = new Date();
            const firstDay = new Date(now.getFullYear(), now.getMonth(), 1);
            const lastDay = new Date(now.getFullYear(), now.getMonth() + 1, 0);
            return { startDate: formatDate(firstDay), endDate: formatDate(lastDay) };
        }
        if (selectedPeriod === 'This Year') {
            const now = new Date();
            const firstDay = new Date(now.getFullYear(), 0, 1);
            const lastDay = new Date(now.getFullYear(), 11, 31);
            return { startDate: formatDate(firstDay), endDate: formatDate(lastDay) };
        }
        const today = new Date();
        const dateStr = formatDate(today);
        return { startDate: dateStr, endDate: dateStr };
    };

    const buildStockOutGroups = (records) => {
        const dateGroups = new Map();

        records.forEach((record) => {
            const dateValue = record.activity_date || record.deducted_date || null;
            let rawDate = null;

            if (dateValue) {
                rawDate = new Date(dateValue);
                if (Number.isNaN(rawDate.getTime())) {
                    rawDate = null;
                }
            }

            const dateKey = rawDate ? formatDate(rawDate) : 'unknown';
            const dateLabel = rawDate
                ? rawDate.toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })
                : 'Unknown Date';

            if (!dateGroups.has(dateKey)) {
                dateGroups.set(dateKey, { dateKey, dateLabel, rows: [] });
            }

            const dateGroup = dateGroups.get(dateKey);
            // Keep individual records instead of aggregating
            // Negative stock generated in wastage is unpriceable — mirror the Gross
            // Profit dash ("-") instead of showing a rupee figure for it.
            const cogsUnavailable = !!record.cogs_unavailable;
            dateGroup.rows.push({
                itemName: record.item_name || 'Unknown Item',
                reason: record.waste_reason || record.reason_type || 'Wastage',
                qty: Number(record.quantity || 0),
                amount: cogsUnavailable ? null : Number(record.actual_cost_impact || record.estimated_cost_impact || 0),
                cogsUnavailable,
                staffName: record.recorded_by_name || 'Unknown Staff',
                notes: record.notes || null,
                stock_out_id: record.stock_out_id,
                // The server's revert verdict: WastageRevertButton renders a lock from it.
                revert: record.revert || null,
                banquetEventId: record.banquet_event_id || null,
                functionName: record.function_name || null,
                eventDate: record.event_date || null
            });
        });

        return Array.from(dateGroups.values())
            .sort((a, b) => (a.dateKey < b.dateKey ? 1 : -1))
            .map((group) => ({
                ...group,
                rows: group.rows.sort((a, b) =>
                    a.itemName.localeCompare(b.itemName, undefined, { sensitivity: 'base' })
                )
            }));
    };

    const normalizeSearchText = (value) => String(value || '').toLowerCase().replace(/\s+/g, ' ').trim();

    const toCamelCaseLabel = (value) => {
        if (!value) return '';
        return String(value)
            .toLowerCase()
            .replace(/[^a-z0-9]+/g, ' ')
            .trim()
            .split(' ')
            .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
            .join(' ');
    };

    const formatQuantity = (value) => {
        const num = Number(value || 0);
        if (Number.isNaN(num)) return '0';
        const fixed = num.toFixed(2);
        return fixed.replace(/\.00$/, '').replace(/(\.\d)0$/, '$1');
    };

    const formatPlates = (value) => `${formatQuantity(value)} plates`;

    const normalizeBillDisplay = (value) => {
        const num = Number(value || 0);
        const rounded = Math.round(num);
        // Data can carry 1-2 paise drift from previous prorated storage.
        // Snap only near-integer values to keep sheet parity.
        if (Math.abs(num - rounded) <= 0.02) {
            return rounded;
        }
        return num;
    };

    const getRawBillPrice = (item) => {
        const netAmount = Number(item?.line_item_amount || 0);
        const lineDiscount = Number(item?.discount_amount || 0);
        const billedBeforeDiscount = netAmount + lineDiscount;
        if (billedBeforeDiscount > 0) return billedBeforeDiscount;
        return Number(item?.actual_line_amount || netAmount || 0);
    };

    const getOrderTotals = (order) => {
        const allItems = Array.isArray(order?.items) ? order.items : [];
        // Unmapped lines ride in `items` so they can be DISPLAYED, and they are still kept
        // out of every COST figure: their COGS is unknown (the API sends null, not 0), so
        // folding them into gross profit would book their revenue as pure margin.
        const items = allItems.filter((item) => !item?.is_unmapped);
        const subtotal = items.reduce((sum, item) => sum + getRawBillPrice(item), 0);
        const discount = items.reduce((sum, item) => sum + Number(item?.discount_amount || 0), 0);
        // Menu items only. This is the figure every COST and MARGIN calculation must use,
        // because it is the only revenue whose cost is known.
        const total = Math.max(subtotal - discount, 0);
        const unmappedItems = allItems.filter((item) => item?.is_unmapped);
        const unmappedAmount = unmappedItems.reduce((sum, item) => sum + getRawBillPrice(item), 0);

        // What the customer was actually billed: menu items PLUS the lines that never
        // resolved to a menu item. The detail modal shows every one of those lines, so a
        // "Total" beneath them that silently omitted them was reporting a number the rows
        // above it visibly contradicted.
        //
        // Kept as a SEPARATE field rather than folded into `total`: the two answer different
        // questions, and only `total` may ever reach a margin calculation.
        const totalWithUnmapped = total + unmappedAmount;

        return {
            subtotal,
            discount,
            total,
            totalWithUnmapped,
            countedItems: items.length,
            unmappedItems,
            unmappedAmount
        };
    };

    const getSalesOrderDateKey = (order) => {
        const rawDate = String(order?.transaction_date || order?.timestamp || '').trim();
        const isoMatch = rawDate.match(/^(\d{4}-\d{2}-\d{2})/);
        if (isoMatch) return isoMatch[1];

        const parsed = new Date(rawDate);
        if (!Number.isNaN(parsed.getTime())) return parsed.toLocaleDateString('en-CA');
        return '';
    };

    const getSalesOrderDateLabel = (order) => {
        const dateKey = getSalesOrderDateKey(order);
        if (!dateKey) return '-';

        const parsed = new Date(`${dateKey}T00:00:00`);
        if (Number.isNaN(parsed.getTime())) return dateKey;
        return parsed.toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
    };

    const getSalesOrderTimeRank = (order) => {
        const rawTime = String(order?.time || '').trim();
        const timeMatch = rawTime.match(/^(\d{1,2}):(\d{2})\s*(AM|PM)$/i);
        if (timeMatch) {
            let hour = Number(timeMatch[1]) % 12;
            const minute = Number(timeMatch[2]);
            const meridian = String(timeMatch[3] || '').toUpperCase();
            if (meridian === 'PM') hour += 12;
            return (hour * 60) + minute;
        }

        const parsedTs = new Date(order?.timestamp || '');
        if (!Number.isNaN(parsedTs.getTime())) {
            return (parsedTs.getHours() * 60) + parsedTs.getMinutes();
        }

        return Number.MAX_SAFE_INTEGER;
    };

    const isNumberLike = (value) => value !== null && value !== undefined && value !== '' && Number.isFinite(Number(value));

    const toNumber = (value, fallback = 0) => {
        const num = Number(value);
        return Number.isFinite(num) ? num : fallback;
    };

    const pickLineItemCostMode = (costItems, headerTotal) => {
        const totals = {
            line: costItems.reduce((sum, item) => sum + item.lineTotal, 0),
            direct: costItems.reduce((sum, item) => sum + item.directTotal, 0),
            converted: costItems.reduce((sum, item) => sum + item.convertedTotal, 0)
        };

        const conversionScale = totals.direct > 0 ? (totals.converted / totals.direct) : 1;
        const isScaleMismatch =
            totals.converted >= 10 &&
            totals.direct > 0 &&
            conversionScale >= 100 &&
            headerTotal > 0 &&
            headerTotal <= 10;

        if (isScaleMismatch) return 'converted';

        const candidates = Object.entries(totals).filter(([, total]) => total > 0);
        if (candidates.length === 0) return 'line';

        if (headerTotal > 0) {
            return candidates.reduce((best, [mode, total]) => {
                const diff = Math.abs(total - headerTotal);
                if (!best || diff < best.diff) return { mode, diff };
                return best;
            }, null).mode;
        }

        return candidates.reduce((best, [mode, total]) => {
            if (!best || total > best.total) return { mode, total };
            return best;
        }, null).mode;
    };

    const normalizeStockInRecord = (record) => {
        // ── What this bill cost is decided on the SERVER ──────────────────────
        //
        // `final_cost` is the shared purchase-valuation ladder (utils/purchaseValuation.js)
        // — the same value /reports/expenses-summary totals into the KPI and the same one
        // the Expense EOM report totals into its purchases figure. Summing it here is what
        // makes the card, this modal and the month-end report three views of ONE number.
        //
        // This function used to re-derive the total in the browser from the line rows. It
        // was a fourth implementation of the same ladder and it drifted: for business 151's
        // July it landed ₹5.17 away from the two server-side ones over 183 bills. The line
        // normalisation below is still needed — it decides what each ROW displays — but it
        // no longer gets a vote on the bill's total; the rows are scaled to the server's
        // figure instead.
        const headerTotal = toNumber(record?.total_cost, 0);
        const authoritativeTotal = isNumberLike(record?.final_cost)
            ? toNumber(record.final_cost, 0)
            : headerTotal;
        const rawItems = Array.isArray(record?.line_items) ? record.line_items : [];

        if (rawItems.length === 0) {
            return { ...record, total_cost: authoritativeTotal, line_items: rawItems };
        }

        const costItems = rawItems.map((item) => {
            const qty = toNumber(item?.quantity, 0);
            const lineTotal = isNumberLike(item?.line_total) ? toNumber(item.line_total, 0) : 0;
            const unitCost = toNumber(item?.unit_cost, 0);
            const storedUnitCost = toNumber(item?.stored_unit_cost, 0);
            const conversionFactor = toNumber(item?.conversion_factor, 1) || 1;
            const directTotal = qty > 0 && unitCost > 0 ? qty * unitCost : 0;
            const convertedUnitCost = storedUnitCost > 0 && conversionFactor > 0
                ? storedUnitCost * conversionFactor
                : 0;
            const convertedTotal = qty > 0 && convertedUnitCost > 0 ? qty * convertedUnitCost : 0;

            return {
                item,
                qty,
                lineTotal,
                unitCost,
                storedUnitCost,
                conversionFactor,
                directTotal,
                convertedUnitCost,
                convertedTotal
            };
        });

        const costMode = pickLineItemCostMode(costItems, headerTotal);

        let normalizedItems = costItems.map((data) => {
            let nextLineTotal = data.lineTotal;
            let nextUnitCost = data.unitCost;

            if (costMode === 'converted' && data.convertedTotal > 0) {
                nextLineTotal = data.convertedTotal;
                nextUnitCost = data.convertedUnitCost;
            } else if (costMode === 'direct' && data.directTotal > 0) {
                nextLineTotal = data.directTotal;
                nextUnitCost = data.qty > 0 ? (data.directTotal / data.qty) : data.unitCost;
            } else if (nextLineTotal <= 0 && data.directTotal > 0) {
                nextLineTotal = data.directTotal;
                nextUnitCost = data.qty > 0 ? (data.directTotal / data.qty) : data.unitCost;
            } else if (nextLineTotal <= 0 && data.convertedTotal > 0) {
                nextLineTotal = data.convertedTotal;
                nextUnitCost = data.convertedUnitCost;
            }

            if (nextLineTotal > 0 && data.qty > 0 && nextUnitCost <= 0) {
                nextUnitCost = nextLineTotal / data.qty;
            }

            return {
                ...data.item,
                line_total: nextLineTotal,
                unit_cost: nextUnitCost
            };
        });

        let lineSum = normalizedItems.reduce((sum, item) => sum + toNumber(item.line_total, 0), 0);
        if (lineSum <= 0 && headerTotal > 0) {
            const weights = normalizedItems.map((item) => Math.max(0, toNumber(item?.quantity, 0)));
            let weightSum = weights.reduce((sum, weight) => sum + weight, 0);
            const useEqualSplit = weightSum <= 0;

            if (useEqualSplit) {
                weightSum = normalizedItems.length;
            }

            const lastAllocIndex = useEqualSplit
                ? normalizedItems.length - 1
                : (() => {
                    for (let i = weights.length - 1; i >= 0; i -= 1) {
                        if (weights[i] > 0) return i;
                    }
                    return weights.length - 1;
                })();

            let allocatedSum = 0;
            normalizedItems = normalizedItems.map((item, index) => {
                const weight = useEqualSplit ? 1 : weights[index];
                let lineTotal = weightSum > 0 ? (headerTotal * weight / weightSum) : 0;
                if (index === lastAllocIndex) {
                    lineTotal = headerTotal - allocatedSum;
                } else {
                    allocatedSum += lineTotal;
                }
                const qty = toNumber(item?.quantity, 0);
                const nextUnitCost = qty > 0 ? (lineTotal / qty) : toNumber(item.unit_cost, 0);
                return {
                    ...item,
                    line_total: lineTotal,
                    unit_cost: nextUnitCost
                };
            });
            lineSum = normalizedItems.reduce((sum, item) => sum + toNumber(item.line_total, 0), 0);
        }
        if (headerTotal > 0 && lineSum > 0) {
            const ratio = Math.max(headerTotal, lineSum) / Math.min(headerTotal, lineSum);
            if (ratio >= 1.2) {
                const scale = headerTotal / lineSum;
                normalizedItems = normalizedItems.map((item) => {
                    const qty = toNumber(item?.quantity, 0);
                    const scaledLineTotal = toNumber(item.line_total, 0) * scale;
                    const scaledUnitCost = qty > 0 ? (scaledLineTotal / qty) : toNumber(item.unit_cost, 0);
                    return {
                        ...item,
                        line_total: scaledLineTotal,
                        unit_cost: scaledUnitCost,
                        scale_adjusted: true
                    };
                });
                lineSum = normalizedItems.reduce((sum, item) => sum + toNumber(item.line_total, 0), 0);
            }
        }

        if (headerTotal > 0 && lineSum > 0 && lineSum < headerTotal) {
            const missingItems = normalizedItems.filter((item) => toNumber(item.line_total, 0) <= 0);
            if (missingItems.length > 0) {
                const missingTotal = headerTotal - lineSum;
                const weights = missingItems.map((item) => Math.max(0, toNumber(item?.quantity, 0)));
                let weightSum = weights.reduce((sum, weight) => sum + weight, 0);
                const useEqualSplit = weightSum <= 0;

                if (useEqualSplit) {
                    weightSum = missingItems.length;
                }

                const lastAllocIndex = useEqualSplit
                    ? missingItems.length - 1
                    : (() => {
                        for (let i = weights.length - 1; i >= 0; i -= 1) {
                            if (weights[i] > 0) return i;
                        }
                        return weights.length - 1;
                    })();

                let allocatedSum = 0;
                let missingIndex = 0;
                normalizedItems = normalizedItems.map((item) => {
                    if (toNumber(item.line_total, 0) > 0) return item;
                    const weight = useEqualSplit ? 1 : weights[missingIndex];
                    let lineTotal = weightSum > 0 ? (missingTotal * weight / weightSum) : 0;
                    if (missingIndex === lastAllocIndex) {
                        lineTotal = missingTotal - allocatedSum;
                    } else {
                        allocatedSum += lineTotal;
                    }
                    const qty = toNumber(item?.quantity, 0);
                    const nextUnitCost = qty > 0 ? (lineTotal / qty) : toNumber(item.unit_cost, 0);
                    missingIndex += 1;
                    return {
                        ...item,
                        line_total: lineTotal,
                        unit_cost: nextUnitCost,
                        missing_allocated: true
                    };
                });

                lineSum = normalizedItems.reduce((sum, item) => sum + toNumber(item.line_total, 0), 0);
            }
        }

        const remainingZeroItems = normalizedItems.filter((item) => toNumber(item.line_total, 0) <= 0);
        const rebalanceTotal = headerTotal > 0 ? headerTotal : lineSum;
        if (rebalanceTotal > 0 && lineSum > 0 && remainingZeroItems.length > 0) {
            const weights = normalizedItems.map((item) => Math.max(0, toNumber(item?.quantity, 0)));
            let weightSum = weights.reduce((sum, weight) => sum + weight, 0);
            const useEqualSplit = weightSum <= 0;

            if (useEqualSplit) {
                weightSum = normalizedItems.length;
            }

            const lastAllocIndex = useEqualSplit
                ? normalizedItems.length - 1
                : (() => {
                    for (let i = weights.length - 1; i >= 0; i -= 1) {
                        if (weights[i] > 0) return i;
                    }
                    return weights.length - 1;
                })();

            let allocatedSum = 0;
            normalizedItems = normalizedItems.map((item, index) => {
                const weight = useEqualSplit ? 1 : weights[index];
                let lineTotal = weightSum > 0 ? (rebalanceTotal * weight / weightSum) : 0;
                if (index === lastAllocIndex) {
                    lineTotal = rebalanceTotal - allocatedSum;
                } else {
                    allocatedSum += lineTotal;
                }
                const qty = toNumber(item?.quantity, 0);
                const nextUnitCost = qty > 0 ? (lineTotal / qty) : toNumber(item.unit_cost, 0);
                return {
                    ...item,
                    line_total: lineTotal,
                    unit_cost: nextUnitCost,
                    rebalance_adjusted: true
                };
            });
            lineSum = normalizedItems.reduce((sum, item) => sum + toNumber(item.line_total, 0), 0);
        }

        // The rows are made to sum to the server's figure, so the modal's lines add up to
        // the card above it. Only the split across lines is decided here; the total is not.
        const recordTotal = authoritativeTotal > 0
            ? authoritativeTotal
            : (lineSum > 0 ? lineSum : headerTotal);

        if (recordTotal > 0 && lineSum > 0 && Math.abs(lineSum - recordTotal) > 0.005) {
            const scale = recordTotal / lineSum;
            normalizedItems = normalizedItems.map((item) => {
                const qty = toNumber(item?.quantity, 0);
                const scaledLineTotal = toNumber(item.line_total, 0) * scale;
                return {
                    ...item,
                    line_total: scaledLineTotal,
                    unit_cost: qty > 0 ? (scaledLineTotal / qty) : toNumber(item.unit_cost, 0),
                    scale_adjusted: true
                };
            });
        }

        return {
            ...record,
            total_cost: recordTotal,
            line_items: normalizedItems
        };
    };

    const getLineItemDisplayTotal = (item) => {
        if (isNumberLike(item?.line_total)) {
            return toNumber(item.line_total, 0);
        }
        const qty = toNumber(item?.quantity, 0);
        const unitCost = toNumber(item?.unit_cost, 0);
        return qty * unitCost;
    };

    const getStockInRecordActualTotal = (record) => normalizeStockInRecord(record).total_cost;

    // ✅ state for backend data
    const [summary, setSummary] = useState({
        total_sales: 0,
        total_orders: 0,
        gross_profit: 0,
        avg_order_value: 0,
    });

    // BEP / Expense states
    const [fixedMonthlyExpense, setFixedMonthlyExpense] = useState(null);
    const [dailyBepTarget, setDailyBepTarget] = useState(0);
    const [bepProgressPercent, setBepProgressPercent] = useState(0);
    const [stockinExpenseTotal, setStockinExpenseTotal] = useState(0);
    
    // Expense tracking state
    const [expenses, setExpenses] = useState({
        total_expenses: 0,
        total_records: 0,
        manual_entries: 0,
        scanned_entries: 0,
        records_with_bills: 0
    });
    const [wastageExpenses, setWastageExpenses] = useState({
        total_expenses: 0,
        total_records: 0
    });
    const [showExpenseModal, setShowExpenseModal] = useState(false);
    const [expenseDetails, setExpenseDetails] = useState([]);
    const [wastageExpenseDetails, setWastageExpenseDetails] = useState([]);
    const [expenseLoading, setExpenseLoading] = useState(false);
    const [expenseError, setExpenseError] = useState(null);
    const [expenseTab, setExpenseTab] = useState('stockin');
    // Fixed Expense (4th tab): the Net Profit Analyzer ledger's share of the selected period.
    const [fixedShare, setFixedShare] = useState({ status: 'idle', data: null, error: null });
    const [fixedSearchQuery, setFixedSearchQuery] = useState('');
    const [fixedSearchField, setFixedSearchField] = useState('name');
    const fixedReqRef = useRef(0);
    const [stockInSearchQuery, setStockInSearchQuery] = useState('');
    const [stockInSearchFilter, setStockInSearchFilter] = useState('item');
    const [stockInFilterDropdownOpen, setStockInFilterDropdownOpen] = useState(false);
    const stockInFilterDropdownRef = useRef(null);
    const [refreshTrigger, setRefreshTrigger] = useState(0);
    const [revertToast, setRevertToast] = useState({ visible: false, message: '', type: 'info' });
    const [exportToast, setExportToast] = useState({ visible: false, message: '', type: 'success' });
    const revertToastRef = useRef(null);
    const [revertConfirm, setRevertConfirm] = useState({ visible: false, stockInId: null, record: null });
    const [revertLoading, setRevertLoading] = useState(false);
    const [wastageRevertConfirm, setWastageRevertConfirm] = useState({ visible: false, stockOutId: null, itemName: '' });
    const [revertSelectAll, setRevertSelectAll] = useState(false);
    const [revertSelected, setRevertSelected] = useState({});
    const [wastageRevertingId, setWastageRevertingId] = useState(null);
    
    // Admin PIN & Lock state
    const [unlockedRecords, setUnlockedRecords] = useState({});
    const [showPinModal, setShowPinModal] = useState(false);
    const [pinModalRecord, setPinModalRecord] = useState(null);
    const [enteredPin, setEnteredPin] = useState('');
    const [pinError, setPinError] = useState('');
    const [pinVerifying, setPinVerifying] = useState(false);

    // Editing line items states
    const [editingLineItemId, setEditingLineItemId] = useState(null);
    const [editQuantity, setEditQuantity] = useState('');
    const [editUnitPrice, setEditUnitPrice] = useState('');
    const [editSaving, setEditSaving] = useState(false);
    // The piece conversation for one edited line, or null.
    const [piecePopup, setPiecePopup] = useState(null);
    // M6 — the profile modal's own error line, so a failed definition is shown INSIDE the
    // screen the person is on rather than as a toast behind it.
    const [pieceProfileError, setPieceProfileError] = useState(null);

    const handleVerifyPin = async () => {
        if (!enteredPin.trim()) {
            setPinError('PIN is required');
            return;
        }

        try {
            setPinVerifying(true);
            setPinError('');

            const res = await http.post('/stock-in/v2/verify-admin-pin', { pin: enteredPin.trim() });

            if (res.success) {
                setUnlockedRecords(prev => ({ ...prev, [pinModalRecord.stock_in_id]: enteredPin.trim() }));
                setShowPinModal(false);
                setRevertConfirm({ visible: true, stockInId: pinModalRecord.stock_in_id, record: pinModalRecord });
                setPinModalRecord(null);
                setEnteredPin('');
            } else {
                setPinError(res.error || 'Incorrect PIN');
            }
        } catch (err) {
            const errorMsg = err.response?.data?.error || 'PIN verification failed';
            setPinError(errorMsg);
        } finally {
            setPinVerifying(false);
        }
    };

    const handleSaveLineItem = async (item) => {
        if (!editQuantity || isNaN(parseFloat(editQuantity)) || parseFloat(editQuantity) <= 0) {
            setRevertToast({ visible: true, message: 'Quantity must be a positive number', type: 'error' });
            return;
        }
        if (editUnitPrice === '' || isNaN(parseFloat(editUnitPrice)) || parseFloat(editUnitPrice) < 0) {
            setRevertToast({ visible: true, message: 'Unit price must be a non-negative number', type: 'error' });
            return;
        }

        try {
            setEditSaving(true);
            const pin = unlockedRecords[revertConfirm.stockInId] || null;
            const payload = {
                quantity: parseFloat(editQuantity),
                unit_cost: parseFloat(editUnitPrice)
            };
            if (pin) {
                payload.pin = pin;
            }

            const res = await http.put(`/stock-in/v2/${revertConfirm.stockInId}/line-items/${item.line_item_id}`, payload);

            if (res.success) {
                setRevertToast({ visible: true, message: 'Line item updated successfully!', type: 'success' });
                fetchExpenseDetails();
                setRefreshTrigger(prev => prev + 1);
                
                const updatedLineItems = revertConfirm.record.line_items.map(li => {
                    if (li.line_item_id === item.line_item_id) {
                        return {
                            ...li,
                            quantity: parseFloat(editQuantity),
                            unit_cost: parseFloat(editUnitPrice),
                            unit_price: parseFloat(editUnitPrice)
                        };
                    }
                    return li;
                });
                
                setRevertConfirm(prev => ({
                    ...prev,
                    record: {
                        ...prev.record,
                        line_items: updatedLineItems
                    }
                }));

                setEditingLineItemId(null);
                setEditQuantity('');
                setEditUnitPrice('');
            } else {
                setRevertToast({ visible: true, message: res.error || 'Failed to update line item', type: 'error' });
            }
        } catch (err) {
            // PIECE GUARD §11.1 — the edit screen is a stock-write door, so it gets the
            // same conversation the submit form does. Without this the ladder's message
            // ("this looks like a packet was entered as 1 piece") would be swallowed and
            // shown as "Failed to save changes", which is unactionable and would quietly
            // train people to give up on the edit rather than fix the number.
            if (err.data?.piece_blocker) {
                setPiecePopup({
                    blocker: { ...err.data.piece_blocker, index: 0 },
                    lineItemId: item.line_item_id,
                    stockInId: revertConfirm.stockInId,
                    quantity: parseFloat(editQuantity),
                    unitCost: parseFloat(editUnitPrice),
                    itemName: item.item_name || item.name,
                    itemId: item.item_id || null,
                    category: item.category || null,
                });
                setEditSaving(false);
                return;
            }
            // This client surfaces server payloads on `err.data`; the old
            // `err.response.data` path is an axios shape that never matched, so every
            // server-side reason was being replaced by a generic sentence.
            const errorMsg = err.data?.error || err.response?.data?.error || err.message || 'Failed to save changes';
            setRevertToast({ visible: true, message: errorMsg, type: 'error' });
            console.error('Save line item error:', err);
        } finally {
            setEditSaving(false);
        }
    };
    
    // Sales Detail Modal state
    const [showSalesDetailModal, setShowSalesDetailModal] = useState(false);
    const [salesDetailData, setSalesDetailData] = useState([]);
    const [salesDetailLoading, setSalesDetailLoading] = useState(false);
    const [salesDetailError, setSalesDetailError] = useState(null);
    const [salesDetailSearchQuery, setSalesDetailSearchQuery] = useState('');
    const [salesDetailSearchFilter, setSalesDetailSearchFilter] = useState('item'); // 'item' | 'date'
    const [salesDetailFilterDropdownOpen, setSalesDetailFilterDropdownOpen] = useState(false);
    const salesDetailFilterDropdownRef = useRef(null);

    // Not in Menu: the worklist (components/notInMenu) — opened from the Total
    // Sales hint, the Sales Detail badges, or the blocking banner.
    const [nimOpen, setNimOpen] = useState(false);
    const notInMenu = useNotInMenuCount();

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (salesDetailFilterDropdownRef.current && !salesDetailFilterDropdownRef.current.contains(event.target)) {
                setSalesDetailFilterDropdownOpen(false);
            }
        };
        if (salesDetailFilterDropdownOpen) {
            document.addEventListener('mousedown', handleClickOutside);
            document.addEventListener('touchstart', handleClickOutside);
        }
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
            document.removeEventListener('touchstart', handleClickOutside);
        };
    }, [salesDetailFilterDropdownOpen]);

    const salesDetailDataDateWise = useMemo(() => {
        const orders = Array.isArray(salesDetailData) ? [...salesDetailData] : [];

        orders.sort((a, b) => {
            const dateA = getSalesOrderDateKey(a);
            const dateB = getSalesOrderDateKey(b);

            if (dateA !== dateB) {
                return dateA.localeCompare(dateB);
            }

            const timeA = getSalesOrderTimeRank(a);
            const timeB = getSalesOrderTimeRank(b);
            if (timeA !== timeB) {
                return timeA - timeB;
            }

            return Number(a?.sale_id || 0) - Number(b?.sale_id || 0);
        });

        return orders.map((order) => {
            const sortedItems = Array.isArray(order?.items)
                ? [...order.items].sort((a, b) =>
                    String(a?.item_name || '').localeCompare(
                        String(b?.item_name || ''),
                        undefined,
                        { sensitivity: 'base', numeric: true }
                    )
                )
                : [];

            return {
                ...order,
                items: sortedItems
            };
        });
    }, [salesDetailData]);

    const filteredSalesDetailOrders = useMemo(() => {
        const query = normalizeSearchText(salesDetailSearchQuery);

        // 'unmapped' is a view mode, not a search field: it narrows each order to
        // just the lines that never resolved to a menu item, dropping orders that
        // have none. The text query still applies on top so the view stays
        // searchable. Purely presentational — totals already exclude these rows.
        if (salesDetailSearchFilter === 'unmapped') {
            const queryWords = query ? query.split(' ').filter(Boolean) : [];
            const matches = (text) => {
                if (queryWords.length === 0) return true;
                const norm = normalizeSearchText(text);
                return queryWords.every((word) => norm.includes(word));
            };
            return salesDetailDataDateWise
                .map((order) => {
                    const items = Array.isArray(order?.items) ? order.items : [];
                    const unmappedOnly = items.filter((i) => i?.is_unmapped && matches(i?.item_name));
                    if (unmappedOnly.length === 0) return null;
                    return { ...order, items: unmappedOnly };
                })
                .filter(Boolean);
        }

        if (!query) return salesDetailDataDateWise;

        const queryWords = query.split(' ').filter(Boolean);
        const matchesAllWords = (text) => {
            const norm = normalizeSearchText(text);
            return queryWords.every((word) => norm.includes(word));
        };

        if (salesDetailSearchFilter === 'date') {
            return salesDetailDataDateWise.filter((order) => {
                const dateLabel = getSalesOrderDateLabel(order);
                const dateKey = getSalesOrderDateKey(order);
                const timeStr = String(order?.time || '');
                return matchesAllWords(`${dateLabel} ${dateKey} ${timeStr}`);
            });
        }

        // When search filter is 'item'
        return salesDetailDataDateWise
            .map((order) => {
                const items = Array.isArray(order?.items) ? order.items : [];
                const matchingItems = items.filter((item) => matchesAllWords(item?.item_name));
                if (matchingItems.length === 0) return null;
                return {
                    ...order,
                    items: matchingItems
                };
            })
            .filter(Boolean);
    }, [salesDetailDataDateWise, salesDetailSearchQuery, salesDetailSearchFilter]);

    const isSingleDaySelected = (!selectedWeek && !selectedDate && (!selectedPeriod || selectedPeriod === 'Today')) ||
                                (selectedWeek && selectedWeek.isCustomRange && selectedWeek.isSingleDay) ||
                                (!selectedWeek && selectedDate && !selectedDate.isMonthSelection);
    
    // BEP modal & form state
    const [bepModalOpen, setBepModalOpen] = useState(false);
    const [bepSyncSaving, setBepSyncSaving] = useState(false);
    const [bepAmount, setBepAmount] = useState('');
    const [bepPeriod, setBepPeriod] = useState('Month');
    const [bepPeriodOpen, setBepPeriodOpen] = useState(false);
    const [bepBreakdown, setBepBreakdown] = useState({ daily: 0, weekly: 0, monthly: 0, yearly: 0 });
    const [isSavingBep, setIsSavingBep] = useState(false);

    const isNcWastageRecord = (record) => {
        const wasteReason = (record?.waste_reason || '').toLowerCase();
        const notes = (record?.notes || '').toLowerCase();

        return wasteReason.includes('non chargeable') ||
            wasteReason === 'nc' ||
            notes.startsWith('nc:') ||
            notes.startsWith('nc item:');
    };

    const ncExpenseRecords = useMemo(
        () => wastageExpenseDetails.filter(isNcWastageRecord),
        [wastageExpenseDetails]
    );

    const stockOutExpenseRecords = useMemo(
        () => wastageExpenseDetails.filter((record) => !isNcWastageRecord(record)),
        [wastageExpenseDetails]
    );

    const ncExpenseTotal = useMemo(
        () => ncExpenseRecords.reduce((sum, row) => sum + (Number(row.actual_cost_impact || row.estimated_cost_impact) || 0), 0),
        [ncExpenseRecords]
    );

    // Rows whose cost is a negative-stock dash ("-") are excluded from the numeric
    // total; when any exist the total itself renders "-", mirroring how Gross Profit
    // shows "-" once an item's COGS is unavailable.
    const stockOutHasUnavailable = useMemo(
        () => stockOutExpenseRecords.some((row) => !!row.cogs_unavailable),
        [stockOutExpenseRecords]
    );

    const stockOutExpenseTotal = useMemo(
        () => stockOutExpenseRecords
            .filter((row) => !row.cogs_unavailable)
            .reduce((sum, row) => sum + (Number(row.actual_cost_impact || row.estimated_cost_impact) || 0), 0),
        [stockOutExpenseRecords]
    );

    const filteredStockInRecords = useMemo(() => {
        const queryWords = normalizeSearchText(stockInSearchQuery).split(' ').filter(Boolean);
        if (queryWords.length === 0) return expenseDetails;

        const hasAllWords = (sourceText) => {
            const normalizedSource = normalizeSearchText(sourceText);
            if (!normalizedSource) return false;
            return queryWords.every((word) => normalizedSource.includes(word));
        };

        if (stockInSearchFilter === 'vendor') {
            return expenseDetails.filter((record) => hasAllWords(record?.vendor_name));
        }

        // When stockInSearchFilter === 'item', filter the records and their line items
        return expenseDetails
            .map((record) => {
                const lineItems = Array.isArray(record?.line_items) ? record.line_items : [];
                
                if (lineItems.length > 0) {
                    const matchingLineItems = lineItems.filter((item) => {
                        const itemName = item?.mapped_item_name || item?.item_name || '';
                        return hasAllWords(itemName);
                    });

                    if (matchingLineItems.length > 0) {
                        const newTotalCost = matchingLineItems.reduce((sum, item) => sum + toNumber(item.line_total, 0), 0);
                        return {
                            ...record,
                            line_items: matchingLineItems,
                            items_count: matchingLineItems.length,
                            total_cost: newTotalCost
                        };
                    }
                    
                    if (hasAllWords(record?.item_name)) {
                        return record;
                    }
                    
                    return null;
                } else {
                    if (hasAllWords(record?.item_name)) {
                        return record;
                    }
                    return null;
                }
            })
            .filter(Boolean);
    }, [expenseDetails, stockInSearchFilter, stockInSearchQuery]);

    const filteredStockInTotal = useMemo(
        () => filteredStockInRecords.reduce((sum, record) => sum + Number(record.total_cost || 0), 0),
        [filteredStockInRecords]
    );

    // When searching a Stock-In ingredient by item name, aggregate the total quantity
    // of the matched line items (exactly what the filtered cards show) so the summary
    // box can display "Quantity" instead of the record count. Summed per unit so mixed
    // units stay meaningful.
    const searchedStockInQuantitySummary = useMemo(() => {
        if (expenseTab !== 'stockin') return null;
        if (stockInSearchFilter !== 'item') return null;
        if (!stockInSearchQuery.trim()) return null;

        const unitTotals = new Map();

        filteredStockInRecords.forEach((record) => {
            const lineItems = Array.isArray(record?.line_items) ? record.line_items : [];
            lineItems.forEach((item) => {
                const unitLabel = item?.unit_symbol || item?.unit_name || 'unit';
                unitTotals.set(unitLabel, (unitTotals.get(unitLabel) || 0) + toNumber(item?.quantity, 0));
            });
        });

        if (unitTotals.size === 0) return null;

        return Array.from(unitTotals.entries())
            .map(([unit, qty]) => `${formatQuantity(qty)} ${formatUnitLabel(unit)}`)
            .join(' + ');
    }, [expenseTab, stockInSearchFilter, stockInSearchQuery, filteredStockInRecords]);

    const activeExpenseRecords = expenseTab === 'stockin'
        ? filteredStockInRecords
        : expenseTab === 'nc'
        ? ncExpenseRecords
        : stockOutExpenseRecords;

    // The ledger's share for the period (null until the module is set up on the server).
    const fixedShareFull = fixedShare.data && fixedShare.data.installed !== false && fixedShare.data.share ? fixedShare.data.share : null;
    const fixedShareView = useMemo(
        () => filterShare(fixedShareFull, fixedSearchQuery, fixedSearchField),
        [fixedShareFull, fixedSearchQuery, fixedSearchField]
    );
    const bepLedger = fixedShare.data && fixedShare.data.bep ? fixedShare.data.bep : null;
    const bepSyncOn = !!(bepLedger && bepLedger.sync);

    useEffect(() => {
        if (!stockInFilterDropdownOpen) return;

        const handleOutsideClick = (event) => {
            if (stockInFilterDropdownRef.current && !stockInFilterDropdownRef.current.contains(event.target)) {
                setStockInFilterDropdownOpen(false);
            }
        };

        document.addEventListener('mousedown', handleOutsideClick);
        return () => document.removeEventListener('mousedown', handleOutsideClick);
    }, [stockInFilterDropdownOpen]);

    useEffect(() => {
        if (expenseTab !== 'stockin') {
            setStockInFilterDropdownOpen(false);
        }
    }, [expenseTab]);
    
    // A stock-in stays revertible until the NEXT TSR is given. The backend decides
    // this from the real transaction sequence (stock-in created_at vs the TSR's
    // tsr_submitted_at) and sends it down as can_revert / tsr_submitted, so the state
    // survives a page refresh instead of living only in the UI.
    const isStockInTsrLocked = (record) => {
        if (!record) return false;
        if (typeof record.can_revert === 'boolean') return !record.can_revert;
        return !!record.tsr_submitted; // older payloads without can_revert
    };

    const stockInLockTitle = (record) => {
        const lockedAt = record?.tsr_submitted_at
            ? new Date(record.tsr_submitted_at).toLocaleString('en-IN', {
                day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit'
            })
            : null;
        return lockedAt
            ? `Locked because a TSR was submitted on ${lockedAt}, after this stock-in was recorded.`
            : 'Locked because sales (TSR) were submitted after this stock-in was recorded.';
    };

    // Handler to revert a stock-out (wastage) record from the expense modal.
    // A banquet wastage row also carries its event: the dialog names it and the
    // amount that goes back onto THAT banquet's gross profit.
    const handleWastageRevert = (stockOutId, itemName, row) => {
        if (!stockOutId) return;
        const banquet = row && row.banquetEventId
            ? { functionName: row.functionName, eventDate: row.eventDate, amount: row.amount }
            : null;
        setWastageRevertConfirm({ visible: true, stockOutId, itemName, banquet });
    };

    const executeWastageRevert = async () => {
        const { stockOutId, itemName } = wastageRevertConfirm;
        setWastageRevertConfirm({ visible: false, stockOutId: null, itemName: '' });
        if (!stockOutId) return;

        try {
            setWastageRevertingId(stockOutId);
            const res = await postUndo(`/wastage/${stockOutId}/revert`, {});
            if (res.success) {
                setRevertToast({ visible: true, message: res.message || 'Wastage reverted successfully!', type: 'success' });
                // Refresh expense details to update totals and remove the reverted record
                fetchExpenseDetails();
                setRefreshTrigger(prev => prev + 1);
            } else {
                setRevertToast({ visible: true, message: res.error || 'Failed to revert wastage', type: 'error' });
            }
        } catch (err) {
            // httpClient throws with err.data, not err.response.data; revertErrorMessage
            // reads both shapes, so the server's own reason ("Locked — closing stock is
            // confirmed up to …") is what the owner sees.
            const errorMsg = revertErrorMessage(err) || 'Failed to revert wastage';
            setRevertToast({ visible: true, message: errorMsg, type: 'error' });
            console.error('Wastage revert error:', err);
            // A 409 means the row's lock changed since the list loaded (a count was
            // confirmed or started): reload so the button shows the lock it now has.
            if (err && err.status === 409) fetchExpenseDetails();
        } finally {
            setWastageRevertingId(null);
        }
    };

    // Daily breakdown table state
    const [dailyBreakdown, setDailyBreakdown] = useState([]);
    const [outletBreakdown, setOutletBreakdown] = useState({ has_outlets: false, rows: [] });
    
    // Fixed expenses for the selected period, read from the Net Profit Analyzer's ledger. A
    // failure here only affects the Fixed Expense tab and the card's "incl. fixed" line; every
    // other figure on the report is untouched.
    const fetchFixedShare = async () => {
        const req = ++fixedReqRef.current;
        setFixedShare((s) => ({ ...s, status: 'loading', error: null }));
        try {
            const r = await http.get(`/net-profit/fixed-share${fixedShareQuery({ selectedWeek, selectedDate, selectedPeriod })}`);
            if (req !== fixedReqRef.current) return;
            if (r && r.success === false) throw new Error(r.error || 'Failed to load fixed expenses');
            setFixedShare({ status: 'ok', data: r || null, error: null });
        } catch (e) {
            if (req !== fixedReqRef.current) return;
            setFixedShare({ status: 'error', data: null, error: e.message || 'Failed to load fixed expenses' });
        }
    };

    useEffect(() => {
        fetchFixedShare();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedPeriod, selectedDate, selectedWeek, refreshTrigger]);

    // Returned from the Analyzer: reopen Expense Details on the tab the person left from.
    useEffect(() => {
        clearReturnPoint();
        if (returnPoint && returnPoint.openExpense) {
            fetchExpenseDetails().then(() => setExpenseTab(returnPoint.tab || 'fixed'));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    /** Fixed Expense tab -> the Analyzer, remembering where to come back to (brief section 10.1). */
    const openFixedInExpenses = (params) => {
        saveReturnPoint({ selectedPeriod, selectedDate, selectedWeek, displayText, tab: 'fixed', openExpense: true });
        setShowExpenseModal(false);
        goTo('net-profit', { ...params, returnTo: 'sales-report' });
    };

    // Fetch expense details for modal
    const fetchExpenseDetails = async () => {
        try {
            setExpenseLoading(true);
            setExpenseError(null);
            setExpenseDetails([]);
            setWastageExpenseDetails([]);
            setShowExpenseModal(true);
            fetchFixedShare();

            const bId = resolveBusinessId();
            if (!bId) return;
            const currentLanguage = language || localStorage.getItem('selectedLanguage') || 'en';
            let queryStr = `?businessId=${bId}&language=${currentLanguage}&noCache=1`;
            
            // Priority 1: Week selection from week picker or custom date range
            if (selectedWeek && selectedWeek.isWeekSelection) {
                const weekStart = formatDate(selectedWeek.start);
                const weekEnd = formatDate(selectedWeek.end);
                queryStr += `&weekStart=${weekStart}&weekEnd=${weekEnd}`;
            } 
            // Priority 2: Custom date range from Dates tab
            else if (selectedWeek && selectedWeek.isCustomRange) {
                const rangeStart = formatDate(selectedWeek.start);
                const rangeEnd = formatDate(selectedWeek.end);
                
                if (selectedWeek.isSingleDay) {
                    queryStr += `&date=${rangeStart}`;
                } else {
                    queryStr += `&weekStart=${rangeStart}&weekEnd=${rangeEnd}`;
                }
            }
            // Priority 3: Month selection from date picker
            else if (selectedDate && selectedDate.isMonthSelection) {
                const monthYear = `${selectedDate.year}-${String(selectedDate.month + 1).padStart(2, '0')}`;
                queryStr += `&period=month&monthYear=${monthYear}`;
            }
            // Priority 4: Legacy single date support
            else if (selectedDate) {
                const dateStr = selectedDate.toLocaleDateString('en-CA');
                queryStr += `&date=${dateStr}`;
            }
            // Priority 5: Standard periods (Today, This Week, This Month, This Year)
            else {
                let periodParam = '';
                if (selectedPeriod === 'Today') {
                    periodParam = 'today';
                } else if (selectedPeriod === 'This Week') {
                    periodParam = 'week';
                } else if (selectedPeriod === 'This Month') {
                    periodParam = 'month';
                } else if (selectedPeriod === 'This Year') {
                    periodParam = 'year';
                }
                if (periodParam) {
                    queryStr += `&period=${periodParam}`;
                }
            }
            
            const details = await http.get(`/reports/expense-details${queryStr}`);
            const detailsArray = Array.isArray(details) ? details : [];
            const normalizedDetailsArray = detailsArray.map((record) => normalizeStockInRecord(record));
            setExpenseDetails(normalizedDetailsArray);

            const stockInTotalActual = normalizedDetailsArray.reduce((sum, record) => sum + Number(record.total_cost || 0), 0);
            setExpenses((prev) => ({
                ...prev,
                total_expenses: stockInTotalActual,
                total_records: normalizedDetailsArray.length
            }));

            const stockOutDetails = await http.get(`/reports/stockout-details${queryStr}`).catch(() => []);
            const stockOutArray = Array.isArray(stockOutDetails) ? stockOutDetails : [];
            setWastageExpenseDetails(stockOutArray);
            
            setExpenseTab('stockin');
            setStockInSearchFilter('item');
            setStockInSearchQuery('');
            setStockInFilterDropdownOpen(false);
        } catch (e) {
            console.error('Failed to fetch expense details:', e);
            setExpenseDetails([]);
            setWastageExpenseDetails([]);
            setExpenseError(e.message || 'Failed to load expense details');
            setShowExpenseModal(true);
        } finally {
            setExpenseLoading(false);
        }
    };
    
    // Helper: compute BEP breakdown given an amount and period
    const computeBepBreakdown = (amount, period, forDate = null) => {
        const num = Number(amount) || 0;
        // Convert input to monthly amount
        let monthly = 0;
        const wksPerYear = 52.1429;
        const daysPerYear = 365.2425;
        switch ((period || 'Month').toLowerCase()) {
            case 'year': monthly = num / 12; break;
            case 'week': monthly = num * (wksPerYear / 12); break; // approximate
            case 'day': monthly = num * (daysPerYear / 12); break; // approximate
            default: monthly = num; break; // month
        }

        const d = forDate || new Date();
        const daysInMonth = new Date(d.getFullYear(), d.getMonth() + 1, 0).getDate();
        const daily = daysInMonth > 0 ? (monthly / daysInMonth) : 0;
        const weekly = monthly / 4;
        const yearly = monthly * 12;
        return { daily, weekly, monthly, yearly };
    };

    // Fetch sales detail for modal
    const fetchSalesDetail = async () => {
        try {
            setSalesDetailLoading(true);
            setSalesDetailError(null);
            setSalesDetailSearchQuery('');
            setSalesDetailFilterDropdownOpen(false);

            const isSingleDay = (!selectedWeek && !selectedDate && (!selectedPeriod || selectedPeriod === 'Today')) ||
                                (selectedWeek && selectedWeek.isCustomRange && selectedWeek.isSingleDay) ||
                                (!selectedWeek && selectedDate && !selectedDate.isMonthSelection);
            if (isSingleDay) {
                setSalesDetailSearchFilter('item');
            }

            setShowSalesDetailModal(true);
            
            const bId = resolveBusinessId();
            if (!bId) {
                throw new Error('Business ID not found');
            }
            
            let params = { businessId: bId };
            
            // Priority 1: Week selection from week picker or custom date range
            if (selectedWeek && selectedWeek.isWeekSelection) {
                params.start_date = formatDate(selectedWeek.start);
                params.end_date = formatDate(selectedWeek.end);
            } 
            // Priority 2: Custom date range from Dates tab
            else if (selectedWeek && selectedWeek.isCustomRange) {
                if (selectedWeek.isSingleDay) {
                    params.date = formatDate(selectedWeek.start);
                } else {
                    params.start_date = formatDate(selectedWeek.start);
                    params.end_date = formatDate(selectedWeek.end);
                }
            }
            // Priority 3: Month selection
            else if (selectedDate && selectedDate.isMonthSelection) {
                const year = selectedDate.year;
                const month = selectedDate.month + 1;
                const firstDay = new Date(year, selectedDate.month, 1);
                const lastDay = new Date(year, month, 0);
                params.start_date = formatDate(firstDay);
                params.end_date = formatDate(lastDay);
            }
            // Priority 4: Single date selection
            else if (selectedDate) {
                params.date = formatDate(selectedDate);
            }
            // Priority 5: Standard periods (Today, This Week, This Month, This Year)
            else if (selectedPeriod === 'This Week') {
                // Monday → today: the week the summary endpoint and the card total.
                const today = new Date();
                params.start_date = formatDate(weekStartMonday(today));
                params.end_date = formatDate(today);
            } else if (selectedPeriod === 'This Month') {
                const today = new Date();
                const monthStart = new Date(today.getFullYear(), today.getMonth(), 1);
                const monthEnd = new Date(today.getFullYear(), today.getMonth() + 1, 0);
                params.start_date = formatDate(monthStart);
                params.end_date = formatDate(monthEnd);
            } else if (selectedPeriod === 'This Year') {
                const today = new Date();
                const yearStart = new Date(today.getFullYear(), 0, 1);
                params.start_date = formatDate(yearStart);
                params.end_date = formatDate(today);
            }
            // Default: Today
            else {
                const today = new Date();
                params.date = formatDate(today);
            }
            
            
            // Build query string from params
            const queryString = Object.keys(params)
                .map(key => `${encodeURIComponent(key)}=${encodeURIComponent(params[key])}`)
                .join('&');
            
            
            const response = await http.get(`/reports/total-sales-detail?${queryString}`);
            
            
            if (response.success) {
                setSalesDetailData(response.orders || []);
            } else {
                throw new Error(response.error || 'Failed to load sales details');
            }
        } catch (error) {
            console.error('Failed to fetch sales detail:', error);
            setSalesDetailError(error.message || 'Failed to load sales details');
        } finally {
            setSalesDetailLoading(false);
        }
    };

    // Save BEP (persist to settings)
    const saveBepSetting = async () => {
        try {
            setIsSavingBep(true);
            const bId = resolveBusinessId();
            if (!bId) return;
            // compute monthly value from entered amount & period
            const breakdown = computeBepBreakdown(bepAmount, bepPeriod, bepBasisDate(selectedDate, selectedWeek));
            const monthlyValue = Number(breakdown.monthly || 0);
            // persist using SettingsService
            const payload = [{ key: 'owner.fixed_monthly_expenses', value: monthlyValue, data_type: 'number', module_scope: 'finance' }];
            await SettingsService.upsertSettings(bId, payload);
            // update local state
            setFixedMonthlyExpense(monthlyValue);
            setBepBreakdown(breakdown);
            // recompute target & percent for current selection
            const br2 = computeBepBreakdown(monthlyValue, 'Month', bepBasisDate(selectedDate, selectedWeek));
            const target2 = contributionTarget(br2, { selectedPeriod, selectedDate, selectedWeek });
            setDailyBepTarget(target2);
            const actualSales = Number(summary.total_sales || 0);
            setBepProgressPercent(target2 > 0 ? (actualSales / target2) * 100 : 0);
            setBepModalOpen(false);
        } catch (err) {
            console.error('Failed to save BEP setting:', err);
            // keep modal open for retry
        } finally {
            setIsSavingBep(false);
        }
    };
    // Break-even "Keep in sync" (brief 9.4): the target follows the fixed-expense ledger. Turned
    // on, the server writes the figure into owner.fixed_monthly_expenses and keeps it current;
    // turned off, the owner's typed value is never overwritten again.
    const toggleBepSync = async (on) => {
        setBepSyncSaving(true);
        try {
            const r = await http.patch('/net-profit/settings', { bepSync: on });
            if (r && r.success === false) throw new Error(r.error || 'Could not change the setting');
            await fetchFixedShare();
            if (on && r && r.bepMonthly != null) {
                const monthly = Number(r.bepMonthly) || 0;
                setFixedMonthlyExpense(monthly);
                setBepAmount(String(monthly));
                setBepPeriod('Month');
                setBepBreakdown(computeBepBreakdown(monthly, 'Month', bepBasisDate(selectedDate, selectedWeek)));
                const target = contributionTarget(computeBepBreakdown(monthly, 'Month', bepBasisDate(selectedDate, selectedWeek)), { selectedPeriod, selectedDate, selectedWeek });
                setDailyBepTarget(target);
            }
        } catch (err) {
            console.error('Failed to change break-even sync:', err);
        } finally {
            setBepSyncSaving(false);
        }
    };

    // Removed Sales by Payment UI; no paymentMethods state needed
    const [salesTrend, setSalesTrend] = useState([]);
    // `topItems` was removed with the /reports/top-items call that fed it — nothing read it.
    const [topProfitItems, setTopProfitItems] = useState([]);
    const [showItemTableView, setShowItemTableView] = useState(false);
    const [showGraphModal, setShowGraphModal] = useState(false);

    // Handle section highlighting and scrolling
    useEffect(() => {
        if (activeSection) {
            const element = document.getElementById(activeSection);
            if (element) {
                element.scrollIntoView({ behavior: 'smooth', block: 'center' });
            }
        }
    }, [activeSection]);

    // Fetch all data when selectedPeriod changes
    useEffect(() => {
        // Fetch summary comparison for percentage change
        const fetchSummaryComparison = async () => {
            try {
                let periodQuery = '';
                if (selectedPeriod === 'Today') {
                    // Use 'today' period parameter for server-side date handling
                    periodQuery = `?period=today`;
                } else if (selectedWeek && selectedWeek.isWeekSelection) {
                    const weekStart = formatDate(selectedWeek.start);
                    const weekEnd = formatDate(selectedWeek.end);
                    periodQuery = `?weekStart=${weekStart}&weekEnd=${weekEnd}`;
                } else if (selectedWeek && selectedWeek.isCustomRange) {
                    if (selectedWeek.isSingleDay) {
                        periodQuery = `?date=${formatDate(selectedWeek.start)}`;
                    } else {
                        periodQuery = `?weekStart=${formatDate(selectedWeek.start)}&weekEnd=${formatDate(selectedWeek.end)}`;
                    }
                } else if (selectedDate && selectedDate.isMonthSelection) {
                    const monthYear = `${selectedDate.year}-${String(selectedDate.month + 1).padStart(2, '0')}`;
                    periodQuery = `?period=month&monthYear=${monthYear}`;
                } else if (selectedDate instanceof Date) {
                    const dateStr = selectedDate.toLocaleDateString('en-CA');
                    periodQuery = `?date=${dateStr}`;
                } else {
                    let periodParam = '';
                    if (selectedPeriod === 'This Week') periodParam = 'week';
                    else if (selectedPeriod === 'This Month') periodParam = 'month';
                    else if (selectedPeriod === 'This Year') periodParam = 'year';
                    if (periodParam) periodQuery = `?period=${periodParam}`;
                }
                // Through reportGet like every other report call: it was the one request
                // still using a bare http.get, so it had no timeout and could sit open
                // indefinitely behind a slow window. It stays non-blocking — a missing
                // comparison hides a delta, it does not stop the report.
                const compResult = await reportGet(`/reports/summary-comparison${periodQuery}`).catch(() => null);
                setSummaryComparison(compResult);
            } catch (err) {
                setSummaryComparison(null);
            }
        };
        fetchSummaryComparison();
        const fetchAllData = async () => {
            const requestId = ++fetchRequestIdRef.current;
            const isLatestRequest = () => fetchRequestIdRef.current === requestId;
            setIsLoading(true);
            setError(null);
            setFatalError(false);
            setSectionErrors([]);
            setPendingSections(REPORT_SECTIONS);
            // Avoid rendering stale values while a new report fetch is in-flight.
            setExpenses(EMPTY_EXPENSES);
            setWastageExpenses(EMPTY_WASTAGE_EXPENSES);

            const settleSection = (section, failed) => {
                if (!isLatestRequest()) return;
                setPendingSections((previous) => previous.filter((name) => name !== section));
                if (failed) {
                    setSectionErrors((previous) => (previous.includes(section) ? previous : [...previous, section]));
                }
            };

            try {
                // Get businessId from localStorage
                const businessId = resolveBusinessId();
                if (!businessId) {
                    console.error('❌ No businessId found');
                    if (isLatestRequest()) {
                        setError('Missing business context. Please re-login and try again.');
                        setFatalError(true);
                        setPendingSections([]);
                        setIsLoading(false);
                    }
                    return;
                }
                const currentLanguage = language || localStorage.getItem('selectedLanguage') || 'en';

                // ── S2.1: no more `noCache=1` ──
                //
                // Every one of these URLs used to end in `&noCache=1`, so each report open
                // recomputed the whole window from scratch and the 5-minute Redis cache
                // never once served a reader. On a multi-day Range Mode selection that is
                // minutes of gross-profit recomputation per screen open, every open.
                //
                // The flag existed because one write path — QR-settled sales — never
                // cleared the report cache, so cached numbers really could go stale. That
                // gap is now closed inside SalesTransactionService (and again after the
                // COMMIT in qrBilling/payment), which is the precondition for trusting the
                // cache. `noCache` is still honoured server-side for debugging; nothing in
                // the app sends it.
                let queryStr = '';
                if (selectedWeek && selectedWeek.isWeekSelection) {
                    // Week selection from week picker - send weekStart and weekEnd
                    const weekStart = formatDate(selectedWeek.start);
                    const weekEnd = formatDate(selectedWeek.end);
                    queryStr = `?weekStart=${weekStart}&weekEnd=${weekEnd}&businessId=${businessId}&language=${currentLanguage}`;
                } else if (selectedWeek && selectedWeek.isCustomRange) {
                    // Custom date range from Dates tab - can be single day or range
                    const rangeStart = formatDate(selectedWeek.start);
                    const rangeEnd = formatDate(selectedWeek.end);

                    if (selectedWeek.isSingleDay) {
                        // Single-day selection: use date parameter for optimized single-day query
                        queryStr = `?date=${rangeStart}&businessId=${businessId}&language=${currentLanguage}`;
                    } else {
                        // Multi-day custom range: use weekStart/weekEnd parameters
                        queryStr = `?weekStart=${rangeStart}&weekEnd=${rangeEnd}&businessId=${businessId}&language=${currentLanguage}`;
                    }
                } else if (selectedPeriod === 'Today') {
                    // Use 'today' period parameter for server-side date handling
                    queryStr = `?period=today&businessId=${businessId}&language=${currentLanguage}`;
                } else if (selectedDate && selectedDate.isMonthSelection) {
                    // Month selection from date picker - send month and year
                    const monthYear = `${selectedDate.year}-${String(selectedDate.month + 1).padStart(2, '0')}`;
                    queryStr = `?period=month&monthYear=${monthYear}&businessId=${businessId}&language=${currentLanguage}`;
                } else if (selectedDate) {
                    // Legacy single date support (if needed in future)
                    queryStr = `?date=${selectedDate.toLocaleDateString('en-CA')}&businessId=${businessId}&language=${currentLanguage}`;
                } else {
                    let periodParam = '';
                    if (selectedPeriod === 'This Week') periodParam = 'week';
                    else if (selectedPeriod === 'This Month') periodParam = 'month';
                    else if (selectedPeriod === 'This Year') periodParam = 'year';
                    if (periodParam) queryStr = `?period=${periodParam}&businessId=${businessId}&language=${currentLanguage}`;
                    else queryStr = `?businessId=${businessId}&language=${currentLanguage}`;
                }
                // Compute basis date for daily-contribution (needed before the requests)
                const bId = businessId;
                const basisDate = bepBasisDate(selectedDate, selectedWeek);
                const basisDateStr = basisDate.toLocaleDateString('en-CA');

                // ── S1.1: three calls deleted ──
                //
                // `/reports/top-items`, `/reports/key-analysis` and `/reports/items-by-date`
                // were fetched on every open and their results assigned to `topItems`,
                // `keyAnalysis`, `pieData` and `pieDate` — four pieces of state that no
                // render site anywhere in this file reads. Each of them ran its own full
                // gross-profit pass over the window and held a database connection while
                // doing it, which on a range selection is how `/reports/expense-details`
                // ends up losing its connection ("Connection terminated due to connection
                // timeout") and reporting ₹0 of expenses.
                //
                // ── S1.2: fired together, rendered as they arrive ──
                //
                // These are still fired in parallel, but each one now updates its own
                // section the moment it resolves. Only the summary holds the page spinner.
                const summaryRequest = reportGet(`/reports/summary${queryStr}`);
                const trendRequest = reportGet(`/reports/trend${queryStr}`);
                const topProfitRequest = reportGet(`/reports/top-profit-items${queryStr}`);
                const dailyContribRequest = bId
                    ? reportGet(`/reports/daily-contribution?date=${basisDateStr}&businessId=${bId}`)
                    : Promise.resolve(null);
                const expensesRequest = reportGet(`/reports/expenses-summary${queryStr}`);
                const expenseDetailsRequest = reportGet(`/reports/expense-details${queryStr}`);
                const stockOutRequest = reportGet(`/reports/stockout-summary${queryStr}`);
                const breakdownRequest = reportGet(`/reports/daily-breakdown${queryStr}`);
                const outletRequest = reportGet(`/reports/outlet-breakdown${queryStr}`);
                outletRequest.then((r) => {
                    if (!isLatestRequest()) return;
                    setOutletBreakdown(r && Array.isArray(r.rows) ? r : { has_outlets: false, rows: [] });
                }).catch(() => {
                    if (!isLatestRequest()) return;
                    // An optional section: a failure hides it rather than blanking the page.
                    setOutletBreakdown({ has_outlets: false, rows: [] });
                });

                // ── Summary: the headline, and the only thing the page spinner waits for ──
                const summaryPromise = summaryRequest.then((summaryResult) => {
                    if (!isLatestRequest()) return null;

                    // Every field the page reads — including the "not in menu" and
                    // estimated-cost figures the old field-by-field copy dropped.
                    const newSummary = normalizeSummary(summaryResult);

                    setSummary(newSummary);
                    settleSection('summary', false);
                    return summaryResult;
                }).catch((err) => {
                    console.error('Failed to load sales summary:', err);
                    if (isLatestRequest()) {
                        // The headline failing IS the page failing. Reporting it as ₹0 —
                        // which is what the old `.catch(() => ({ total_sales: 0, ... }))`
                        // did — is the "GP shows 0" complaint in its original form.
                        setError(err?.isTimeout
                            ? 'This range is taking longer than usual to calculate. The costing for it is still being worked out in the background — try again in a moment and it should come back quickly.'
                            : 'Could not load the sales summary for this range. Please try again.');
                        settleSection('summary', true);
                    }
                    return null;
                }).finally(() => {
                    // The spinner ends here whatever happened, and the rest of the screen
                    // fills in behind it.
                    if (isLatestRequest()) setIsLoading(false);
                });

                // ── Everything else: independent, each rendering on arrival ──
                const trendPromise = trendRequest.then((trendResult) => {
                    if (!isLatestRequest()) return;
                    setSalesTrend(Array.isArray(trendResult) ? trendResult : []);
                    settleSection('trend', false);
                }).catch((err) => {
                    console.error('Failed to load sales trend:', err);
                    // Guarded: a late failure from a range the reader has already moved
                    // off must not blank the range they are looking at now.
                    if (!isLatestRequest()) return;
                    setSalesTrend([]);
                    settleSection('trend', true);
                });

                const topProfitPromise = topProfitRequest.then((topProfitResult) => {
                    if (!isLatestRequest()) return;
                    setTopProfitItems(Array.isArray(topProfitResult) ? topProfitResult : []);
                    settleSection('topProfit', false);
                }).catch((err) => {
                    console.error('Failed to load top profit items:', err);
                    if (!isLatestRequest()) return;
                    setTopProfitItems([]);
                    settleSection('topProfit', true);
                });

                // BEP needs the period's actual sales, so it is the one place that still
                // waits on a second request — its own, plus the summary.
                const bepPromise = Promise.all([
                    dailyContribRequest.catch((err) => {
                        console.error('Failed to load daily contribution:', err);
                        return Promise.reject(err);
                    }),
                    summaryPromise
                ]).then(([dailyContribResult, summaryResult]) => {
                    if (!isLatestRequest()) return;
                    if (dailyContribResult) {
                        const monthly = Number(dailyContribResult.fixed_monthly_expenses || 0);
                        setFixedMonthlyExpense(monthly);
                        const br = computeBepBreakdown(monthly, 'Month', basisDate);
                        // One rule for the figure and the card's label.
                        const target = contributionTarget(br, { selectedPeriod, selectedDate, selectedWeek });
                        setDailyBepTarget(Number(target || 0));
                        const actualSalesForPeriod = Number((summaryResult && summaryResult.total_sales) || 0);
                        setBepProgressPercent(target > 0 ? (actualSalesForPeriod / target) * 100 : 0);
                        setStockinExpenseTotal(Number(dailyContribResult.stockin_total || 0));
                    } else {
                        setFixedMonthlyExpense(null);
                        setDailyBepTarget(0);
                        setBepProgressPercent(0);
                        setStockinExpenseTotal(0);
                    }
                    settleSection('bep', false);
                }).catch(() => {
                    if (!isLatestRequest()) return;
                    setFixedMonthlyExpense(null);
                    setDailyBepTarget(0);
                    setBepProgressPercent(0);
                    setStockinExpenseTotal(0);
                    settleSection('bep', true);
                });

                // Expenses are the sum of the detail rows when they arrive, and the
                // summary figure otherwise — the same precedence as before, so the card
                // still agrees with the modal it opens.
                const expensesPromise = Promise.all([
                    expensesRequest.catch((err) => {
                        console.error('Failed to load expenses summary:', err);
                        return { __failed: true };
                    }),
                    expenseDetailsRequest.catch((err) => {
                        console.error('Failed to load expense details:', err);
                        return { __failed: true };
                    })
                ]).then(([expenseResult, expenseDetailsResult]) => {
                    if (!isLatestRequest()) return;
                    const summaryFailed = Boolean(expenseResult && expenseResult.__failed);
                    const detailsFailed = Boolean(expenseDetailsResult && expenseDetailsResult.__failed);

                    const detailsArray = (!detailsFailed && Array.isArray(expenseDetailsResult))
                        ? expenseDetailsResult
                        : [];
                    const normalizedDetailsArray = detailsArray.map((record) => normalizeStockInRecord(record));

                    if (normalizedDetailsArray.length > 0) {
                        const stockInTotalActual = normalizedDetailsArray.reduce((sum, record) => sum + Number(record.total_cost || 0), 0);
                        setExpenses({
                            total_expenses: stockInTotalActual,
                            total_records: normalizedDetailsArray.length,
                            manual_entries: Number((!summaryFailed && expenseResult?.manual_entries) || 0),
                            scanned_entries: Number((!summaryFailed && expenseResult?.scanned_entries) || 0),
                            records_with_bills: Number((!summaryFailed && expenseResult?.records_with_bills) || 0)
                        });
                        // Keep detail cache warm so modal opens with consistent values.
                        setExpenseDetails(normalizedDetailsArray);
                    } else if (!summaryFailed && expenseResult) {
                        setExpenses({
                            total_expenses: Number(expenseResult.total_expenses || 0),
                            total_records: Number(expenseResult.total_records || 0),
                            manual_entries: Number(expenseResult.manual_entries || 0),
                            scanned_entries: Number(expenseResult.scanned_entries || 0),
                            records_with_bills: Number(expenseResult.records_with_bills || 0)
                        });
                    } else {
                        setExpenses(EMPTY_EXPENSES);
                    }

                    settleSection('expenses', summaryFailed && detailsFailed);
                });

                const stockOutPromise = stockOutRequest.then((stockOutResult) => {
                    if (!isLatestRequest()) return;
                    if (stockOutResult) {
                        setWastageExpenses({
                            total_expenses: Number(stockOutResult.total_expenses || 0),
                            total_records: Number(stockOutResult.total_records || 0)
                        });
                    } else {
                        setWastageExpenses(EMPTY_WASTAGE_EXPENSES);
                    }
                    settleSection('wastage', false);
                }).catch((err) => {
                    console.error('Failed to load wastage summary:', err);
                    if (!isLatestRequest()) return;
                    setWastageExpenses(EMPTY_WASTAGE_EXPENSES);
                    settleSection('wastage', true);
                });

                const breakdownPromise = breakdownRequest.then((breakdownResult) => {
                    if (!isLatestRequest()) return;
                    setDailyBreakdown(Array.isArray(breakdownResult) ? breakdownResult : []);
                    settleSection('breakdown', false);
                }).catch((err) => {
                    console.error('Failed to load daily breakdown:', err);
                    if (!isLatestRequest()) return;
                    setDailyBreakdown([]);
                    settleSection('breakdown', true);
                });

                // Nothing below blocks the screen — this only keeps the effect alive until
                // every section has settled, so `pendingSections` empties exactly once.
                await Promise.allSettled([
                    summaryPromise,
                    trendPromise,
                    topProfitPromise,
                    bepPromise,
                    expensesPromise,
                    stockOutPromise,
                    breakdownPromise
                ]);
            } catch (err) {
                console.error('Sales report load failed:', err);
                if (isLatestRequest()) {
                    setError('Failed to load sales data. Please try again.');
                    setFatalError(true);
                    setPendingSections([]);
                    setIsLoading(false);
                }
            }
        };
        fetchAllData();
    }, [selectedPeriod, selectedDate, selectedWeek, language, refreshTrigger]); // Added language and refreshTrigger to trigger refetch on language change or reversion
    // Pie chart renderers
    const renderPieChart = (data, dataKey, title, sectionId) => (
        <div
            id={sectionId}
            className="trends-wrapper"
            style={{
                border: `3px solid ${sectionId === activeSection ? '#2196F3' : '#e0e0e0'}`,
                minHeight: 350,
                marginBottom: 24,
                transition: 'all 0.3s ease',
                scrollMarginTop: '100px',
                scrollMarginBottom: '20px'
            }}
            onMouseLeave={() => setHoverIndex(null)}
        >
            <h2 className="section-title">{title}</h2>
            {data && data.length > 0 ? (
                <ResponsiveContainer width="100%" height={340}>
                    <PieChart onMouseLeave={() => setHoverIndex(null)}>
                        <Pie
                            data={data.map(entry => ({
                                ...entry,
                                total_sales: Number(entry.total_sales),
                                gross_profit: Number(entry.gross_profit)
                            }))}
                            dataKey={dataKey}
                            nameKey="item_name"
                            cx="50%"
                            cy="50%"
                            outerRadius={110}
                            innerRadius={0}
                            startAngle={30}
                            endAngle={390}
                            isAnimationActive
                            stroke="none"
                            label={({ name, cx, cy, midAngle, outerRadius, index }) => {
                                const RADIAN = Math.PI / 180;
                                const radius = outerRadius + 18;
                                const x = cx + radius * Math.cos(-midAngle * RADIAN);
                                const y = cy + radius * Math.sin(-midAngle * RADIAN);
                                return (
                                    <text
                                        x={x}
                                        y={y}
                                        fill="#666"
                                        textAnchor={x > cx ? 'start' : 'end'}
                                        dominantBaseline="central"
                                        fontSize={15}
                                        fontWeight={500}
                                        style={{ pointerEvents: 'none' }} // Prevent label from interfering with hover
                                    >
                                        {name}
                                    </text>
                                );
                            }}
                            activeIndex={0}
                            activeShape={props => {
                                // Explode the first slice
                                const RADIAN = Math.PI / 180;
                                const { cx, cy, midAngle, innerRadius, outerRadius, startAngle, endAngle, fill } = props;
                                const sin = Math.sin(-RADIAN * midAngle);
                                const cos = Math.cos(-RADIAN * midAngle);
                                const sx = cx + (outerRadius + 10) * cos;
                                const sy = cy + (outerRadius + 10) * sin;
                                const mx = cx + (outerRadius + 30) * cos;
                                const my = cy + (outerRadius + 30) * sin;
                                const ex = mx + (cos >= 0 ? 1 : -1) * 22;
                                const ey = my;
                                return (
                                    <g>
                                        <path d={`M${sx},${sy}L${mx},${my}L${ex},${ey}`} stroke={fill} fill="none" />
                                        <circle cx={ex} cy={ey} r={2} fill={fill} stroke="none" />
                                        <Sector
                                            cx={cx}
                                            cy={cy}
                                            innerRadius={innerRadius}
                                            outerRadius={outerRadius + 10}
                                            startAngle={startAngle}
                                            endAngle={endAngle}
                                            fill={fill}
                                        />
                                    </g>
                                );
                            }}
                            explode={true}
                            explodeOffset={10}
                        >
                            {data.map((entry, idx) => (
                                <Cell
                                    key={`cell-${idx}`}
                                    fill={PIE_COLORS[idx % PIE_COLORS.length]}
                                    stroke="none"
                                    style={{
                                        opacity: hoverIndex === null ? 1 : (hoverIndex === idx ? 1 : 0.3),
                                        transition: 'opacity 0.3s ease',
                                        outline: 'none'
                                    }}
                                    onMouseEnter={() => setHoverIndex(idx)}
                                    onMouseLeave={() => setHoverIndex(null)}
                                />
                            ))}
                        </Pie>
                        <Tooltip
                            isAnimationActive={false}
                            content={({ active, payload }) => {
                                if (active && payload && payload.length) {
                                    const entry = payload[0].payload;
                                    return (
                                        <div style={{ background: '#222', color: '#fff', borderRadius: 8, padding: 12, minWidth: 120 }}>
                                            <div style={{ fontWeight: 600, fontSize: 16, marginBottom: 4 }}>{entry.mapped_item_name || entry.item_name}</div>
                                            <div style={{ fontWeight: 500 }}>Value: <span style={{ fontWeight: 700 }}>₹{Number(entry[dataKey]).toLocaleString('en-IN')}</span></div>
                                        </div>
                                    );
                                }
                                return null;
                            }}
                        />
                    </PieChart>
                </ResponsiveContainer>
            ) : (
                <div style={{ textAlign: 'center', color: '#ff6600', fontWeight: 500, padding: '2rem 0' }}>
                    {t('reports.noDataAvailable')}
                </div>
            )}
        </div>
    );

    // 🔹 handle period change
    const handlePeriodChange = (value) => {
        setSelectedPeriod(value);
        setDisplayText(value);
        setSelectedDate(null);
        setSelectedWeek(null); // Clear week selection
        setPeriodOpen(false);
    };

    const handleCalendarClick = () => {
        setPeriodOpen(false); // Close the period dropdown when calendar is clicked
        setShowDatePicker(true);
    };

    // Close period dropdown on outside click or Escape
    useEffect(() => {
        if (!periodOpen) return;
        const handleClick = (e) => {
            if (periodContainerRef.current && !periodContainerRef.current.contains(e.target)) {
                setPeriodOpen(false);
            }
        };
        const handleKey = (e) => {
            if (e.key === 'Escape') setPeriodOpen(false);
        };
        document.addEventListener('mousedown', handleClick);
        document.addEventListener('keydown', handleKey);
        return () => {
            document.removeEventListener('mousedown', handleClick);
            document.removeEventListener('keydown', handleKey);
        };
    }, [periodOpen]);

    const handleWeekSelect = (week) => {
        setSelectedWeek(week);
        setSelectedPeriod('');
        setSelectedDate(null); // Clear date selection
        // If single day, show DD/MM/YYYY, else show range
        const toDMY = (d) => `${String(new Date(d).getDate()).padStart(2,'0')}/${String(new Date(d).getMonth()+1).padStart(2,'0')}/${new Date(d).getFullYear()}`;
        const label = week.isSingleDay ? toDMY(week.start) : formatWeekDisplay(week);
        setDisplayText(label);
        setShowDatePicker(false);
    };

    const handleDateSelect = (date) => {
        setSelectedDate(date);
        setSelectedWeek(null); // Clear week selection when month is selected
        
        // Check if this is a month selection (from date picker) or single date
        if (date.isMonthSelection) {
            // Format as "October 2025" for month selections
            const monthNames = ['January', 'February', 'March', 'April', 'May', 'June',
                                'July', 'August', 'September', 'October', 'November', 'December'];
            const formattedDate = `${monthNames[date.month]} ${date.year}`;
            setDisplayText(formattedDate);
        } else {
            // Format as DD/MM/YYYY for single date selections
            const toDMY = (d) => `${String(d.getDate()).padStart(2,'0')}/${String(d.getMonth()+1).padStart(2,'0')}/${d.getFullYear()}`;
            const formattedDate = toDMY(date);
            setDisplayText(formattedDate);
        }
        setSelectedPeriod('');
        setShowDatePicker(false);
    };

    // key analysis
    // `keyAnalysis` was removed with the /reports/key-analysis call that fed it — nothing read it.

    // ===== Chart Data Calculation for Sales Trend =====
    let chartData = [];
    let grossProfitChartData = [];
    if (!isLoading && !error) {
        if (selectedWeek && selectedWeek.isWeekSelection) {
            // Week selection: Show 7 days breakdown (Sun-Sat)
            const weekDays = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
            
            // Map backend ISO date strings to weekday index using getUTCDay()
            const dayMap = {};
            const grossProfitMap = {};
            if (Array.isArray(salesTrend)) {
                salesTrend.forEach(entry => {
                    const dateObj = new Date(entry.label || entry.date);
                    const dayIdx = dateObj.getUTCDay();
                    const dayName = weekDays[dayIdx];
                    dayMap[dayName] = Number(entry.total_sales || 0);
                    grossProfitMap[dayName] = Number(entry.gross_profit || 0);
                });
            }
            
            // Display Sun-Sat in order
            chartData = weekDays.map(day => ({ label: day, total_sales: dayMap[day] || 0 }));
            grossProfitChartData = weekDays.map(day => ({ label: day, gross_profit: grossProfitMap[day] || 0 }));
            
        } else if (selectedDate && selectedDate.isMonthSelection) {
            // Month selection: Show 4 weeks breakdown (e.g., Week 1, Week 2, Week 3, Week 4)
            const weekLabels = ['Week 1', 'Week 2', 'Week 3', 'Week 4'];
            chartData = [0, 1, 2, 3].map(i => {
                const week = salesTrend[i];
                return {
                    label: weekLabels[i],
                    total_sales: week ? Number(week.total_sales || 0) : 0
                };
            });
            grossProfitChartData = [0, 1, 2, 3].map(i => {
                const week = salesTrend[i];
                return {
                    label: weekLabels[i],
                    gross_profit: week ? Number(week.gross_profit || 0) : 0
                };
            });
        } else if (selectedPeriod === 'This Month') {
            // Always show 4 weeks, use numeric values
            const weekLabels = ['Week 1', 'Week 2', 'Week 3', 'Week 4'];
            chartData = [0, 1, 2, 3].map(i => {
                const week = salesTrend[i];
                return {
                    label: weekLabels[i],
                    total_sales: week ? Number(week.total_sales) : 0
                };
            });
            grossProfitChartData = [0, 1, 2, 3].map(i => {
                const week = salesTrend[i];
                return {
                    label: weekLabels[i],
                    gross_profit: week ? Number(week.gross_profit || 0) : 0
                };
            });
        } else if (selectedPeriod === 'This Year') {
            // Always show 12 months, use numeric values
            const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
            chartData = months.map((label, i) => {
                const month = salesTrend[i];
                return {
                    label,
                    total_sales: month ? Number(month.total_sales) : 0
                };
            });
            grossProfitChartData = months.map((label, i) => {
                const month = salesTrend[i];
                return {
                    label,
                    gross_profit: month ? Number(month.gross_profit || 0) : 0
                };
            });
        } else if (selectedPeriod === 'This Week') {
            // Always show Mon-Sun, use numeric values
            const weekDays = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
            
            // Map backend ISO date strings to weekday index using getUTCDay()
            const dayMap = {};
            const grossProfitMap = {};
            salesTrend.forEach(entry => {
                const dateObj = new Date(entry.label);
                const dayIdx = dateObj.getUTCDay();
                const dayName = weekDays[dayIdx];
                dayMap[dayName] = Number(entry.total_sales);
                grossProfitMap[dayName] = Number(entry.gross_profit || 0);
            });
            
            // Display Mon-Sun in order
            const orderedDays = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
            chartData = orderedDays.map(day => ({ label: day, total_sales: dayMap[day] || 0 }));
            grossProfitChartData = orderedDays.map(day => ({ label: day, gross_profit: grossProfitMap[day] || 0 }));
            
        } else {
            // Default: show whatever is returned
            chartData = salesTrend.map(d => {
                const dateObj = new Date(d.label);
                return {
                    ...d,
                    label: dateObj.toLocaleDateString('en-US', { weekday: 'short' }),
                    total_sales: Number(d.total_sales)
                };
            });
            grossProfitChartData = salesTrend.map(d => {
                const dateObj = new Date(d.label);
                return {
                    ...d,
                    label: dateObj.toLocaleDateString('en-US', { weekday: 'short' }),
                    gross_profit: Number(d.gross_profit || 0)
                };
            });
        }
    }
    // A failed summary request leaves the previous range's figures in `summary`.
    // Exporting or sharing them under this period's name is worse than not at all.
    const staleSummaryToast = (action) => setExportToast({
        visible: true,
        message: `❌ This period's summary didn't load. Retry before ${action}.`,
        type: 'error'
    });

    const openExportPicker = () => {
        if (summaryUnavailable) {
            staleSummaryToast('exporting');
            return;
        }
        setShowExportPicker(true);
    };

    const handleShareReport = async (e) => {
        if (e) {
            e.preventDefault();
            e.stopPropagation();
        }
        if (summaryUnavailable) {
            staleSummaryToast('sharing');
            return;
        }

        // Show background generation status toast
        setExportToast({
            visible: true,
            message: '⏳ Preparing your report PDF... Please wait.',
            type: 'info'
        });

        try {
            // The same document Export produces: the period's /reports/enhanced-export
            // answer, assembled by buildSalesPdfData, rendered by the editorial PDF.
            // A failed detail fetch still shares the headline, profit and chart
            // sections — Export behaves the same way.
            let enhanced = null;
            try {
                const response = await http.get(`/reports/enhanced-export${salesEnhancedExportQuery({
                    selectedPeriod, selectedDate, selectedWeek,
                    businessId: resolveBusinessId() || '',
                    language: language || localStorage.getItem('selectedLanguage') || 'en',
                })}`);
                if (response && response.success) enhanced = response.data;
            } catch (err) {
                console.warn('Share: detailed report data unavailable, sharing the summary sections', err);
            }
            const data = buildSalesPdfData({
                summary, expenses, wastageExpenses, topProfitItems, chartData, dailyBreakdown,
                selectedPeriod, selectedDate, selectedWeek, displayText,
                enhanced,
                preparedFor: await getPreparedFor(),
                generated: new Date().toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric', timeZone: 'Asia/Kolkata' }),
                outletRows: outletBreakdown.rows,
                fixedShare: fixedShareFull,
            });
            const sections = salesPdfSections({ selectedPeriod, selectedDate, selectedWeek, topProfitItems, dailyBreakdown, outletRows: outletBreakdown.rows, fixedShare: fixedShareFull }).map((x) => x.id);
            const fileDate = new Date().toLocaleDateString('en-CA', { timeZone: 'Asia/Kolkata' });
            const fileName = `Sales_Report_${fileDate}.pdf`;
            const blob = await buildEditorialSalesPdfBlob(data, sections, fileName);
            const pdfFile = new File([blob], fileName, { type: 'application/pdf' });

            if (navigator.share && navigator.canShare && navigator.canShare({ files: [pdfFile] })) {
                await navigator.share({
                    title: 'Sales Report',
                    text: 'Sales report from Invexis',
                    files: [pdfFile]
                });
                
                // Hide background generation toast once native share sheet is shown
                setExportToast({ visible: false, message: '', type: 'success' });
                return;
            }

            // Fallback: download PDF if direct file share is unsupported.
            const blobUrl = URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = blobUrl;
            link.download = fileName;
            link.target = '_blank';
            link.rel = 'noopener noreferrer';
            document.body.appendChild(link);
            link.click();
            link.remove();
            URL.revokeObjectURL(blobUrl);
            
            // Show non-blocking success toast
            setExportToast({
                visible: true,
                message: '✅ Report PDF generated and downloaded successfully!',
                type: 'success'
            });
        } catch (error) {
            if (error?.name === 'AbortError') {
                setExportToast({ visible: false, message: '', type: 'success' });
                return;
            }
            console.error('Share failed:', error);
            setExportToast({
                visible: true,
                message: '❌ Unable to share report as PDF. Please try again.',
                type: 'error'
            });
        }
    };

    const getEnterDelayStyle = (delayMs = 0) => ({
        '--report-enter-delay': `${Math.min(Math.max(delayMs, 0), 420)}ms`,
    });

    const getStaggerDelayStyle = (index, baseDelay = 0, step = 55, maxDelay = 420) => (
        getEnterDelayStyle(Math.min(baseDelay + (index * step), maxDelay))
    );

    // Re-run the load for the range currently on screen. `refreshTrigger` is already a
    // dependency of the report effect, so bumping it repeats exactly the fetch that
    // failed — no separate retry path to keep in step with the real one.
    const retryReport = () => setRefreshTrigger((previous) => previous + 1);

    // True when the summary request itself failed. Its figures are then unknown, and the
    // page renders them as "—". `summary` still holds the previous range's zeros, and
    // printing those as this range's takings is the "GP shows 0" complaint exactly.
    const summaryUnavailable = sectionErrors.includes('summary');

    /** A summary figure, or an em dash when the request that produces it failed. */
    const summaryMoney = (value) => (summaryUnavailable ? '—' : `₹${formatIndian(value || 0)}`);

    return (
        <>
            <div className="report-container" ref={reportRef}>
                {!isLoading && (
                    <>
                        {/* ===== HEADER ===== */}
                        <header className="sr-top-header">
                            <div className="sr-top-header-row">
                                <button
                                    onClick={() => goTo('reports')}
                                    className="sr-top-back-btn"
                                    aria-label="Back to Reports"
                                >
                                    <ArrowLeftIcon />
                                </button>
                                <h1 className="sr-top-title">{t('reports.salesReports')}</h1>
                            </div>
                        </header>

                        <div className="sr-top-picker" ref={periodContainerRef}>
                            <button
                                type="button"
                                className="sr-top-calendar-btn"
                                onClick={handleCalendarClick}
                                aria-label="Open calendar"
                            >
                                <CalendarIcon />
                            </button>
                            <button
                                type="button"
                                className="sr-top-period-trigger"
                                onClick={() => setPeriodOpen(o => !o)}
                                aria-haspopup="listbox"
                                aria-expanded={periodOpen}
                            >
                                <span className="sr-top-period-label">
                                    {selectedWeek && selectedWeek.isWeekSelection ? formatWeekDisplay(selectedWeek) :
                                     selectedWeek && selectedWeek.isCustomRange ? displayText :
                                     selectedDate ? displayText :
                                     selectedPeriod === 'Today' ? t('reports.today') :
                                     selectedPeriod === 'This Week' ? t('reports.thisWeek') :
                                     selectedPeriod === 'This Month' ? t('reports.thisMonth') :
                                     selectedPeriod === 'This Year' ? t('reports.thisYear') :
                                     t('reports.today')}
                                </span>
                                <span className={`sr-top-chevron ${periodOpen ? 'open' : ''}`} />
                            </button>
                            {periodOpen && (
                                <div className="sr-top-period-menu">
                                    <div className="sr-top-select-list">
                                        <button type="button" className={`sr-top-period-option ${selectedPeriod === 'Today' ? 'active' : ''}`} onClick={() => handlePeriodChange('Today')}>{t('reports.today')}</button>
                                        <button type="button" className={`sr-top-period-option ${selectedPeriod === 'This Week' ? 'active' : ''}`} onClick={() => handlePeriodChange('This Week')}>Week</button>
                                        <button type="button" className={`sr-top-period-option ${selectedPeriod === 'This Month' ? 'active' : ''}`} onClick={() => handlePeriodChange('This Month')}>Month</button>
                                        <button type="button" className={`sr-top-period-option ${selectedPeriod === 'This Year' ? 'active' : ''}`} onClick={() => handlePeriodChange('This Year')}>Year</button>
                                        {selectedDate && (
                                            <button type="button" className={`sr-top-period-option ${selectedPeriod === '' ? 'active' : ''}`} onClick={() => { setSelectedPeriod(''); setDisplayText(displayText); setPeriodOpen(false); }}>{displayText}</button>
                                        )}
                                    </div>
                                </div>
                            )}
                        </div>
                    </>
                )}

                {/* Loading State */}
                {isLoading && (
                    <div style={{
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        minHeight: '100vh',
                        flexDirection: 'column',
                        gap: '20px',
                        width: '100%',
                        position: 'fixed',
                        top: 0,
                        left: 0,
                        zIndex: 999,
                        background: '#ffffff'
                    }}>
                        <div style={{ position: 'relative', width: '95px', height: '95px' }}>
                            <img 
                                src="/invexis-logo.png"
                                alt="Invexis"
                                style={{
                                    position: 'absolute',
                                    top: '50%',
                                    left: '50%',
                                    transform: 'translate(-50%, -50%)',
                                    width: '70px',
                                    height: '70px',
                                    borderRadius: '50%',
                                    backgroundColor: '#fff',
                                    padding: '0px',
                                    pointerEvents: 'none',
                                    objectFit: 'contain',
                                    boxShadow: '0 2px 12px rgba(0,0,0,0.15)',
                                    zIndex: 1
                                }}
                            />
                            <div style={{
                                position: 'absolute',
                                top: '50%',
                                left: '50%',
                                transform: 'translate(-50%, -50%)',
                                width: '70px',
                                height: '70px',
                                border: '6px solid #000000',
                                borderTop: '6px solid #f9c806',
                                borderRight: '6px solid #f9c806',
                                borderRadius: '50%',
                                animation: 'salesReportSpin 1s linear infinite',
                                zIndex: 16
                            }}></div>
                        </div>
                        <p style={{
                            color: '#666',
                            fontSize: '16px',
                            textAlign: 'center',
                            margin: 0
                        }}>Loading sales data...</p>
                        <style>{`
                            @keyframes salesReportSpin { 
                                0% { transform: translate(-50%, -50%) rotate(0deg); } 
                                100% { transform: translate(-50%, -50%) rotate(360deg); } 
                            }
                        `}</style>
                    </div>
                )}

                {/* Error State — now with a way out of it. A report that failed used to
                    leave the reader with a red box and no action but to change the range
                    and change it back. */}
                {error && (
                    <div style={{
                        backgroundColor: '#f8d7da',
                        color: '#721c24',
                        padding: '1rem',
                        borderRadius: '8px',
                        margin: '1rem 0',
                        border: '1px solid #f5c6cb',
                        textAlign: 'center'
                    }}>
                        <div style={{ marginBottom: '0.75rem' }}>{error}</div>
                        <button
                            type="button"
                            onClick={retryReport}
                            style={{
                                padding: '0.5rem 1.25rem',
                                borderRadius: '6px',
                                border: '1px solid #721c24',
                                background: '#fff',
                                color: '#721c24',
                                fontWeight: 600,
                                cursor: 'pointer'
                            }}
                        >
                            Retry
                        </button>
                    </div>
                )}

                {/* Partial-failure banner.
                    The summary arrived, so the page is usable, but one or more sections
                    did not. Every one of these used to be swallowed into a ₹0 — an
                    expenses request that lost its database connection under load read as
                    "no expenses this period", which is a different and much more
                    expensive statement. */}
                {!isLoading && !error && sectionErrors.length > 0 && (
                    <div style={{
                        backgroundColor: '#fff7ed',
                        color: '#9a3412',
                        padding: '0.75rem 1rem',
                        borderRadius: '8px',
                        margin: '0.75rem 0',
                        border: '1px solid #fdba74',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        gap: '0.75rem',
                        flexWrap: 'wrap',
                        fontSize: '13px',
                        lineHeight: 1.45
                    }}>
                        <span>
                            Couldn&apos;t load{' '}
                            <strong>
                                {sectionErrors
                                    .map((section) => REPORT_SECTION_LABELS[section] || section)
                                    .join(', ')}
                            </strong>
                            . {sectionErrors.length === 1 ? 'That figure is' : 'Those figures are'} shown as
                            blank rather than zero — the rest of this report is complete.
                        </span>
                        <button
                            type="button"
                            onClick={retryReport}
                            style={{
                                padding: '0.4rem 1rem',
                                borderRadius: '6px',
                                border: '1px solid #9a3412',
                                background: '#fff',
                                color: '#9a3412',
                                fontWeight: 600,
                                cursor: 'pointer',
                                whiteSpace: 'nowrap'
                            }}
                        >
                            Retry
                        </button>
                    </div>
                )}

                {/* Sections still arriving after the summary rendered. Without this the
                    screen looks finished while numbers are still moving. */}
                {!isLoading && !error && pendingSections.length > 0 && (
                    <div style={{
                        color: '#6b7280',
                        fontSize: '12px',
                        margin: '0.5rem 0 0.25rem',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0.5rem'
                    }}>
                        <span style={{
                            width: '10px',
                            height: '10px',
                            border: '2px solid #d1d5db',
                            borderTopColor: '#6b7280',
                            borderRadius: '50%',
                            display: 'inline-block',
                            animation: 'salesReportInlineSpin 0.8s linear infinite'
                        }} />
                        Loading {pendingSections.length} more section{pendingSections.length === 1 ? '' : 's'}…
                    </div>
                )}

                {/* ===== SUMMARY CARDS =====
                    Gated on `fatalError`, not on `error`.

                    `error` is set when the SUMMARY request fails, and this block used to be
                    hidden whenever it was — which hid the whole report, including the
                    sections that had loaded perfectly well. On a heavy range that is what
                    "the data is not displaying" actually was: /reports/daily-breakdown had
                    come back in 4 ms and was being thrown away because a different request
                    was still grinding.

                    Now only a fatal error (no business context — nothing can be shown at
                    all) hides the report. A failed summary shows its own figures as "—",
                    keeps its error box and Retry above, and lets everything else render. */}
                {!isLoading && !fatalError && (
                    <main>
                        <div className="stats-grid" id="sales-summary">
                            {/* Total Sales - Clickable */}
                            <div
                                className="sr-stat-card report-enter-card"
                                style={{ cursor: 'pointer', ...getEnterDelayStyle(40) }}
                                onClick={fetchSalesDetail}
                            >
                                <p className="card-title" style={{ color: 'black', marginBottom: '0.25rem' }}>{t('reports.totalSales')}</p>
                                <p className="card-value" style={{ marginBottom: '-0.25rem' }}>{summaryMoney(summary.total_sales)}</p>
                                {/* The card is the whole bill — menu lines plus the ones that never
                                    resolved to a menu item — so it agrees with the Sales Detail sheet
                                    it opens. Naming the non-menu share here is what makes the two
                                    reconcilable at a glance instead of merely equal. */}
                                {!summaryUnavailable && Number(summary.unmapped_revenue || 0) > 0 && (
                                    <p style={{ fontSize: 10, color: '#6b7280', margin: 0, marginTop: 6, lineHeight: '1.25' }} onClick={(e) => { e.stopPropagation(); setNimOpen(true); }}>
                                        <span
                                            role="button"
                                            tabIndex={0}
                                            className="sr-nim-link"
                                            style={{ textDecoration: 'underline', cursor: 'pointer' }}
                                            onClick={(e) => { e.stopPropagation(); setNimOpen(true); }}
                                            onKeyDown={(e) => { if (e.key === 'Enter') { e.stopPropagation(); setNimOpen(true); } }}
                                        >
                                            incl. ₹{formatIndian(Number(summary.unmapped_revenue))} not in menu ›
                                        </span>
                                    </p>
                                )}
                                {/* Under the business's Sales − COGS − Discounts formula the card
                                    is every channel's takings; say how much of it came online. */}
                                {!summaryUnavailable && isSalesCogsDiscount(summary) && Number(summary.online_sales || 0) > 0 && (
                                    <p style={{ fontSize: 10, color: '#6b7280', margin: 0, marginTop: 4, lineHeight: '1.25' }}>
                                        incl. ₹{formatIndian(Number(summary.online_sales))} online (delivery platforms)
                                    </p>
                                )}
                                <p style={{ fontSize: 9, color: '#aaa', margin: 0, marginTop: 4, lineHeight: '1.2' }}>Click to view details</p>
                            </div>
                            {/* Expense (Stock-In Entries) - Clickable */}
                            <div
                              className="sr-stat-card report-enter-card"
                              style={{ cursor: 'pointer', ...getEnterDelayStyle(120) }}
                              onClick={fetchExpenseDetails}
                            >
                                <p className="card-title" style={{ color: 'black', marginBottom: '0.25rem' }}>{t('reports.expense')}</p>
                                <p className="card-value" style={{ marginBottom: '-0.25rem' }}>
                                    ₹{formatIndian(Number(expenses.total_expenses || 0) + Number(wastageExpenses.total_expenses || 0))}
                                </p>
                                {/* Stock-In + wastage only. Fixed expenses are listed in the details window's
                                    Fixed Expense tab but, for now, are NOT added to this figure. */}
                                <p style={{ fontSize: 9, color: '#aaa', margin: 0, marginTop: 4, lineHeight: '1.2' }}>Click to view details</p>
                            </div>
                            {/* Gross Profit — net of wastage. Sales - COGS - wastage. */}
                            <div className="sr-stat-card report-enter-card" style={getEnterDelayStyle(200)}>
                                {(() => {
                                    // Reads net_profit (= gross_profit - total_wastage), by product decision:
                                    // the manufacturing cost of wasted plates must come off this figure, so
                                    // recording wastage visibly reduces it.
                                    //
                                    // Deliberate, and worth knowing before you "fix" it:
                                    //   - The backend keeps the two apart on purpose ("Gross profit should
                                    //     only reflect sales minus sold-item COGS. Wastage is reported
                                    //     separately as net profit impact." — reports.js). Both figures are
                                    //     returned; this card chooses the net one.
                                    //   - The "Top 5 By Gross-Profit" chart below sums per-item GROSS profit,
                                    //     so it will total MORE than this card by exactly the wastage amount.
                                    //     That gap is expected, not a bug.
                                    //   - The Gross Profit vs Manufacturing Cost panel further down still
                                    //     reads display_gross_profit, for the same reason.
                                    //
                                    // Falls back through gross_profit so the card still renders if a caller
                                    // omits the net fields.
                                    const grossProfitSigned = Number((summary.display_net_profit ?? summary.net_profit ?? summary.display_gross_profit ?? summary.gross_profit) ?? 0);
                                    const grossProfitIsLoss = grossProfitSigned < 0;
                                    // A2: what the subtotal above leaves out.
                                    const excludedCogsCount = Number(summary.excluded_cogs_item_count || 0);
                                    const excludedCogsRevenue = Number(summary.excluded_cogs_revenue || 0);
                                    const excludedCogsNames = (summary.excluded_cogs_items || [])
                                        .map((i) => i.item_name).filter(Boolean).join(', ');
                                    // Non-menu lines are COSTED now (at the business's measured rate), so they
                                    // are part of this figure rather than an exclusion from it. What the badge
                                    // still names is revenue nothing could price at all: a dish whose
                                    // ingredients have no price history, on the days it could not be costed.
                                    //
                                    // That the non-menu cost is an estimate is said by the "~" marker and the
                                    // estimated-share note, which is the vocabulary this card already uses for
                                    // money that is in the total but was not measured directly.
                                    const notInMenuCogs = summary.unmapped_cogs;
                                    const notInMenuEstimated = !!summary.unmapped_cogs_estimated;
                                    const notInMenuRate = Number(summary.unmapped_cost_ratio || 0);
                                    return (
                                        <>
                                            <p className="card-title" style={{ color: 'black' }}>
                                                {grossProfitIsLoss ? 'Net Loss' : t('reports.grossProfit')}
                                            </p>
                                            <p className="card-value" style={{ color: grossProfitIsLoss ? '#FF3B30' : undefined }}>
                                                {/* Dashes with the rest of the summary when the summary REQUEST
                                                    failed — otherwise this card kept showing the previous
                                                    period's profit beside a dashed Total Sales, which reads as
                                                    a figure for the period on screen and is not one. */}
                                                {summaryUnavailable || summary.all_cogs_unavailable
                                                    ? '-'
                                                    : `${grossProfitSigned < 0 ? '−' : ''}₹${formatIndian(Math.abs(grossProfitSigned))}`}
                                            </p>
                                            {/* The business's own formula, stated with its terms so the card
                                                can be checked against the Total Sales card beside it. */}
                                            {!summaryUnavailable && !summary.all_cogs_unavailable && isSalesCogsDiscount(summary) && (
                                                <p style={{ fontSize: '11px', color: '#6b7280', marginTop: '2px', lineHeight: 1.35 }}>
                                                    sales − COGS ₹{formatIndian(Number(summary.total_cogs || 0))} − discounts ₹{formatIndian(Number(summary.total_discount || 0))}
                                                </p>
                                            )}
                                            {/* A2. Name what the figure leaves out, and size it. An unqualified
                                                total that quietly omits two dishes is a worse lie than the dash
                                                this replaced; a total that says which two is just a subtotal. */}
                                            {!summaryUnavailable && !summary.all_cogs_unavailable && excludedCogsCount > 0 && (
                                                <p style={{ fontSize: '11px', color: '#6b7280', marginTop: '2px', lineHeight: 1.35 }}
                                                   title={excludedCogsNames}>
                                                    excludes {excludedCogsCount} unpriced item{excludedCogsCount === 1 ? '' : 's'}
                                                    {excludedCogsRevenue > 0 ? ` (₹${formatIndian(excludedCogsRevenue)} sales)` : ''}
                                                </p>
                                            )}
                                            {/* The non-menu half of the bill has no recipe to cost from, so its cost
                                                is the business's own measured rate applied to its own sales. Saying
                                                the rate out loud is what makes the figure checkable. */}
                                            {!summaryUnavailable && !summary.all_cogs_unavailable && notInMenuEstimated && notInMenuCogs > 0 && (
                                                <p style={{ fontSize: '11px', color: '#6b7280', marginTop: '2px', lineHeight: 1.35 }}
                                                   title={`Bill lines with no menu item have no recipe to cost from. They are charged at ${(notInMenuRate * 100).toFixed(1)}% — this period's own measured food-cost rate — so they carry a cost instead of booking as pure margin.`}>
                                                    incl. ~₹{formatIndian(notInMenuCogs)} estimated cost on not-in-menu sales
                                                </p>
                                            )}
                                        </>
                                    );
                                })()}
                            </div>
                            {/* Daily Contribution */}
                            <div className="sr-stat-card report-enter-card" style={{ cursor: 'pointer', ...getEnterDelayStyle(280) }} onClick={() => {
                                // open modal to edit BEP (in sync: the fixed-expense ledger's figure)
                                const startValue = bepSyncOn && bepLedger && bepLedger.monthly != null ? bepLedger.monthly : fixedMonthlyExpense;
                                setBepAmount(startValue ? String(startValue) : '');
                                setBepPeriod('Month');
                                // compute initial breakdown (will be recalculated inside modal on change)
                                const monthly = startValue ? Number(startValue) : 0;
                                const daysInMonth = (() => { const d = new Date(); return new Date(d.getFullYear(), d.getMonth() + 1, 0).getDate(); })();
                                setBepBreakdown({ daily: monthly ? monthly / daysInMonth : 0, weekly: monthly ? monthly / 4 : 0, monthly: monthly || 0, yearly: monthly ? monthly * 12 : 0 });
                                setBepModalOpen(true);
                            }}>
                                <p className="card-title" style={{ color: 'black' }}>
                                    {contributionLabel(t, { selectedPeriod, selectedDate, selectedWeek })}
                                </p>
                                <p className="card-value">₹{formatIndian((bepSyncOn && bepLedger.monthly != null
                                    ? contributionTarget(computeBepBreakdown(bepLedger.monthly, 'Month', bepBasisDate(selectedDate, selectedWeek)), { selectedPeriod, selectedDate, selectedWeek })
                                    : dailyBepTarget) || 0)}</p>
                                {bepSyncOn ? (
                                    <p style={{ marginTop: 4, fontSize: 12, color: '#6b7280' }}>break-even target · from fixed expenses</p>
                                ) : fixedMonthlyExpense === null ? (
                                    <p style={{ marginTop: 8, fontSize: 12 }}>{t('reports.clickToSetBep')}</p>
                                ) : (
                                    <p style={{ marginTop: 4, fontSize: 12, color: '#6b7280' }}>break-even target</p>
                                )}
                            </div>
                        </div>

                        {/* Sales by Payment section removed as requested */}

                        {/* ===== BEP Modal (opened when clicking Daily Contribution card) ===== */}
                        {bepModalOpen && (
                            <div className="bep-modal-overlay" onClick={() => setBepModalOpen(false)}>
                                <div className="bep-modal-container" onClick={e => e.stopPropagation()}>
                                    <h3 className="bep-modal-title">Set Break-Even Point Target</h3>
                                    <p className="bep-modal-subtitle">Enter your total estimated expenses</p>

                                    {bepLedger && bepLedger.hasLedger && (
                                        <div style={{ background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: 10, padding: '10px 12px', margin: '4px 0 14px', textAlign: 'left' }}>
                                            {bepLedger.monthly != null && (
                                                <button
                                                    type="button"
                                                    disabled={bepSyncOn}
                                                    onClick={() => {
                                                        setBepAmount(String(bepLedger.monthly));
                                                        setBepPeriod('Month');
                                                        setBepBreakdown(computeBepBreakdown(bepLedger.monthly, 'Month', bepBasisDate(selectedDate, selectedWeek)));
                                                    }}
                                                    style={{ width: '100%', padding: '9px 12px', borderRadius: 8, border: '1px solid #111111', background: bepSyncOn ? '#f3f4f6' : '#ffffff', color: '#111111', fontSize: 13, fontWeight: 600, cursor: bepSyncOn ? 'default' : 'pointer' }}
                                                >
                                                    Use my fixed expenses — ₹{formatIndian(bepLedger.monthly)} a month
                                                </button>
                                            )}
                                            {fixedShare.data && fixedShare.data.viewer && fixedShare.data.viewer.canEdit && (
                                                <label style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, marginTop: bepLedger.monthly != null ? 10 : 0, fontSize: 13, cursor: 'pointer' }}>
                                                    <span>
                                                        <strong>Keep in sync</strong>
                                                        <span style={{ display: 'block', fontSize: 11, color: '#6b7280', marginTop: 2 }}>
                                                            Salaries + fixed + spread purchases + monthly bills. The target includes salaries and is shown to everyone who opens this report. Off: your typed target is never overwritten.
                                                        </span>
                                                    </span>
                                                    <input type="checkbox" checked={bepSyncOn} disabled={bepSyncSaving} onChange={(e) => toggleBepSync(e.target.checked)} />
                                                </label>
                                            )}
                                        </div>
                                    )}

                                    <div className="bep-input-row">
                                        <div className="bep-amount-input">
                                            <span style={{ fontSize: 18 }}>₹</span>
                                            <input type="number" value={bepAmount} onChange={e => {
                                                const v = e.target.value;
                                                setBepAmount(v);
                                                const br = computeBepBreakdown(v, bepPeriod, selectedDate || new Date());
                                                setBepBreakdown(br);
                                            }} placeholder="Amount" />
                                        </div>
                                        <div className="bep-period-selector">
                                            <button type="button"
                                                onClick={() => setBepPeriodOpen(o => !o)}
                                                className="bep-period-button"
                                            >
                                                <span>{bepPeriod}</span>
                                                <span className="bep-period-arrow" />
                                            </button>
                                            {bepPeriodOpen && (
                                                <div className="bep-period-dropdown">
                                                    {['Month','Year','Week','Day'].map(opt => (
                                                        <div key={opt}
                                                            onClick={() => {
                                                                setBepPeriod(opt);
                                                                const br = computeBepBreakdown(bepAmount, opt, selectedDate || new Date());
                                                                setBepBreakdown(br);
                                                                setBepPeriodOpen(false);
                                                            }}
                                                            className={`bep-period-option ${bepPeriod === opt ? 'active' : ''}`}
                                                        >{opt}</div>
                                                    ))}
                                                </div>
                                            )}
                                        </div>
                                    </div>

                                    <div className="bep-breakdown-box">
                                        <strong className="bep-breakdown-title">BEP Targets Breakdown:</strong>
                                        <div className="bep-breakdown-content">
                                            <div className="bep-breakdown-grid">
                                                <div className="bep-breakdown-item left">Daily: ₹{(bepBreakdown.daily || 0).toFixed(0)}</div>
                                                <div className="bep-breakdown-item right">Monthly: ₹{(bepBreakdown.monthly || 0).toFixed(0)}</div>
                                                <div className="bep-breakdown-item left">Weekly: ₹{(bepBreakdown.weekly || 0).toFixed(0)}</div>
                                                <div className="bep-breakdown-item right">Yearly: ₹{(bepBreakdown.yearly || 0).toFixed(0)}</div>
                                            </div>
                                        </div>
                                    </div>

                                    <div className="bep-button-row">
                                        <button
                                            onClick={() => setBepModalOpen(false)}
                                            className="bep-button bep-button-cancel"
                                        >
                                            Cancel
                                        </button>
                                        <button
                                            onClick={saveBepSetting}
                                            disabled={isSavingBep}
                                            className="bep-button bep-button-save"
                                        >
                                            {isSavingBep ? 'Saving...' : 'Save'}
                                        </button>
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* ===== EXPENSE DETAILS MODAL ===== */}
                        {showExpenseModal && (
                            <div className="expense-modal-overlay" onClick={() => setShowExpenseModal(false)}>
                                <div className="expense-modal-container" onClick={e => e.stopPropagation()}>
                                    {/* Modal Header */}
                                    <div className="expense-modal-header" style={{ 
                                        padding: '24px', 
                                        borderBottom: '1px solid #e5e7eb',
                                        background: '#fff',
                                        color: '#000000'
                                    }}>
                                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                            <div style={{ textAlign: 'left' }}>
                                                <h3 className="expense-modal-title" style={{ margin: 0, fontSize: '18px', fontWeight: 600, color: '#000000', textAlign: 'left' }}>Expense Details</h3>
                                                <p className="expense-modal-subtitle" style={{ margin: '4px 0 0 0', fontSize: '16px', color: '#6b7280', textAlign: 'left' }}>
                                                    {expenseTab === 'stockin'
                                                        ? 'Stock-In records'
                                                        : expenseTab === 'stockout'
                                                        ? 'Stock-Out (Wastage) records'
                                                        : expenseTab === 'fixed'
                                                        ? 'Fixed expense records'
                                                        : 'Non Chargeable (NC) records'}
                                                </p>
                                            </div>
                                            <button onClick={() => setShowExpenseModal(false)} style={{ 
                                                background: 'rgb(255, 255, 255)', 
                                                border: 'none', 
                                                borderRadius: '50%', 
                                                width: '32px', 
                                                height: '32px', 
                                                cursor: 'pointer',
                                                color: '#000000',
                                                fontSize: '20px',
                                                marginTop: '-20px',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center'
                                            }}>×</button>
                                        </div>
                                        
                                        {/* Tabs */}
                                        <div style={{ display: 'flex', gap: '8px', marginTop: '14px', flexWrap: 'wrap' }}>
                                            <button
                                                onClick={() => setExpenseTab('stockin')}
                                                style={{
                                                    padding: '6px 12px',
                                                    borderRadius: '8px',
                                                    border: expenseTab === 'stockin' ? '1px solid #111111' : '1px solid #e5e7eb',
                                                    background: expenseTab === 'stockin' ? '#111111' : '#ffffff',
                                                    color: expenseTab === 'stockin' ? '#ffffff' : '#111111',
                                                    fontSize: '13px',
                                                    fontWeight: 600,
                                                    cursor: 'pointer'
                                                }}
                                            >
                                                Stock-In
                                            </button>
                                            <button
                                                onClick={() => setExpenseTab('stockout')}
                                                style={{
                                                    padding: '6px 12px',
                                                    borderRadius: '8px',
                                                    border: expenseTab === 'stockout' ? '1px solid #111111' : '1px solid #e5e7eb',
                                                    background: expenseTab === 'stockout' ? '#111111' : '#ffffff',
                                                    color: expenseTab === 'stockout' ? '#ffffff' : '#111111',
                                                    fontSize: '13px',
                                                    fontWeight: 600,
                                                    cursor: 'pointer'
                                                }}
                                            >
                                                Stock-Out
                                            </button>
                                            <button
                                                onClick={() => setExpenseTab('nc')}
                                                style={{
                                                    padding: '6px 12px',
                                                    borderRadius: '8px',
                                                    border: expenseTab === 'nc' ? '1px solid #111111' : '1px solid #e5e7eb',
                                                    background: expenseTab === 'nc' ? '#111111' : '#ffffff',
                                                    color: expenseTab === 'nc' ? '#ffffff' : '#111111',
                                                    fontSize: '13px',
                                                    fontWeight: 600,
                                                    cursor: 'pointer'
                                                }}
                                            >
                                                Non Chargeable - NC
                                            </button>
                                            <button
                                                onClick={() => setExpenseTab('fixed')}
                                                style={{
                                                    padding: '6px 12px',
                                                    borderRadius: '8px',
                                                    border: expenseTab === 'fixed' ? '1px solid #111111' : '1px solid #e5e7eb',
                                                    background: expenseTab === 'fixed' ? '#111111' : '#ffffff',
                                                    color: expenseTab === 'fixed' ? '#ffffff' : '#111111',
                                                    fontSize: '13px',
                                                    fontWeight: 600,
                                                    cursor: 'pointer'
                                                }}
                                            >
                                                Fixed Expense
                                            </button>
                                        </div>

                                        {/* Summary Stats */}
                                        {expenseTab === 'fixed' && (
                                            <FixedExpenseHeader
                                                view={fixedShareView}
                                                query={fixedSearchQuery}
                                                setQuery={setFixedSearchQuery}
                                                field={fixedSearchField}
                                                setField={setFixedSearchField}
                                            />
                                        )}
                                        {expenseTab !== 'fixed' && (
                                        <div className="expense-summary-stats" style={{ display: 'flex', gap: '16px', marginTop: '16px' }}>
                                            <div className="expense-summary-card" style={{ 
                                                flex: '1',
                                                background: '#fff', 
                                                padding: '16px 20px', 
                                                borderRadius: '8px',
                                                color: '#000000',
                                                border: '1px solid #e5e7eb'
                                            }}>
                                                <div className="expense-summary-label" style={{ fontSize: '14px', color: '#6b7280' }}>Total Expenses</div>
                                                <div className="expense-summary-value" style={{ fontSize: '1.125rem', fontWeight: 600, marginTop: '8px', color: '#000000' }}>
                                                    {expenseTab === 'stockout' && stockOutHasUnavailable
                                                        ? '-'
                                                        : `₹${(expenseTab === 'stockin'
                                                            ? filteredStockInTotal
                                                            : expenseTab === 'nc'
                                                            ? ncExpenseTotal
                                                            : stockOutExpenseTotal).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}
                                                </div>
                                            </div>
                                            <div className="expense-summary-card" style={{ 
                                                flex: '1',
                                                background: '#fff', 
                                                padding: '16px 20px', 
                                                borderRadius: '8px',
                                                color: '#000000',
                                                border: '1px solid #e5e7eb'
                                            }}>
                                                <div className="expense-summary-label" style={{ fontSize: '14px', color: '#6b7280' }}>
                                                    {searchedStockInQuantitySummary ? 'Quantity' : 'Total Records'}
                                                </div>
                                                <div className="expense-summary-value" style={{ fontSize: '1.125rem', fontWeight: 600, marginTop: '8px', color: '#000000' }}>
                                                    {searchedStockInQuantitySummary
                                                        ? searchedStockInQuantitySummary
                                                        : expenseTab === 'stockin'
                                                        ? filteredStockInRecords.length
                                                        : expenseTab === 'nc'
                                                        ? ncExpenseRecords.length
                                                        : stockOutExpenseRecords.length}
                                                </div>
                                            </div>
                                        </div>
                                        )}

                                        {expenseTab === 'stockin' && (
                                            <div className="expense-search-filter-row">
                                                <div className="expense-search-wrapper">
                                                    <StandardSearch
                                                        value={stockInSearchQuery}
                                                        onChange={setStockInSearchQuery}
                                                        onClear={() => setStockInSearchQuery('')}
                                                        placeholder={stockInSearchFilter === 'item' ? 'Search items...' : 'Search vendors...'}
                                                        showClearButton={true}
                                                        style={{
                                                            marginBottom: '0',
                                                            borderRadius: '10px',
                                                            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
                                                            height: '44px',
                                                            padding: '0 0 0 16px'
                                                        }}
                                                    />
                                                </div>
                                                <div className="expense-dropdown-filter-container" ref={stockInFilterDropdownRef}>
                                                    <button
                                                        type="button"
                                                        className="expense-dropdown-filter-btn"
                                                        onClick={() => setStockInFilterDropdownOpen((prev) => !prev)}
                                                        title="Filter search by"
                                                    >
                                                        <span className="expense-dropdown-filter-value">{stockInSearchFilter === 'item' ? 'Item' : 'Vendor'}</span>
                                                        <FontAwesomeIcon icon={faAngleDown} className="expense-dropdown-arrow" />
                                                    </button>
                                                    {stockInFilterDropdownOpen && (
                                                        <div className="expense-dropdown-menu">
                                                            <button
                                                                type="button"
                                                                className={`expense-dropdown-option ${stockInSearchFilter === 'item' ? 'active' : ''}`}
                                                                onClick={() => {
                                                                    setStockInSearchFilter('item');
                                                                    setStockInFilterDropdownOpen(false);
                                                                }}
                                                            >
                                                                Item
                                                            </button>
                                                            <button
                                                                type="button"
                                                                className={`expense-dropdown-option ${stockInSearchFilter === 'vendor' ? 'active' : ''}`}
                                                                onClick={() => {
                                                                    setStockInSearchFilter('vendor');
                                                                    setStockInFilterDropdownOpen(false);
                                                                }}
                                                            >
                                                                Vendor
                                                            </button>
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                    
                                    {/* Modal Body - Scrollable */}
                                    <div className="expense-modal-body expense-modal-scroll" style={{ 
                                        flex: 1,
                                        display: 'flex',
                                        flexDirection: 'column',
                                        overflow: 'hidden',
                                        minHeight: 0,
                                        padding: '24px',
                                        boxSizing: 'border-box'
                                    }}>
                                        {expenseTab === 'fixed' ? (
                                            <FixedExpenseBody
                                                state={fixedShare}
                                                view={fixedShareView}
                                                query={fixedSearchQuery}
                                                field={fixedSearchField}
                                                onRetry={fetchFixedShare}
                                                onOpenCard={(c) => openFixedInExpenses(c.sourceKind === 'worker'
                                                    ? { route: 'worker', workerId: c.sourceId, month: c.month }
                                                    : { route: 'item', itemId: c.sourceId, month: c.month })}
                                                onOpenGroup={(g) => openFixedInExpenses({ route: 'staff', month: g.cards.reduce((m, c) => (c.month && c.month > m ? c.month : m), '') || undefined })}
                                                onSetup={() => openFixedInExpenses({ route: 'add' })}
                                            />
                                        ) : expenseLoading ? (
                                            <div style={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                flex: 1,
                                                minHeight: '100%',
                                                textAlign: 'center',
                                                color: '#9ca3af'
                                            }}>
                                                <p style={{ fontSize: '16px' }}>Loading expense details...</p>
                                            </div>
                                        ) : expenseError ? (
                                            <div style={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                flex: 1,
                                                minHeight: '100%',
                                                textAlign: 'center',
                                                color: '#b91c1c',
                                                flexDirection: 'column',
                                                gap: '12px'
                                            }}>
                                                <p style={{ fontSize: '16px', margin: 0 }}>❌ {expenseError}</p>
                                                <button
                                                    type="button"
                                                    onClick={fetchExpenseDetails}
                                                    style={{
                                                        padding: '8px 16px',
                                                        borderRadius: '8px',
                                                        border: '1px solid #111111',
                                                        background: '#111111',
                                                        color: '#ffffff',
                                                        fontSize: '13px',
                                                        fontWeight: 600,
                                                        cursor: 'pointer'
                                                    }}
                                                >
                                                    Retry
                                                </button>
                                            </div>
                                        ) : activeExpenseRecords.length === 0 ? (
                                              <div style={{
                                                  display: 'flex',
                                                  alignItems: 'center',
                                                  justifyContent: 'center',
                                                  flex: 1,
                                                  minHeight: '100%',
                                                  transform: 'translateY(-8%)',
                                                  textAlign: 'center',
                                                  color: '#9ca3af'
                                            }}>
                                                <p style={{ fontSize: '16px' }}>
                                                    {expenseTab === 'stockin'
                                                        ? (stockInSearchQuery.trim()
                                                            ? `No stock-in records found for ${stockInSearchFilter} "${stockInSearchQuery.trim()}"`
                                                            : 'No stock-in records found for this period')
                                                        : expenseTab === 'nc'
                                                        ? 'No NC (Non Chargeable) records found for this period'
                                                        : 'No stock-out (wastage) records found for this period'}
                                                </p>
                                            </div>
                                        ) : (
                                            <div className="expense-records-scroll-area" style={{
                                                display: 'flex',
                                                flexDirection: 'column',
                                                gap: '12px',
                                                minHeight: '100%'
                                            }}>
                                                {expenseTab === 'stockin' && filteredStockInRecords.map((record, idx) => {
                                                    const displayRecord = normalizeStockInRecord(record);
                                                    const lineItems = Array.isArray(displayRecord.line_items) ? displayRecord.line_items : [];
                                                    const itemsCount = displayRecord.items_count || lineItems.length;

                                                    return (
                                                    <div key={idx} className="expense-record-card" style={{ 
                                                        border: '1px solid #e5e7eb', 
                                                        borderRadius: '12px', 
                                                        padding: '16px',
                                                        background: '#fff',
                                                        transition: 'all 0.2s',
                                                        cursor: record.image_url ? 'pointer' : 'default'
                                                    }}
                                                    onMouseEnter={(e) => {
                                                        if (record.image_url) {
                                                            e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.1)';
                                                            e.currentTarget.style.transform = 'translateY(-2px)';
                                                        }
                                                    }}
                                                    onMouseLeave={(e) => {
                                                        if (record.image_url) {
                                                            e.currentTarget.style.boxShadow = 'none';
                                                            e.currentTarget.style.transform = 'translateY(0)';
                                                        }
                                                    }}
                                                    onClick={() => {
                                                        if (record.image_url) {
                                                            window.open(record.image_url, '_blank');
                                                        }
                                                    }}>
                                                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: '12px' }}>
                                                            {/* Left: Info */}
                                                            <div style={{ flex: 1, minWidth: '200px' }}>
                                                                <div className="expense-record-header" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '8px', marginBottom: '8px' }}>
                                                                    <div className="expense-badges" style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                                                                        <span className="expense-price" style={{ 
                                                                            fontSize: '1.125rem', 
                                                                            fontWeight: 600, 
                                                                            color: '#000000' 
                                                                        }}>
                                                                            ₹{Number(displayRecord.total_cost || 0).toLocaleString('en-IN')}
                                                                        </span>
                                                                        <span className="expense-badge" style={{ 
                                                                            background: record.is_negative_fix
                                                                                ? '#fee2e2'
                                                                                : (record.entry_method === 'Scan Bill' ? '#dbeafe' : '#e0e7ff'),
                                                                            color: record.is_negative_fix
                                                                                ? '#b91c1c'
                                                                                : (record.entry_method === 'Scan Bill' ? '#1e40af' : '#4338ca'),
                                                                            padding: '2px 8px',
                                                                            borderRadius: '6px',
                                                                            fontSize: '12px',
                                                                            fontWeight: 500
                                                                        }}>
                                                                            {record.is_negative_fix ? 'Negative Fix' : (record.entry_method || 'Manual Entry')}
                                                                        </span>
                                                                        {record.image_url && (
                                                                            <span style={{ 
                                                                                background: '#fef3c7',
                                                                                color: '#92400e',
                                                                                padding: '2px 8px',
                                                                                borderRadius: '6px',
                                                                                fontSize: '11px',
                                                                                fontWeight: 500
                                                                            }}>
                                                                                📄 Bill Available
                                                                            </span>
                                                                        )}
                                                                        
                                                                        {/* Revert Button - stays enabled until the next TSR is given */}
                                                                        {isStockInTsrLocked(record) && !unlockedRecords[record.stock_in_id] ? (
                                                                            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                                                                <button
                                                                                    disabled={true}
                                                                                    style={{
                                                                                        background: '#f3f4f6',
                                                                                        color: '#9ca3af',
                                                                                        border: '1px solid #e5e7eb',
                                                                                        borderRadius: '6px',
                                                                                        padding: '4px 10px',
                                                                                        fontSize: '12px',
                                                                                        fontWeight: 500,
                                                                                        cursor: 'not-allowed',
                                                                                        display: 'flex',
                                                                                        alignItems: 'center',
                                                                                        gap: '4px',
                                                                                        opacity: 0.6
                                                                                    }}
                                                                                    title={stockInLockTitle(record)}
                                                                                >
                                                                                    Revert
                                                                                </button>
                                                                                <button
                                                                                    onClick={(e) => {
                                                                                        e.stopPropagation();
                                                                                        setPinModalRecord(record);
                                                                                        setShowPinModal(true);
                                                                                    }}
                                                                                    style={{
                                                                                        background: '#fef2f2',
                                                                                        color: '#ef4444',
                                                                                        border: '1px solid #fee2e2',
                                                                                        borderRadius: '6px',
                                                                                        padding: '4px 8px',
                                                                                        fontSize: '12px',
                                                                                        cursor: 'pointer',
                                                                                        display: 'flex',
                                                                                        alignItems: 'center',
                                                                                        justifyContent: 'center',
                                                                                        transition: 'all 0.2s'
                                                                                    }}
                                                                                    title="Click to enter Admin PIN and unlock editing/reverting"
                                                                                    onMouseEnter={(e) => {
                                                                                        e.currentTarget.style.background = '#fee2e2';
                                                                                    }}
                                                                                    onMouseLeave={(e) => {
                                                                                        e.currentTarget.style.background = '#fef2f2';
                                                                                    }}
                                                                                >
                                                                                    <FontAwesomeIcon icon={faLock} />
                                                                                </button>
                                                                            </div>
                                                                        ) : (
                                                                            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                                                                <button
                                                                                    onClick={(e) => {
                                                                                        e.stopPropagation();
                                                                                        setRevertConfirm({ visible: true, stockInId: record.stock_in_id, record });
                                                                                    }}
                                                                                    style={{
                                                                                        background: '#fee2e2',
                                                                                        color: '#dc2626',
                                                                                        border: '1px solid #fecaca',
                                                                                        borderRadius: '6px',
                                                                                        padding: '4px 10px',
                                                                                        fontSize: '12px',
                                                                                        fontWeight: 500,
                                                                                        cursor: 'pointer',
                                                                                        display: 'flex',
                                                                                        alignItems: 'center',
                                                                                        gap: '4px',
                                                                                        transition: 'all 0.2s',
                                                                                        opacity: 1
                                                                                    }}
                                                                                    onMouseEnter={(e) => {
                                                                                        e.currentTarget.style.background = '#fecaca';
                                                                                        e.currentTarget.style.borderColor = '#fca5a5';
                                                                                    }}
                                                                                    onMouseLeave={(e) => {
                                                                                        e.currentTarget.style.background = '#fee2e2';
                                                                                        e.currentTarget.style.borderColor = '#fecaca';
                                                                                    }}
                                                                                    title={record.is_negative_fix ? "Revert this negative stock fix" : "Revert this stock-in"}
                                                                                >
                                                                                    Revert
                                                                                </button>
                                                                                {isStockInTsrLocked(record) && unlockedRecords[record.stock_in_id] && (
                                                                                    <span style={{ color: '#16a34a', display: 'inline-flex', alignItems: 'center', gap: '4px', fontSize: '12px' }} title="Unlocked with Admin PIN">
                                                                                        <FontAwesomeIcon icon={faUnlock} /> Unlocked
                                                                                    </span>
                                                                                )}
                                                                            </div>
                                                                        )}
                                                                    </div>
                                                                    <div className="expense-date" style={{ fontSize: '14px', color: '#6b7280' }}>
                                                                        <strong>Date:</strong> {new Date(record.received_date).toLocaleDateString('en-IN', { 
                                                                            day: 'numeric', 
                                                                            month: 'short', 
                                                                            year: 'numeric' 
                                                                        })}
                                                                    </div>
                                                                </div>
                                                                
                                                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: '13px', color: '#6b7280', marginBottom: '4px' }}>
                                                                </div>
                                                                
                                                                {record.received_by && (
                                                                    <div style={{ fontSize: '13px', color: '#6b7280' }}>
                                                                        <strong>Received by:</strong> {record.received_by}
                                                                    </div>
                                                                )}
                                                                
                                                                {/* Ingredients/Line Items List */}
                                                                {lineItems.length > 0 && (
                                                                    <div style={{ 
                                                                        marginTop: '12px', 
                                                                        paddingTop: '12px', 
                                                                        borderTop: '1px solid #e5e7eb' 
                                                                    }}>
                                                                        <div className="expense-items-header" style={{ 
                                                                            fontSize: '16px', 
                                                                            fontWeight: 600, 
                                                                            color: '#374151',
                                                                            marginBottom: '8px',
                                                                            display: 'flex',
                                                                            alignItems: 'center',
                                                                            justifyContent: 'space-between',
                                                                            gap: '6px',
                                                                            flexWrap: 'wrap'
                                                                        }}>
                                                                            <span className="expense-items-count">Items Purchased ({itemsCount})</span>
                                                                            {record.vendor_name && (
                                                                                <span className="expense-vendor" style={{ fontSize: '16px', color: '#6b7280', fontWeight: 400 }}>
                                                                                    <strong>Vendor:</strong> {record.vendor_name}
                                                                                </span>
                                                                            )}
                                                                        </div>
                                                                        <div style={{ 
                                                                            display: 'flex', 
                                                                            flexDirection: 'column', 
                                                                            gap: '6px',
                                                                            maxHeight: '360px',
                                                                            overflowY: 'auto',
                                                                            scrollbarWidth: 'none',
                                                                            msOverflowStyle: 'none'
                                                                        }} className="expense-items-scroll">
                                                                            {lineItems.map((item, itemIdx) => (
                                                                                <div key={itemIdx} style={{ 
                                                                                    display: 'flex', 
                                                                                    justifyContent: 'space-between',
                                                                                    alignItems: 'center',
                                                                                    padding: '6px 10px',
                                                                                    background: '#fff',
                                                                                    borderRadius: '6px',
                                                                                    fontSize: '14px',
                                                                                    gap: '8px',
                                                                                    border: '1px solid #e5e7eb'
                                                                                }}>
                                                                                    <div style={{ flex: 1, display: 'flex', alignItems: 'center', gap: '6px' }}>
                                                                                        <span style={{ 
                                                                                            color: '#000000',
                                                                                            fontSize: '14px',
                                                                                            background: 'transparent',
                                                                                            padding: '0',
                                                                                            textAlign: 'left',
                                                                                            fontWeight: 600
                                                                                        }}>
                                                                                            {itemIdx + 1}.
                                                                                        </span>
                                                                                        <span style={{
                                                                                            color: '#000000',
                                                                                            fontWeight: 500,
                                                                                            fontSize: '14px'
                                                                                        }}>
                                                                                            {item.mapped_item_name || item.item_name}
                                                                                        </span>
                                                                                        {/* TCSF Price Outlier Flag */}
                                                                                        {item.tcsf_anomaly && (
                                                                                            <span style={{
                                                                                                backgroundColor: '#fef3c7',
                                                                                                color: '#92400e',
                                                                                                fontSize: '9px',
                                                                                                fontWeight: 600,
                                                                                                padding: '2px 6px',
                                                                                                borderRadius: '4px',
                                                                                                border: '1px solid #fbbf24'
                                                                                            }}>
                                                                                                ⚠
                                                                                            </span>
                                                                                        )}
                                                                                    </div>
                                                                                    <div style={{ 
                                                                                        display: 'flex', 
                                                                                        flexDirection: 'column',
                                                                                        gap: '2px', 
                                                                                        alignItems: 'flex-end',
                                                                                        flexShrink: 0
                                                                                    }}>
                                                                                        <div style={{ 
                                                                                            color: '#6b7280',
                                                                                            fontSize: '11px',
                                                                                            display: 'flex',
                                                                                            alignItems: 'center',
                                                                                            gap: '4px',
                                                                                            // TCSF: Yellow highlight for price outliers
                                                                                            backgroundColor: item.tcsf_anomaly ? '#fef3c7' : 'transparent',
                                                                                            padding: item.tcsf_anomaly ? '4px 6px' : '0',
                                                                                            borderRadius: item.tcsf_anomaly ? '4px' : '0'
                                                                                        }}>
                                                                                            <span>{formatQuantity(item.quantity, item.unit_symbol || item.unit_name)}</span>
                                                                                            <span style={{ color: '#9ca3af' }}>@</span>
                                                                                            <span style={{ fontWeight: 500, color: item.tcsf_anomaly ? '#92400e' : '#374151' }}>₹{Number(item.unit_cost || 0).toFixed(2)}/{formatUnitLabel(item.unit_symbol || item.unit_name || 'unit')}</span>
                                                                                            {/* Show deviation percentage */}
                                                                                            {item.tcsf_deviation && (
                                                                                                <span style={{
                                                                                                    fontSize: '9px',
                                                                                                    color: '#92400e',
                                                                                                    fontWeight: 600,
                                                                                                    marginLeft: '4px'
                                                                                                }}>
                                                                                                    ({Number(item.tcsf_deviation).toFixed(1)}% {item.tcsf_direction || ''})
                                                                                                </span>
                                                                                            )}
                                                                                        </div>
                                                                                        <span style={{ 
                                                                                            color: '#22c55e',
                                                                                            fontWeight: 700,
                                                                                            fontSize: '14px',
                                                                                            minWidth: '60px',
                                                                                            textAlign: 'right'
                                                                                        }}>
                                                                                            ₹{getLineItemDisplayTotal(item).toFixed(2)}
                                                                                        </span>
                                                                                    </div>
                                                                                </div>
                                                                            ))}
                                                                        </div>
                                                                    </div>
                                                                )}
                                                                
                                                                {record.original_filename && lineItems.length === 0 && (
                                                                    <div style={{ fontSize: '12px', color: '#9ca3af', marginTop: '4px',  }}>
                                                                        {record.original_filename}
                                                                    </div>
                                                                )}
                                                            </div>
                                                            
                                                            {/* Right: Bill Image Preview */}
                                                            {record.image_url && (
                                                                <div style={{ 
                                                                    width: '80px', 
                                                                    height: '80px', 
                                                                    borderRadius: '8px', 
                                                                    overflow: 'hidden',
                                                                    border: '2px solid #fbbf24',
                                                                    flexShrink: 0
                                                                }}>
                                                                    <img 
                                                                        src={record.image_url} 
                                                                        alt="Bill" 
                                                                        style={{ 
                                                                            width: '100%', 
                                                                            height: '100%', 
                                                                            objectFit: 'cover' 
                                                                        }}
                                                                        onError={(e) => {
                                                                            e.target.style.display = 'none';
                                                                        }}
                                                                    />
                                                                </div>
                                                            )}
                                                        </div>
                                                        
                                                        {record.image_url && (
                                                            <div style={{ 
                                                                marginTop: '12px', 
                                                                paddingTop: '12px', 
                                                                borderTop: '1px solid #e5e7eb',
                                                                fontSize: '12px',
                                                                color: '#6b7280',
                                                                textAlign: 'center'
                                                            }}>
                                                                Click to view full bill image →
                                                            </div>
                                                        )}
                                                    </div>
                                                );
                                                })}
                                                {(expenseTab === 'stockout' || expenseTab === 'nc') && (
                                                    buildStockOutGroups(expenseTab === 'nc' ? ncExpenseRecords : stockOutExpenseRecords).map((group) => (
                                                        <div key={group.dateKey} className="expense-record-card" style={{
                                                            border: '1px solid #e5e7eb',
                                                            borderRadius: '12px',
                                                            padding: '16px',
                                                            background: '#fff'
                                                        }}>
                                                            <div className="expense-record-header" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '8px', marginBottom: '8px' }}>
                                                                <div className="expense-badges" style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                                                                    <span className="expense-price" style={{
                                                                        fontSize: '1.125rem',
                                                                        fontWeight: 600,
                                                                        color: '#000000'
                                                                    }}>
                                                                        {group.rows.some((row) => row.cogsUnavailable)
                                                                            ? '-'
                                                                            : `₹${group.rows.reduce((sum, row) => sum + Number(row.amount || 0), 0).toLocaleString('en-IN')}`}
                                                                    </span>
                                                                    <span className="expense-badge" style={{
                                                                        background: '#fee2e2',
                                                                        color: '#dc2626',
                                                                        padding: '2px 8px',
                                                                        borderRadius: '6px',
                                                                        fontSize: '12px',
                                                                        fontWeight: 500
                                                                    }}>
                                                                        Stock-Out
                                                                    </span>
                                                                </div>
                                                                <div className="expense-date" style={{ fontSize: '14px', color: '#6b7280' }}>
                                                                    <strong>Date:</strong> {group.dateLabel}
                                                                </div>
                                                            </div>

                                                            <div style={{
                                                                marginTop: '12px',
                                                                paddingTop: '12px',
                                                                borderTop: '1px solid #e5e7eb'
                                                            }}>
                                                                <div className="expense-items-header" style={{
                                                                    fontSize: '16px',
                                                                    fontWeight: 600,
                                                                    color: '#374151',
                                                                    marginBottom: '8px',
                                                                    display: 'flex',
                                                                    alignItems: 'center',
                                                                    justifyContent: 'space-between',
                                                                    gap: '6px',
                                                                    flexWrap: 'wrap'
                                                                }}>
                                                                    <span className="expense-items-count">Items ({group.rows.length})</span>
                                                                </div>
                                                                <div style={{
                                                                    display: 'flex',
                                                                    flexDirection: 'column',
                                                                    gap: '6px',
                                                                    maxHeight: '360px',
                                                                    overflowY: 'auto',
                                                                    scrollbarWidth: 'none',
                                                                    msOverflowStyle: 'none'
                                                                }} className="expense-items-scroll">
                                                                    {group.rows.map((row, rowIndex) => (
                                                                        <div key={`${row.itemName}-${row.reason}-${row.stock_out_id}-${rowIndex}`} style={{
                                                                            display: 'flex',
                                                                            flexDirection: 'column',
                                                                            padding: '12px',
                                                                            background: '#ffffff',
                                                                            borderRadius: '8px',
                                                                            border: '1px solid #e5e7eb',
                                                                            fontSize: '14px',
                                                                            gap: '8px',
                                                                            marginBottom: '8px'
                                                                        }}>
                                                                            {/* First Row - Item Details */}
                                                                            <div style={{
                                                                                display: 'grid',
                                                                                gridTemplateColumns: '16px minmax(0, 1fr) auto',
                                                                                alignItems: 'start',
                                                                                gap: '6px'
                                                                            }}>
                                                                                <div style={{
                                                                                    color: '#000000',
                                                                                    fontSize: '14px',
                                                                                    background: 'transparent',
                                                                                    padding: '0',
                                                                                    minWidth: '16px',
                                                                                    textAlign: 'left',
                                                                                    marginTop: '2px',
                                                                                    fontWeight: 600
                                                                                }}>
                                                                                    {rowIndex + 1}.
                                                                                </div>
                                                                                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: '4px', minWidth: 0 }}>
                                                                                    <span style={{
                                                                                        color: '#000000',
                                                                                        fontWeight: 600,
                                                                                        fontSize: '14px',
                                                                                        whiteSpace: 'nowrap',
                                                                                        overflow: 'hidden',
                                                                                        textOverflow: 'ellipsis'
                                                                                    }}>
                                                                                        {toCamelCaseLabel(row.itemName)}
                                                                                    </span>
                                                                                    {row.reason && (
                                                                                        <span style={{
                                                                                            background: '#e0e7ff',
                                                                                            color: '#4338ca',
                                                                                            padding: '2px 6px',
                                                                                            borderRadius: '6px',
                                                                                            fontSize: '11px',
                                                                                            fontWeight: 500
                                                                                        }}>
                                                                                            {row.reason}
                                                                                        </span>
                                                                                    )}
                                                                                    {row.banquetEventId && (
                                                                                        <span style={{
                                                                                            background: '#fef3c7',
                                                                                            color: '#92400e',
                                                                                            padding: '2px 6px',
                                                                                            borderRadius: '6px',
                                                                                            fontSize: '11px',
                                                                                            fontWeight: 500,
                                                                                            maxWidth: '100%',
                                                                                            whiteSpace: 'nowrap',
                                                                                            overflow: 'hidden',
                                                                                            textOverflow: 'ellipsis'
                                                                                        }}>
                                                                                            {banquetBadgeLabel(row)}
                                                                                        </span>
                                                                                    )}
                                                                                </div>
                                                                                <div style={{
                                                                                    display: 'flex',
                                                                                    flexDirection: 'column',
                                                                                    gap: '2px',
                                                                                    alignItems: 'flex-end',
                                                                                    justifySelf: 'end'
                                                                                }}>
                                                                                    <div style={{
                                                                                        color: '#6b7280',
                                                                                        fontSize: '11px'
                                                                                    }}>
                                                                                        {formatPlates(row.qty)}
                                                                                    </div>
                                                                                    <span style={{
                                                                                        color: '#dc2626',
                                                                                        fontWeight: 700,
                                                                                        fontSize: '14px',
                                                                                        minWidth: '60px',
                                                                                        textAlign: 'right'
                                                                                    }}>
                                                                                        {row.cogsUnavailable ? '-' : `₹${Number(row.amount || 0).toLocaleString('en-IN')}`}
                                                                                    </span>
                                                                                    <WastageRevertButton
                                                                                        row={row}
                                                                                        reverting={wastageRevertingId === row.stock_out_id}
                                                                                        onRevert={handleWastageRevert}
                                                                                    />
                                                                                </div>
                                                                            </div>

                                                                            {/* Second Row - Staff and Reason */}
                                                                            <div style={{
                                                                                display: 'grid',
                                                                                gridTemplateColumns: '76px minmax(0, 1fr)',
                                                                                rowGap: '6px',
                                                                                columnGap: '8px',
                                                                                alignItems: 'start',
                                                                                paddingTop: '8px',
                                                                                borderTop: '1px solid #f3f4f6',
                                                                                fontSize: '12px'
                                                                            }}>
                                                                                {/* Staff Name */}
                                                                                <span style={{
                                                                                    color: '#6b7280',
                                                                                    fontWeight: 500,
                                                                                    textAlign: 'left',
                                                                                    display: 'block'
                                                                                }}>
                                                                                    Recorded by:
                                                                                </span>
                                                                                <div style={{ display: 'flex', justifyContent: 'flex-start', alignItems: 'center' }}>
                                                                                    <span style={{
                                                                                        background: '#f0fdf4',
                                                                                        color: '#15803d',
                                                                                        padding: '2px 8px',
                                                                                        borderRadius: '6px',
                                                                                        fontSize: '11px',
                                                                                        fontWeight: 600,
                                                                                        display: 'inline-block',
                                                                                        textAlign: 'left'
                                                                                    }}>
                                                                                        {row.staffName}
                                                                                    </span>
                                                                                </div>

                                                                                {/* Reason */}
                                                                                {row.notes && (
                                                                                    <>
                                                                                        <span style={{
                                                                                            color: '#6b7280',
                                                                                            fontWeight: 500,
                                                                                            textAlign: 'left',
                                                                                            display: 'block',
                                                                                            marginTop: '1px'
                                                                                        }}>
                                                                                            Reason:
                                                                                        </span>
                                                                                        <span style={{
                                                                                            background: 'transparent',
                                                                                            color: '#000000',
                                                                                            padding: '1px 0',
                                                                                            borderRadius: '6px',
                                                                                            fontSize: '11px',
                                                                                            textAlign: 'left',
                                                                                            wordBreak: 'break-word',
                                                                                            lineHeight: 1.3
                                                                                        }}>
                                                                                            {row.notes}
                                                                                        </span>
                                                                                    </>
                                                                                )}
                                                                            </div>
                                                                        </div>
                                                                    ))}
                                                                </div>
                                                            </div>
                                                        </div>
                                                    ))
                                                )}
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* ===== SALES DETAIL MODAL ===== */}
                        {showSalesDetailModal && (
                            <div className="expense-modal-overlay" onClick={() => setShowSalesDetailModal(false)}>
                                <div className="expense-modal-container" onClick={e => e.stopPropagation()}>
                                    {/* Modal Header */}
                                    <div className="sd-modal-header">
                                        <div className="sd-modal-header-flex">
                                            <div className="sd-modal-header-left">
                                                <h3 className="sd-modal-title">Sales Detail Breakdown</h3>
                                                <p className="sd-modal-subtitle">
                                                    Complete order details with discount breakdown
                                                </p>
                                            </div>
                                            <button onClick={() => setShowSalesDetailModal(false)} className="sd-modal-close-btn">×</button>
                                        </div>
                                    </div>
                                    
                                    {/* Modal Body - Scrollable */}
                                    <div className="sd-modal-body expense-modal-scroll" style={{ display: 'flex', flexDirection: 'column' }}>
                                        {/* Top Search & Filter bar matching style */}
                                        <div className="sd-search-filter-row">
                                            <div className="sd-search-wrapper">
                                                <StandardSearch
                                                    value={salesDetailSearchQuery}
                                                    onChange={setSalesDetailSearchQuery}
                                                    onClear={() => setSalesDetailSearchQuery('')}
                                                    placeholder={salesDetailSearchFilter === 'item' ? 'Search items...' : 'Search dates...'}
                                                    showClearButton={true}
                                                    style={{
                                                        marginBottom: '0',
                                                        borderRadius: '10px',
                                                        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)',
                                                        height: '44px',
                                                        padding: '0 0 0 16px'
                                                    }}
                                                />
                                            </div>
                                            <div className="sd-dropdown-filter-container" ref={salesDetailFilterDropdownRef}>
                                                <button
                                                    type="button"
                                                    className="sd-dropdown-filter-btn"
                                                    onClick={() => setSalesDetailFilterDropdownOpen((prev) => !prev)}
                                                    title="Filter search by"
                                                >
                                                    <span className="sd-dropdown-filter-value">
                                                        {salesDetailSearchFilter === 'item'
                                                            ? 'Item'
                                                            : salesDetailSearchFilter === 'unmapped'
                                                                ? 'Unmapped Items'
                                                                : 'Date'}
                                                    </span>
                                                    <FontAwesomeIcon icon={faAngleDown} className="sd-dropdown-arrow" />
                                                </button>
                                                {salesDetailFilterDropdownOpen && (
                                                    <div className="sd-dropdown-menu">
                                                        <button
                                                            type="button"
                                                            className={`sd-dropdown-option ${salesDetailSearchFilter === 'item' ? 'active' : ''}`}
                                                            onClick={() => {
                                                                setSalesDetailSearchFilter('item');
                                                                setSalesDetailFilterDropdownOpen(false);
                                                            }}
                                                        >
                                                            Item
                                                        </button>
                                                        {!isSingleDaySelected && (
                                                            <button
                                                                type="button"
                                                                className={`sd-dropdown-option ${salesDetailSearchFilter === 'date' ? 'active' : ''}`}
                                                                onClick={() => {
                                                                    setSalesDetailSearchFilter('date');
                                                                    setSalesDetailFilterDropdownOpen(false);
                                                                }}
                                                            >
                                                                Date
                                                            </button>
                                                        )}
                                                        {/* A view mode rather than a search field: selecting it
                                                            shows only the bill lines that never resolved to a
                                                            menu item. Display-only — these rows are already
                                                            excluded from every total. */}
                                                        <button
                                                            type="button"
                                                            className={`sd-dropdown-option ${salesDetailSearchFilter === 'unmapped' ? 'active' : ''}`}
                                                            onClick={() => {
                                                                setSalesDetailSearchFilter('unmapped');
                                                                setSalesDetailFilterDropdownOpen(false);
                                                            }}
                                                        >
                                                            Unmapped Items
                                                        </button>
                                                    </div>
                                                )}
                                            </div>
                                        </div>

                                        {salesDetailLoading ? (
                                            <div className="sd-loading-container" style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
                                                <div className="sd-loading-spinner"></div>
                                                <p className="sd-loading-text">Loading sales details...</p>
                                            </div>
                                        ) : salesDetailError ? (
                                            <div className="sd-error-container" style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
                                                <p className="sd-error-text">❌ {salesDetailError}</p>
                                                <button onClick={fetchSalesDetail} className="sd-retry-btn">
                                                    Retry
                                                </button>
                                            </div>
                                        ) : salesDetailData.length === 0 ? (
                                            <div className="sd-empty-container" style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
                                                <p className="sd-empty-text">No sales data found for this period</p>
                                            </div>
                                        ) : filteredSalesDetailOrders.length === 0 ? (
                                            <div className="sd-empty-container" style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
                                                <p className="sd-empty-text">No matching sales records found</p>
                                            </div>
                                        ) : (
                                            <div className="sd-content-wrapper">
                                                {filteredSalesDetailOrders.map((order, orderIndex) => {
                                                    const getBillPrice = (item) => {
                                                        return normalizeBillDisplay(getRawBillPrice(item));
                                                    };

                                                    const {
                                                        subtotal: itemsSubtotal,
                                                        discount: discountAmount,
                                                        totalWithUnmapped: orderCalculatedTotal,
                                                        countedItems,
                                                        unmappedItems: orderUnmappedItems,
                                                        unmappedAmount: orderUnmappedAmount
                                                    } = getOrderTotals(order);
                                                    const orderDateLabel = getSalesOrderDateLabel(order);
                                                    
                                                    return (
                                                        <div key={`${getSalesOrderDateKey(order)}-${order.sale_id || orderIndex}-${orderIndex}`} className="sd-order-card">
                                                            {/* Order Header */}
                                                            <div className="sd-order-header">
                                                                <div>
                                                                    <span className="sd-order-id">
                                                                        Date: {orderDateLabel}
                                                                    </span>
                                                                    <span className="sd-order-id" style={{ display: 'none' }} aria-hidden="true">
                                                                        Order #{order.sale_id}
                                                                    </span>
                                                                    {order.payment_method && (
                                                                        <span className="sd-payment-badge">
                                                                            {order.payment_method}
                                                                        </span>
                                                                    )}
                                                                    {order.outlet && (
                                                                        <span className="sd-payment-badge">
                                                                            {order.outlet}
                                                                        </span>
                                                                    )}
                                                                </div>
                                                                <span className="sd-order-time">{order.time}</span>
                                                            </div>

                                                            {/* Items Table */}
                                                            <table className="sd-items-table">
                                                                <thead>
                                                                    <tr className="sd-table-header">
                                                                        <th className="sd-th-sno">S.NO</th>
                                                                        <th className="sd-th-item">ITEM</th>
                                                                        <th className="sd-th-qty">QTY</th>
                                                                        <th className="sd-th-amount">BILL PRICE</th>
                                                                        <th className="sd-th-discount">TOTAL DISCOUNT</th>
                                                                    </tr>
                                                                </thead>
                                                                <tbody>
                                                                    {order.items.map((item, itemIndex) => (
                                                                        <tr key={itemIndex} className="sd-table-row">
                                                                            <td className="sd-td-sno">
                                                                                {itemIndex + 1}
                                                                            </td>
                                                                            <td className="sd-td-item">
                                                                                {item.item_name}
                                                                                {item.is_unmapped && (
                                                                                    <span
                                                                                        className="sd-unmapped-badge"
                                                                                        role="button"
                                                                                        style={{ cursor: 'pointer' }}
                                                                                        title="This bill line did not match any menu item. Click to map it — its stock is deducted when you confirm."
                                                                                        onClick={() => setNimOpen(true)}
                                                                                    >
                                                                                        Not in Menu
                                                                                    </span>
                                                                                )}
                                                                            </td>
                                                                            <td className="sd-td-qty">
                                                                                {Number(item.quantity_sold).toFixed(0)}
                                                                            </td>
                                                                            <td className="sd-td-amount">
                                                                                ₹{getBillPrice(item).toFixed(2)}
                                                                            </td>
                                                                            <td className="sd-td-discount">
                                                                                ₹{Number(item.discount_amount || 0).toFixed(2)}
                                                                            </td>
                                                                        </tr>
                                                                    ))}
                                                                </tbody>
                                                            </table>

                                                            {/* Order Summary */}
                                                            <div className="sd-order-summary">
                                                                <div className="sd-summary-row">
                                                                    <span className="sd-summary-label">Subtotal ({countedItems} items)</span>
                                                                    <span className="sd-summary-value">
                                                                        ₹{itemsSubtotal.toFixed(2)}
                                                                    </span>
                                                                </div>
                                                                {discountAmount > 0 && (
                                                                    <div className="sd-summary-row">
                                                                        <span className="sd-summary-label">Discount</span>
                                                                        <span className="sd-summary-discount">
                                                                            -₹{discountAmount.toFixed(2)}
                                                                        </span>
                                                                    </div>
                                                                )}
                                                                {orderUnmappedItems.length > 0 && (
                                                                    <div className="sd-summary-row">
                                                                        {/* No longer "— excluded": these amounts are now part of the
                                                                            Total below. The label still names them so the reader can
                                                                            see how much of the total has no costing behind it. */}
                                                                        <span className="sd-summary-label sd-summary-label-unmapped"
                                                                              style={{ cursor: 'pointer' }}
                                                                              onClick={() => setNimOpen(true)}
                                                                              title="Bill lines that never matched a menu item. Included in the Total below. Click to map them.">
                                                                            Not in Menu ({orderUnmappedItems.length})
                                                                        </span>
                                                                        <span className="sd-summary-unmapped">
                                                                            ₹{orderUnmappedAmount.toFixed(2)}
                                                                        </span>
                                                                    </div>
                                                                )}
                                                                <div className="sd-summary-total-row">
                                                                    <span className="sd-summary-total-label">Total</span>
                                                                    <span className="sd-summary-total-value">
                                                                        ₹{orderCalculatedTotal.toFixed(2)}
                                                                    </span>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    );
                                                })}
                                                
                                                {/* Grand Total Footer */}
                                                <div className="sd-grand-total">
                                                    <span className="sd-grand-total-label">
                                                        Total ({salesDetailDataDateWise.length} orders, {salesDetailDataDateWise.reduce((sum, order) => sum + order.items.length, 0)} items)
                                                    </span>
                                                    <span className="sd-grand-total-value">
                                                        ₹{salesDetailDataDateWise.reduce((sum, order) => sum + getOrderTotals(order).totalWithUnmapped, 0).toFixed(2)}
                                                    </span>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* ===== WEEK NAVIGATOR (only shown when week is selected) ===== */}
                        <WeekNavigator
                            selectedWeek={selectedWeek}
                            navigateWeek={navigateWeek}
                            goToThisWeek={goToThisWeek}
                            getWeekStart={getWeekStart}
                            formatWeekDisplay={formatWeekDisplay}
                        />

                        {/* ===== SALES TREND (Static Bar Chart for all periods) ===== */}
                        {/* Hide sales comparison when a custom range (selectedWeek.isCustomRange) is active */}
                        {((!selectedPeriod.includes('Today') && !selectedDate && !selectedWeek) || (selectedDate && selectedDate.isMonthSelection) || selectedWeek) && !(selectedWeek && selectedWeek.isCustomRange) ? (
                            <div
                                id="sales-trend"
                                className="trends-wrapper report-enter-card"
                                style={{
                                    ...getEnterDelayStyle(80),
                                    border: (activeSection === 'sales-trend' ? '3px solid #2196F3' : '1px solid #e0e0e0'),
                                    transition: 'all 0.3s ease',
                                    scrollMarginTop: '100px',
                                    scrollMarginBottom: '20px',
                                    marginTop: '24px',
                                    marginBottom: '8px',
                                    padding: '20px',
                                    paddingTop: '10px',
                                    paddingBottom: '0px',
                                    backgroundColor: '#fff',
                                    borderRadius: '12px',
                                    boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                                    minHeight: '350px',
                                    outline: 'none',
                                    WebkitTapHighlightColor: 'transparent'
                                }}
                                tabIndex="-1"
                                onFocus={(e) => e.target.style.outline = 'none'}
                            >
                                <h2 className="section-title" style={{ marginTop: '0px', marginBottom: '20px', fontSize: '18px', fontWeight: 600, color: 'black' }}>
                                    {selectedWeek && selectedWeek.isWeekSelection ? 'Day-of-Week Sales Comparison' :
                                        selectedDate && selectedDate.isMonthSelection ? 'Week-of-Month Sales Comparison' :
                                        selectedPeriod === 'This Week' ? 'Day-of-Week Sales Comparison' :
                                        selectedPeriod === 'This Month' ? 'Week-of-Month Sales Comparison' :
                                            selectedPeriod === 'This Year' ? 'Month-of-Year Sales Comparison' :
                                                'Sales Comparison'}
                                </h2>
                                {chartData && chartData.length > 0 && chartData.some(d => Number(d.total_sales || 0) > 0) ? (
                                    <ResponsiveContainer width="100%" height={(windowWidth < 768 ? 300 : 380)}>
                                        <BarChart
                                            data={chartData}
                                            margin={{
                                                top: (windowWidth < 768 ? 15 : 20),
                                                right: (windowWidth < 768 ? 5 : 30),
                                                left: (windowWidth < 768 ? -10 : 0),
                                                bottom: 10
                                            }}
                                        >
                                            <CartesianGrid strokeDasharray="3 3" />
                                            <XAxis
                                                dataKey="label"
                                                angle={(windowWidth < 480 ? -45 : 0)}
                                                textAnchor={(windowWidth < 480 ? "end" : "middle")}
                                                height={(windowWidth < 768 ? 40 : 60)}
                                                fontSize={(windowWidth < 480 ? 10 : 12)}
                                            />
                                            <YAxis 
                                                domain={[0, 'auto']}
                                                tickFormatter={(value) => `₹${Number(value || 0).toLocaleString('en-IN')}`}
                                            />
                                            <Tooltip
                                                formatter={(value, name) => [`₹${value.toLocaleString('en-IN')}`, 'Total Net Sale (₹)']}
                                                labelFormatter={(label) => `${label}`}
                                            />
                                            <Bar
                                                dataKey="total_sales"
                                                name="Total Net Sale (₹)"
                                                radius={[4, 4, 0, 0]}
                                            >
                                                {chartData.map((entry, index) => {
                                                    const values = chartData.map(d => Number(d.total_sales || 0));
                                                    const minValue = Math.min(...values.filter(v => v > 0));
                                                    const maxValue = Math.max(...values);
                                                    const currentValue = Number(entry.total_sales || 0);

                                                    let fillColor = '#2563eb'; // Default blue
                                                    if (currentValue === 0) fillColor = '#93c5fd'; // Light blue for zero
                                                    else if (currentValue === maxValue && maxValue > 0) fillColor = '#1e40af'; // Dark blue for highest
                                                    else if (currentValue === minValue && minValue > 0) fillColor = '#60a5fa'; // Medium blue for lowest

                                                    return <Cell key={`cell-${index}`} fill={fillColor} />;
                                                })}
                                            </Bar>
                                        </BarChart>
                                    </ResponsiveContainer>
                                ) : (
                                    <div style={{ 
                                        display: 'flex', 
                                        flexDirection: 'column',
                                        alignItems: 'center', 
                                        justifyContent: 'center', 
                                        height: windowWidth < 768 ? '300px' : '380px',
                                        color: '#999',
                                        gap: '10px'
                                    }}>
                                        <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                                            <path d="M12 2v20M2 12h20M6 6l12 12M6 18L18 6"/>
                                        </svg>
                                        <p style={{ fontSize: '16px', fontWeight: 500 }}>{t('reports.noSalesData')}</p>
                                        <p style={{ fontSize: '14px', color: '#bbb' }}>
                                            {selectedPeriod === 'This Week' ? 'No sales recorded this week' : 
                                             selectedPeriod === 'This Month' ? 'No sales recorded this month' :
                                             selectedPeriod === 'This Year' ? 'No sales recorded this year' :
                                             'No sales data for selected period'}
                                        </p>
                                    </div>
                                )}
                            </div>
                        ) : null}

                        <OutletWiseSales rows={outletBreakdown.rows} />

                        {/* ===== DATE-WISE BREAKDOWN TABLE (Only for custom date range selection) ===== */}
                        {dailyBreakdown && dailyBreakdown.length > 0 && (selectedWeek && selectedWeek.isCustomRange && !selectedWeek.isSingleDay) && (
                            <div
                                id="date-wise-breakdown"
                                className="trends-wrapper date-breakdown-wrapper report-enter-card"
                                style={{
                                    ...getEnterDelayStyle(140),
                                    marginTop: '24px',
                                    marginBottom: '24px',
                                    padding: '16px 14px',
                                    backgroundColor: '#fff',
                                    borderRadius: '12px',
                                    boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                                    border: '1px solid #e5e7eb'
                                }}
                            >
                                <h2 className="section-title" style={{ marginBottom: '12px', fontSize: '16px', fontWeight: 600, color: '#000' }}>
                                    Date-wise Breakdown
                                </h2>
                                <div style={{ overflowX: 'hidden' }}>
                                    <table style={{
                                        width: '100%',
                                        borderCollapse: 'collapse',
                                        fontSize: '14px',
                                        tableLayout: 'fixed'
                                    }}>
                                        <thead>
                                            <tr style={{ borderBottom: '1px solid #e0e0e0' }}>
                                                <th style={{ padding: '1rem 1rem 1rem 0.7rem', textAlign: 'left', fontWeight: 600, color: '#000', width: '22%' }}>DATE</th>
                                                <th style={{ padding: '8px 6px', textAlign: 'right', fontWeight: 600, color: '#000', width: '26%', whiteSpace: 'nowrap' }}>TOTAL SALE</th>
                                                <th style={{ padding: '8px 6px', textAlign: 'right', fontWeight: 600, color: '#000', width: '26%' }}>EXPENSE</th>
                                                <th style={{ padding: '8px 6px', textAlign: 'right', fontWeight: 600, color: '#000', width: '26%' }}>WASTAGE</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {dailyBreakdown.map((row, index) => {
                                                const d = new Date(row.date);
                                                const dd = String(d.getDate()).padStart(2,'0');
                                                const mm = String(d.getMonth()+1).padStart(2,'0');
                                                return (
                                                    <tr key={index} className="report-enter-item" style={{
                                                        ...getStaggerDelayStyle(index, 120, 28, 320),
                                                        borderBottom: '1px solid #f1f1f1',
                                                        backgroundColor: index % 2 === 0 ? '#ffffff' : '#f9f9f9'
                                                    }}>
                                                        <td style={{ padding: '8px 6px', paddingLeft: 0, color: '#000', fontWeight: 500 }}>
                                                            {dd}/{mm}
                                                        </td>
                                                        <td style={{ padding: '8px 6px', textAlign: 'right', fontWeight: 600, color: '#000' }}>
                                                            ₹{(row.total_sales || 0).toLocaleString('en-IN')}
                                                        </td>
                                                        <td style={{ padding: '8px 6px', textAlign: 'right', fontWeight: 600, color: '#ff6b35' }}>
                                                            ₹{(row.expense || 0).toLocaleString('en-IN')}
                                                        </td>
                                                        <td style={{ padding: '8px 6px', textAlign: 'right', fontWeight: 600, color: '#dc3545' }}>
                                                            ₹{(row.wastage || 0).toLocaleString('en-IN')}
                                                        </td>
                                                    </tr>
                                                );
                                            })}
                                            {/* Totals Row */}
                                            <tr style={{ borderTop: '2px solid #e0e0e0', backgroundColor: '#f5f6f7', fontWeight: 700 }}>
                                                <td style={{ padding: '8px 6px', color: '#000' }}>TOTAL</td>
                                                <td style={{ padding: '8px 6px', textAlign: 'right', color: '#34c759' }}>
                                                    ₹{dailyBreakdown.reduce((sum, row) => sum + (row.total_sales || 0), 0).toLocaleString('en-IN')}
                                                </td>
                                                <td style={{ padding: '8px 6px', textAlign: 'right', color: '#ff6b35' }}>
                                                    ₹{dailyBreakdown.reduce((sum, row) => sum + (row.expense || 0), 0).toLocaleString('en-IN')}
                                                </td>
                                                <td style={{ padding: '8px 6px', textAlign: 'right', color: '#dc3545' }}>
                                                    ₹{dailyBreakdown.reduce((sum, row) => sum + (row.wastage || 0), 0).toLocaleString('en-IN')}
                                                </td>
                                            </tr>
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        )}

                        {/* ===== TOP 5 ITEMS BY GROSS PROFIT (Show for single-day filters only) ===== */}
                        {(() => {
                            const isTodayDefault = selectedPeriod === 'Today' && !selectedWeek && !selectedDate;
                            const isSingleDayCustom = Boolean(selectedWeek && selectedWeek.isCustomRange && selectedWeek.isSingleDay);
                            const isLegacySingleDate = Boolean(selectedDate && !selectedDate.isMonthSelection);
                            const shouldShow = isTodayDefault || isSingleDayCustom || isLegacySingleDate;
                            return shouldShow;
                        })() && (
                            <div
                                id="top-5-items"
                                className="trends-wrapper report-enter-card"
                                style={{
                                    ...getEnterDelayStyle(200),
                                    marginTop: '24px',
                                    marginBottom: '24px',
                                    padding: '20px',
                                    width: '100%',
                                    maxWidth: '564px',
                                    backgroundColor: '#fff',
                                    borderRadius: '12px',
                                    border: '1px solid #e0e0e0',
                                    boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                                    minHeight: '260px',
                                    marginLeft: 'auto',
                                    marginRight: 'auto'
                                }}
                            >
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: windowWidth < 768 ? '16px' : '24px', paddingBottom: '8px' }}>
                                    <h2 className="section-title" style={{ margin: 0, fontSize: windowWidth < 768 ? '16px' : '18px', fontWeight: 600, background: 'transparent', padding: 0 }}>
                                        {t('reports.topItemsTitle')}
                                    </h2>
                                    <button 
                                        onClick={() => setShowGraphModal(true)}
                                        style={{
                                            background: '#f3f4f6',
                                            border: '1px solid #d1d5db',
                                            borderRadius: '6px',
                                            color: '#374151',
                                            padding: '4px 10px',
                                            fontSize: '12px',
                                            fontWeight: '600',
                                            cursor: 'pointer',
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: '4px',
                                            transition: 'all 0.2s ease',
                                            boxShadow: '0 1px 2px rgba(0,0,0,0.05)'
                                        }}
                                        onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = '#e5e7eb'; }}
                                        onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = '#f3f4f6'; }}
                                    >
                                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                            <line x1="18" y1="20" x2="18" y2="10"></line>
                                            <line x1="12" y1="20" x2="12" y2="4"></line>
                                            <line x1="6" y1="20" x2="6" y2="14"></line>
                                        </svg>
                                        <span>View Graph</span>
                                    </button>
                                </div>
                                {(
                                    topProfitItems.slice(0, 5).length > 0 ? (
                                        <div style={{ position: 'relative' }}>
                                            {/* Custom Bar Chart with Real Data */}
                                            {(() => {
                                                const compact = windowWidth < 768;
                                                
                                                // Step 1: Convert negative gross profits to 0
                                                const itemsWithPositiveProfit = topProfitItems.slice(0, 5).map(item => {
                                                    const isCogsUnavailable = !!(item.cogs_unavailable || item.gross_profit === null || item.gross_profit === undefined);
                                                    return {
                                                        ...item,
                                                        _is_cogs_unavailable: isCogsUnavailable,
                                                        adjusted_gross_profit: isCogsUnavailable ? null : Math.max(0, Number(item.gross_profit || 0))
                                                    };
                                                });
                                                
                                                // Step 2: Calculate total of only positive values
                                                const totalPositiveGrossProfit = itemsWithPositiveProfit
                                                    .filter(i => i.adjusted_gross_profit !== null)
                                                    .reduce((sum, i) => sum + i.adjusted_gross_profit, 0);
                                                
                                                // Step 3: Calculate normalized percentages that sum to 100%
                                                const maxPercentage = Math.max(...itemsWithPositiveProfit.map(item => 
                                                    (item.adjusted_gross_profit !== null && totalPositiveGrossProfit > 0)
                                                        ? ((item.adjusted_gross_profit / totalPositiveGrossProfit) * 100)
                                                        : 0
                                                ));
                                                
                                                return itemsWithPositiveProfit.map((item, idx) => {
                                                    // Calculate percentage based on positive values only (ensures total = 100%)
                                                    const profitPercentage = (item.adjusted_gross_profit !== null && totalPositiveGrossProfit > 0)
                                                        ? ((item.adjusted_gross_profit / totalPositiveGrossProfit) * 100)
                                                        : 0;
                                                    const displayPercentage = item.adjusted_gross_profit === null ? '-' : `${Math.round(profitPercentage)}%`;

                                                    // Calculate bar width to fill full width (100% = full width)
                                                    const barWidthPercent = item.adjusted_gross_profit === null ? 15 : ((profitPercentage / 100) * 70); // Scale to 100% max

                                                return (
                                                    <div key={idx} className="report-enter-item" style={{
                                                        ...getStaggerDelayStyle(idx, 130, 60, 360),
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        marginBottom: '10px',
                                                        gap: '12px'
                                                    }}>
                                                        {/* Item Image */}
                                                        {item.image_url && !isPlaceholderImage(item.image_url) ? (
                                                            <div style={{
                                                                width: compact ? '32px' : '40px',
                                                                height: compact ? '32px' : '40px',
                                                                flexShrink: 0,
                                                                borderRadius: compact ? '6px' : '8px',
                                                                overflow: 'hidden',
                                                                backgroundColor: '#f0f0f0',
                                                                border: '1px solid #e2e8f0'
                                                            }}>
                                                                <img
                                                                    src={item.image_url}
                                                                    alt={item.item_name}
                                                                    style={{
                                                                        width: '100%',
                                                                        height: '100%',
                                                                        objectFit: 'cover'
                                                                    }}
                                                                    onError={(e) => {
                                                                        e.target.style.display = 'none';
                                                                        const placeholder = e.target.parentNode.querySelector('.item-image-placeholder-fallback');
                                                                        if (placeholder) placeholder.style.display = 'flex';
                                                                    }}
                                                                />
                                                                <div 
                                                                    className="item-image-placeholder-fallback" 
                                                                    style={{ 
                                                                        display: 'none',
                                                                        width: '100%',
                                                                        height: '100%',
                                                                        alignItems: 'center',
                                                                        justifyContent: 'center',
                                                                        backgroundColor: '#f8fafc',
                                                                        color: '#94a3b8'
                                                                    }}
                                                                >
                                                                    <Utensils size={compact ? 14 : 18} />
                                                                </div>
                                                            </div>
                                                        ) : (
                                                            <div style={{
                                                                width: compact ? '32px' : '40px',
                                                                height: compact ? '32px' : '40px',
                                                                flexShrink: 0,
                                                                borderRadius: compact ? '6px' : '8px',
                                                                backgroundColor: '#f8fafc',
                                                                border: '1px solid #e2e8f0',
                                                                display: 'flex',
                                                                alignItems: 'center',
                                                                justifyContent: 'center',
                                                                color: '#94a3b8'
                                                            }}>
                                                                <Utensils size={compact ? 14 : 18} />
                                                            </div>
                                                        )}

                                                        {/* Bar with item name */}
                                                        <div 
                                                            title={item.item_name}
                                                            style={{
                                                            height: compact ? '36px' : '44px',
                                                            width: `${Math.max(barWidthPercent, 15)}%`,
                                                            backgroundColor: (item._is_cogs_unavailable || item.is_estimated || item.no_recipe) ? '#93b8f5' : '#2563eb',
                                                            borderRadius: compact ? '4px' : '6px',
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            paddingLeft: compact ? '10px' : '16px',
                                                            paddingRight: compact ? '10px' : '16px',
                                                            flexShrink: 0
                                                        }}>
                                                            <span style={{
                                                                color: '#fff',
                                                                fontWeight: 600,
                                                                fontSize: compact ? '12px' : '15px',
                                                                whiteSpace: 'nowrap',
                                                                overflow: 'hidden',
                                                                textOverflow: 'ellipsis'
                                                            }}>
                                                                {item.item_name}
                                                            </span>
                                                        </div>

                                                        {/* Percentage - separate element */}
                                                        <div style={{
                                                            color: item.adjusted_gross_profit === null ? '#666' : '#2563eb',
                                                            fontWeight: 700,
                                                            fontSize: compact ? '13px' : '16px',
                                                            whiteSpace: 'nowrap',
                                                            flexShrink: 0,
                                                            minWidth: compact ? '40px' : '50px'
                                                        }}>
                                                            {displayPercentage}
                                                        </div>
                                                    </div>
                                                );
                                            });
                                            })()}
                                            {/* X-axis scale */}
                                            {(() => {
                                                const ticks = [0, 20, 40, 60, 80, 100];
                                                
                                                return (
                                                    <div style={{
                                                        display: 'flex',
                                                        marginTop: windowWidth < 768 ? '12px' : '16px',
                                                        marginBottom: windowWidth < 768 ? '16px' : '24px',
                                                        marginLeft: windowWidth < 768 ? '44px' : '52px',
                                                        position: 'relative',
                                                        width: '70%'
                                                    }}>
                                                        {ticks.map((tick, idx) => (
                                                            <div key={idx} style={{
                                                                position: 'absolute',
                                                                left: `${(tick / 100) * 100}%`,
                                                                transform: 'translateX(-50%)',
                                                                fontSize: windowWidth < 768 ? '11px' : '13px',
                                                                color: '#666',
                                                                fontWeight: 500
                                                            }}>
                                                                {tick}%
                                                            </div>
                                                        ))}
                                                    </div>
                                                );
                                            })()}
                                        </div>
                                    ) : (
                                        <NoDataPlaceholder
                                            type="profit"
                                            title="No profit data yet"
                                            subtitle="Your top earning items will rank here once sales are recorded."
                                            minHeight="180px"
                                        />
                                )
                            )}
                            </div>
                        )}

                        {/* ===== GRAPH MODAL - Full items by Gross Profit ===== */}
                        {showGraphModal && (
                            <div className="graph-modal-overlay" onClick={() => setShowGraphModal(false)}>
                                <div className="graph-modal-container" onClick={(e) => e.stopPropagation()}>
                                    {/* Modal Header */}
                                    <div className="graph-modal-header">
                                        <h2 className="graph-modal-title">
                                            {t('reports.topItemsTitle')}
                                        </h2>
                                        <button
                                            className="graph-modal-close-btn"
                                            onClick={() => setShowGraphModal(false)}
                                            aria-label="Close"
                                        >
                                            ✕
                                        </button>
                                    </div>
                                    {/* Modal Body */}
                                    <div className="graph-modal-body">
                                        {(() => {
                                            const allItems = topProfitItems;
                                            const compact = windowWidth < 768;

                                            const itemsWithPositiveProfit = allItems.map(item => {
                                                const isCogsUnavailable = !!(item.cogs_unavailable || item.gross_profit === null || item.gross_profit === undefined);
                                                return {
                                                    ...item,
                                                    _is_cogs_unavailable: isCogsUnavailable,
                                                    adjusted_gross_profit: isCogsUnavailable ? null : Math.max(0, Number(item.gross_profit || 0))
                                                };
                                            });

                                            const totalPositiveGrossProfit = itemsWithPositiveProfit
                                                .filter(i => i.adjusted_gross_profit !== null)
                                                .reduce((sum, i) => sum + i.adjusted_gross_profit, 0);

                                            return allItems.length > 0 ? (
                                                <div style={{ position: 'relative', padding: '0 16px 16px' }}>
                                                    {/* Scrollable bar chart container */}
                                                    <div style={{ paddingRight: '4px' }}>
                                                        {itemsWithPositiveProfit.map((item, idx) => {
                                                            const profitPercentage = (item.adjusted_gross_profit !== null && totalPositiveGrossProfit > 0)
                                                                ? ((item.adjusted_gross_profit / totalPositiveGrossProfit) * 100)
                                                                : 0;
                                                            const displayPercentage = item.adjusted_gross_profit === null ? '-' : `${Math.round(profitPercentage)}%`;
                                                            const barWidthPercent = item.adjusted_gross_profit === null ? 15 : ((profitPercentage / 100) * 70);

                                                            return (
                                                                <div key={idx} style={{
                                                                    display: 'flex',
                                                                    alignItems: 'center',
                                                                    marginBottom: compact ? '8px' : '10px',
                                                                    gap: '10px'
                                                                }}>
                                                                    {/* Item Image */}
                                                                    {item.image_url && !isPlaceholderImage(item.image_url) ? (
                                                                        <div style={{
                                                                            width: compact ? '28px' : '36px',
                                                                            height: compact ? '28px' : '36px',
                                                                            flexShrink: 0,
                                                                            borderRadius: compact ? '5px' : '7px',
                                                                            overflow: 'hidden',
                                                                            backgroundColor: '#f0f0f0',
                                                                            border: '1px solid #e2e8f0'
                                                                        }}>
                                                                            <img
                                                                                src={item.image_url}
                                                                                alt={item.item_name}
                                                                                style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                                                                                onError={(e) => {
                                                                                    e.target.style.display = 'none';
                                                                                    const placeholder = e.target.parentNode.querySelector('.item-image-placeholder-fallback');
                                                                                    if (placeholder) placeholder.style.display = 'flex';
                                                                                }}
                                                                            />
                                                                            <div 
                                                                                className="item-image-placeholder-fallback" 
                                                                                style={{ 
                                                                                    display: 'none',
                                                                                    width: '100%',
                                                                                    height: '100%',
                                                                                    alignItems: 'center',
                                                                                    justifyContent: 'center',
                                                                                    backgroundColor: '#f8fafc',
                                                                                    color: '#94a3b8'
                                                                                }}
                                                                            >
                                                                                <Utensils size={compact ? 13 : 16} />
                                                                            </div>
                                                                        </div>
                                                                    ) : (
                                                                        <div style={{
                                                                            width: compact ? '28px' : '36px',
                                                                            height: compact ? '28px' : '36px',
                                                                            flexShrink: 0,
                                                                            borderRadius: compact ? '5px' : '7px',
                                                                            backgroundColor: '#f8fafc',
                                                                            border: '1px solid #e2e8f0',
                                                                            display: 'flex',
                                                                            alignItems: 'center',
                                                                            justifyContent: 'center',
                                                                            color: '#94a3b8'
                                                                        }}>
                                                                            <Utensils size={compact ? 13 : 16} />
                                                                        </div>
                                                                    )}

                                                                    {/* Bar with item name */}
                                                                    <div style={{
                                                                        height: compact ? '32px' : '40px',
                                                                        width: `${Math.max(barWidthPercent, 15)}%`,
                                                                        backgroundColor: (item._is_cogs_unavailable || item.is_estimated || item.no_recipe) ? '#93b8f5' : '#2563eb',
                                                                        borderRadius: compact ? '4px' : '6px',
                                                                        display: 'flex',
                                                                        alignItems: 'center',
                                                                        paddingLeft: compact ? '8px' : '14px',
                                                                        paddingRight: compact ? '8px' : '10px',
                                                                        flexShrink: 0,
                                                                        transition: 'width 0.4s ease'
                                                                    }}>
                                                                        <span style={{
                                                                            color: '#fff',
                                                                            fontWeight: 600,
                                                                            fontSize: compact ? '11px' : '13px',
                                                                            whiteSpace: 'nowrap',
                                                                            overflow: 'hidden',
                                                                            textOverflow: 'ellipsis'
                                                                        }}>
                                                                            {item.item_name}
                                                                        </span>
                                                                    </div>

                                                                    {/* Percentage */}
                                                                    <div style={{
                                                                        color: item.adjusted_gross_profit === null ? '#666' : '#2563eb',
                                                                        fontWeight: 700,
                                                                        fontSize: compact ? '12px' : '14px',
                                                                        whiteSpace: 'nowrap',
                                                                        flexShrink: 0,
                                                                        minWidth: compact ? '36px' : '46px'
                                                                    }}>
                                                                        {displayPercentage}
                                                                    </div>
                                                                </div>
                                                            );
                                                        })}
                                                    </div>
                                                    {/* X-axis scale */}
                                                    {(() => {
                                                        const ticks = [0, 20, 40, 60, 80, 100];
                                                        return (
                                                            <div style={{
                                                                display: 'flex',
                                                                marginTop: compact ? '10px' : '14px',
                                                                marginBottom: compact ? '12px' : '18px',
                                                                marginLeft: compact ? '38px' : '46px',
                                                                position: 'relative',
                                                                width: '70%'
                                                            }}>
                                                                {ticks.map((tick, tidx) => (
                                                                    <div key={tidx} style={{
                                                                        position: 'absolute',
                                                                        left: `${(tick / 100) * 100}%`,
                                                                        transform: 'translateX(-50%)',
                                                                        fontSize: compact ? '10px' : '12px',
                                                                        color: '#666',
                                                                        fontWeight: 500
                                                                    }}>
                                                                        {tick}%
                                                                    </div>
                                                                ))}
                                                            </div>
                                                        );
                                                    })()}
                                                </div>
                                            ) : (
                                                <NoDataPlaceholder
                                                    type="profit"
                                                    title="No profit data yet"
                                                    subtitle="Your top earning items will rank here once sales are recorded."
                                                    minHeight="220px"
                                                />
                                            );
                                        })()}
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* ===== GROSS PROFIT vs COGS DISTRIBUTION PIE CHART (Today, Selected Date, Selected Week, and All Periods) ===== */}
                        {(selectedPeriod === 'Today' || selectedDate || selectedWeek || selectedPeriod === 'This Week' || selectedPeriod === 'This Month' || selectedPeriod === 'This Year') && (
                            <div
                                id="profit-cogs-distribution"
                                className="trends-wrapper report-enter-card"
                                style={{
                                    ...getEnterDelayStyle(260),
                                    border: (activeSection === 'profit-cogs-distribution' ? '3px solid #2196F3' : '1px solid #e0e0e0'),
                                    transition: 'all 0.3s ease',
                                    scrollMarginTop: '100px',
                                    scrollMarginBottom: '20px',
                                    marginTop: '8px',
                                    marginBottom: '24px',
                                    padding: '20px',
                                    backgroundColor: '#fff',
                                    borderRadius: '12px',
                                    boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                                    minHeight: '260px'
                                }}
                            >
                                <h2 className="section-title" style={{ marginTop: '0px', marginBottom: '20px', fontSize: '18px', fontWeight: 600, background: 'transparent', padding: 0, color: 'black' }}>
                                    {t('reports.grossProfitVsCogs')}
                                </h2>
                                {/* A failed summary request leaves the PREVIOUS range's figures in
                                    `summary`; drawing them here would present them as this period's. */}
                                {summaryUnavailable ? (
                                    <p style={{ color: '#6b7280', fontSize: '13px', margin: '8px 0' }}>
                                        The summary didn&apos;t load, so this chart is blank rather than showing another period&apos;s figures.
                                    </p>
                                ) : (summary.total_sales > 0 || Number(expenses.total_expenses || 0) + Number(wastageExpenses.total_expenses || 0) > 0) ? (
                                    <div style={{ display: 'flex', alignItems: 'flex-start', gap: '20px', width: '100%', padding: '0 10px', flexDirection: (windowWidth <= 480 ? 'column' : 'row') }}>
                                        {/* Pie Chart */}
                                        <div style={{ flex: windowWidth <= 480 ? '1' : '0 0 220px', position: 'relative', width: windowWidth <= 480 ? '100%' : 'auto', maxWidth: windowWidth <= 480 ? '280px' : 'none', margin: windowWidth <= 480 ? '0 auto' : '0' }}>
                                            {!(summary.all_cogs_unavailable && Number(summary.total_sales || 0) > 0) && (
                                            <>
                                            <ResponsiveContainer width="100%" height={220}>
                                                <PieChart>
                                                    <Pie
                                                        data={donutSegments(summary, expenses)}
                                                        dataKey="value"
                                                        nameKey="name"
                                                        cx="50%"
                                                        cy="50%"
                                                        outerRadius={75}
                                                        innerRadius={45}
                                                        startAngle={90}
                                                        endAngle={450}
                                                        isAnimationActive
                                                        stroke="none"
                                                        strokeWidth={0}
                                                    >
                                                        {/* The same wedges, so data and colours cannot drift apart. */}
                                                        {donutSegments(summary, expenses).map((seg) => (
                                                            <Cell key={seg.key} fill={seg.color} stroke="none" strokeWidth={0} />
                                                        ))}
                                                    </Pie>
                                                    <Tooltip
                                                        formatter={(value, name, props) => {
                                                            const actualValue = props.payload?.actualValue || value;
                                                            return [`₹${Number(actualValue).toLocaleString('en-IN')}`, name];
                                                        }}
                                                        contentStyle={{
                                                            backgroundColor: '#fff',
                                                            border: '1px solid #ddd',
                                                            borderRadius: '8px',
                                                            padding: '8px 12px',
                                                            boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                                                            zIndex: 9999
                                                        }}
                                                        wrapperStyle={{ zIndex: 9999 }}
                                                    />
                                                </PieChart>
                                            </ResponsiveContainer>
                                            {/* Center Text */}
                                            <div style={{
                                                position: 'absolute',
                                                top: '50%',
                                                left: '50%',
                                                transform: 'translate(-50%, -50%)',
                                                textAlign: 'center',
                                                pointerEvents: 'none',
                                                zIndex: 10,
                                                backgroundColor: '#fff',
                                                borderRadius: '50%',
                                                width: '90px',
                                                height: '90px',
                                                display: 'flex',
                                                flexDirection: 'column',
                                                justifyContent: 'center',
                                                alignItems: 'center'
                                            }}>
                                                <div style={{ fontSize: '10px', color: '#666', marginBottom: '2px' }}>Net Sale</div>
                                                <div style={{ fontSize: '14px', fontWeight: 'bold', color: '#333' }}>
                                                    ₹{(() => {
                                                        const totalSales = Number(summary.total_sales || 0);
                                                        return totalSales.toLocaleString('en-IN');
                                                    })()}
                                                </div>
                                            </div>
                                                </>
                                                )}
                                        </div>

                                        {/* Legend */}
                                        <div style={{ flex: '1', paddingTop: (windowWidth <= 480 ? '0' : '3rem'), minWidth: '0', width: '100%', textAlign: 'left' }}>
                                            {(() => {
                                                const totalSales = Number(summary.total_sales || 0);
                                                const grossProfitSigned = profitSplit(summary).profit; // the card's figure: net of wastage
                                                const grossProfit = Math.abs(grossProfitSigned);
                                                const grossProfitIsLoss = grossProfitSigned < 0;
                                                const totalWastage = Number(summary.total_wastage || 0);
                                                const totalExpenseVal = Number(expenses.total_expenses || 0) + Number(wastageExpenses.total_expenses || 0);
                                                const cogsUnavailable = !!summary.all_cogs_unavailable;
                                                // These totals ARE shown — part of the cost behind them was priced from an
                                                // old purchase price, so they carry "~". A separate state from
                                                // "unavailable": estimated money is in the total, unknown money is not.
                                                const cogsEstimated = !cogsUnavailable && !!summary.has_estimated_cogs_items;
                                                const estimatedAmount = Number(summary.estimated_cogs_amount || 0);
                                                const estimatedCount = Number(summary.estimated_cogs_item_count || 0);
                                                const estMark = cogsEstimated ? '~' : '';
                                                // A2: dishes held out of these totals entirely — no price history at
                                                // all, so neither the ladder nor the setting can reach them.
                                                const excludedCount = Number(summary.excluded_cogs_item_count || 0);
                                                const excludedRevenue = Number(summary.excluded_cogs_revenue || 0);

                                                // Use actual COGS from backend, fallback to calculation only if undefined
                                                const cogs = profitSplit(summary).cogs;


                                                // For Gross Profit & COGS: % of the sales these two figures actually
                                                // describe, so they still add to 100%.
                                                //
                                                // NOT % of Total Sales any more. Total Sales is the whole bill, including
                                                // "Not in Menu" lines that have no cost and therefore contribute to
                                                // neither of these two numbers; dividing by it would leave the pie
                                                // summing to the menu's share of the bill instead of to 100.
                                                const profitBasis = (summary.display_sales_basis !== undefined && summary.display_sales_basis !== null)
                                                    ? Number(summary.display_sales_basis)
                                                    : totalSales;
                                                const grossProfitPercent = profitBasis > 0 ? ((grossProfit / profitBasis) * 100) : 0;
                                                const cogsPercent = profitBasis > 0 ? ((cogs / profitBasis) * 100) : 0;
                                                // Wastage is a DRAWN SLICE, so it needs a share like the other two.
                                                // Without one the legend read 88.3% + 11.7% = 100% beside a donut with
                                                // THREE wedges in it — a third of the picture carrying no number at all.
                                                // net profit + COGS + wastage === sales basis, so these three now total
                                                // 100% by construction.
                                                const wastagePercent = profitBasis > 0 ? ((totalWastage / profitBasis) * 100) : 0;
                                                
                                                // For Wastage: Show absolute amount (independent of sales)

                                                return (
                                                    <>
                                                        {totalSales > 0 && (grossProfit > 0.01 || cogsUnavailable || grossProfitIsLoss) && (
                                                            <div style={{ marginBottom: '16px' }}>
                                                                <div style={{ display: 'flex', alignItems: 'flex-start' }}>
                                                                    <div style={{
                                                                        width: '14px',
                                                                        height: '14px',
                                                                        backgroundColor: grossProfitIsLoss ? '#FF3B30' : '#34c759',
                                                                        borderRadius: '3px',
                                                                        flexShrink: 0,
                                                                        marginTop: '3px',
                                                                        marginRight: '8px'
                                                                    }}></div>
                                                                    <div style={{ textAlign: 'left', width: '100%' }}>
                                                                        <div style={{ fontSize: '14px', fontWeight: '600', color: 'black', marginBottom: '4px', textAlign: 'left' }}>
                                                                            {grossProfitIsLoss ? 'Net Loss' : 'Gross Profit'} {cogsUnavailable ? '-' : `${estMark}${grossProfitPercent.toFixed(1)}%`}
                                                                        </div>
                                                                        <div style={{ fontSize: '15px', fontWeight: 'bold', color: grossProfitIsLoss ? '#FF3B30' : '#34c759', textAlign: 'left' }}>
                                                                            {cogsUnavailable ? '-' : `${estMark}${grossProfitIsLoss ? '−' : ''}₹${grossProfit.toLocaleString('en-IN')}`}
                                                                        </div>
                                                                        {/* C9. Fires only past the threshold, and says what to DO rather than merely
                                                                            that something is wrong. Placed above the "~" note so the scale of the
                                                                            estimate is read before its explanation. */}
                                                                        {cogsEstimated && summary.estimated_cogs_share?.warn && (
                                                                            <div style={{ fontSize: '11px', color: '#b45309', background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 6, padding: '6px 8px', marginTop: '6px', maxWidth: '260px', fontWeight: 600 }}>
                                                                                {summary.estimated_cogs_share.message}
                                                                            </div>
                                                                        )}
                                                                        {cogsEstimated && (
                                                                            <div style={{ fontSize: '11px', color: '#a16207', marginTop: '4px', maxWidth: '260px' }}>
                                                                                ~ Includes ₹{estimatedAmount.toLocaleString('en-IN', { maximumFractionDigits: 0 })} estimated
                                                                                cost across {estimatedCount} item{estimatedCount === 1 ? '' : 's'} priced from an
                                                                                earlier purchase price.
                                                                            </div>
                                                                        )}
                                                                        {/* A2. Stated separately from the "~" note above because the two describe
                                                                            opposite things: "~" is money that IS in this figure but was estimated;
                                                                            this is money that is NOT in it at all. Collapsing them would let an
                                                                            owner read "estimated" and assume the total is still complete. */}
                                                                        {!cogsUnavailable && excludedCount > 0 && (
                                                                            <div style={{ fontSize: '11px', color: '#6b7280', marginTop: '4px', maxWidth: '260px' }}>
                                                                                Excludes {excludedCount} item{excludedCount === 1 ? '' : 's'} with no price history
                                                                                {excludedRevenue > 0 ? ` (₹${excludedRevenue.toLocaleString('en-IN', { maximumFractionDigits: 0 })} sales)` : ''}.
                                                                                Count or buy {excludedCount === 1 ? 'it' : 'them'} to bring {excludedCount === 1 ? 'it' : 'them'} in.
                                                                            </div>
                                                                        )}
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        )}

                                                        {totalSales > 0 && summary.all_cogs_unavailable && (
                                                            <div style={{ marginBottom: '16px' }}>
                                                                <div style={{ display: 'flex', alignItems: 'flex-start' }}>
                                                                    <div style={{
                                                                        width: '14px',
                                                                        height: '14px',
                                                                        backgroundColor: '#93b8f5',
                                                                        borderRadius: '3px',
                                                                        flexShrink: 0,
                                                                        marginTop: '3px',
                                                                        marginRight: '8px'
                                                                    }}></div>
                                                                    <div style={{ textAlign: 'left', width: '100%' }}>
                                                                        <div style={{ fontSize: '14px', fontWeight: '600', color: 'black', marginBottom: '4px' }}>
                                                                            Manufacturing Cost
                                                                        </div>
                                                                        <div style={{ fontSize: '15px', fontWeight: 'bold', color: '#666', marginBottom: '6px' }}>
                                                                            -
                                                                        </div>
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        )}

                                                        {totalSales > 0 && !summary.all_cogs_unavailable && cogs > 0.01 && (
                                                            <div style={{ marginBottom: '16px' }}>
                                                                <div style={{ display: 'flex', alignItems: 'flex-start' }}>
                                                                    <div style={{
                                                                        width: '14px',
                                                                        height: '14px',
                                                                        backgroundColor: '#FF9500',
                                                                        borderRadius: '3px',
                                                                        flexShrink: 0,
                                                                        marginTop: '3px',
                                                                        marginRight: '8px'
                                                                    }}></div>
                                                                    <div style={{ textAlign: 'left', width: '100%' }}>
                                                                        <div style={{ fontSize: '14px', fontWeight: '600', color: 'black', marginBottom: '4px' }}>
                                                                            Manufacturing Cost {estMark}{cogsPercent.toFixed(1)}%
                                                                        </div>
                                                                        <div style={{ fontSize: '15px', fontWeight: 'bold', color: '#FF9500', marginBottom: '6px' }}>
                                                                            {estMark}₹{cogs.toLocaleString('en-IN')}
                                                                        </div>
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        )}

                                                        {totalWastage > 0.01 && (
                                                            <div style={{ marginBottom: '8px' }}>
                                                                <div style={{ display: 'flex', alignItems: 'flex-start' }}>
                                                                    <div style={{
                                                                        width: '14px',
                                                                        height: '14px',
                                                                        backgroundColor: '#FF3B30',
                                                                        borderRadius: '3px',
                                                                        flexShrink: 0,
                                                                        marginTop: '3px',
                                                                        marginRight: '8px'
                                                                    }}></div>
                                                                    <div style={{ textAlign: 'left', width: '100%' }}>
                                                                        <div style={{ fontSize: '14px', fontWeight: '600', color: 'black', marginBottom: '4px', textAlign: 'left' }}>
                                                                            Waste & Losses {wastagePercent.toFixed(1)}%
                                                                        </div>
                                                                        <div style={{ fontSize: '15px', fontWeight: 'bold', color: '#FF3B30', textAlign: 'left' }}>
                                                                            ₹{totalWastage.toLocaleString('en-IN')}
                                                                        </div>
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        )}

                                                        {/* Expense-only view when no sales exist */}
                                                        {totalSales === 0 && Number(expenses.total_expenses || 0) > 0 && (
                                                                <div style={{ marginBottom: '16px' }}>
                                                                    <div style={{ display: 'flex', alignItems: 'flex-start' }}>
                                                                        <div style={{
                                                                            width: '14px',
                                                                            height: '14px',
                                                                            backgroundColor: '#FF9500',
                                                                            borderRadius: '3px',
                                                                            flexShrink: 0,
                                                                            marginTop: '3px',
                                                                            marginRight: '8px'
                                                                        }}></div>
                                                                        <div style={{ textAlign: 'left', width: '100%' }}>
                                                                            <div style={{ fontSize: '14px', fontWeight: '600', color: 'black', marginBottom: '4px', textAlign: 'left' }}>
                                                                                Expenses (Stock-In)
                                                                            </div>
                                                                            <div style={{ fontSize: '15px', fontWeight: 'bold', color: '#FF9500', textAlign: 'left' }}>
                                                                                ₹{Number(expenses.total_expenses || 0).toLocaleString('en-IN')}
                                                                            </div>
                                                                        </div>
                                                                    </div>
                                                                </div>
                                                        )}
                                                        {totalSales === 0 && grossProfit < 0 && (
                                                                    <div style={{ marginBottom: '16px' }}>
                                                                        <div style={{ display: 'flex', alignItems: 'flex-start' }}>
                                                                            <div style={{
                                                                                width: '14px',
                                                                                height: '14px',
                                                                                backgroundColor: '#FF3B30',
                                                                                borderRadius: '3px',
                                                                                flexShrink: 0,
                                                                                marginTop: '3px',
                                                                                marginRight: '8px'
                                                                            }}></div>
                                                                            <div style={{ textAlign: 'left', width: '100%' }}>
                                                                                <div style={{ fontSize: '14px', fontWeight: '600', color: 'black', marginBottom: '4px', textAlign: 'left' }}>
                                                                                    Net Loss
                                                                                </div>
                                                                                <div style={{ fontSize: '15px', fontWeight: 'bold', color: '#FF3B30', textAlign: 'left' }}>
                                                                                    ₹{Math.abs(grossProfit).toLocaleString('en-IN')}
                                                                                </div>
                                                                            </div>
                                                                        </div>
                                                                    </div>
                                                        )}

                                                        {summary.has_estimated_items && (
                                                            <div style={{
                                                                marginTop: '12px',
                                                                padding: '8px 10px',
                                                                backgroundColor: '#FFF8E1',
                                                                borderRadius: '6px',
                                                                border: '1px solid #FFE082',
                                                                display: 'flex',
                                                                alignItems: 'flex-start',
                                                                gap: '6px'
                                                            }}>
                                                                
                                                                <div style={{ fontSize: '11px', color: '#8D6E00', lineHeight: '1.4' }}>
                                                                    <strong>{summary.estimated_item_count}</strong> item{summary.estimated_item_count > 1 ? 's' : ''} sold without recipe/ingredients. Their COGS is shown as - until recipe and stock data are available.
                                                                </div>
                                                            </div>
                                                        )}
                                                    </>
                                                );
                                            })()}
                                        </div>
                                    </div>
                                ) : (
                                    <NoDataPlaceholder
                                        type="compare"
                                        title="Nothing to compare yet"
                                        subtitle="Add sales to see gross profit weighed against manufacturing cost."
                                        minHeight="220px"
                                    />
                                )}
                            </div>
                        )}


                    </main>
                )}

                {/* ===== FOOTER ===== */}
                {!isLoading && !fatalError && (
                <footer className="report-footer">
                    <button type="button" className="footer-btn export-btn" onClick={openExportPicker}>
                        <ExportIcon /> Export Report
                    </button>
                    <button type="button" className="footer-btn share-btn" onClick={handleShareReport}><ShareIcon /> Share</button>
                </footer>
                )}

                {/* ===== EXPORT PICKER MODAL ===== */}
                <NotInMenuWorklist
                    open={nimOpen}
                    onClose={() => setNimOpen(false)}
                    onChanged={() => { retryReport(); notInMenu.reload(); }}
                />
                <ExportReportModal
                    isOpen={showExportPicker}
                    onClose={() => setShowExportPicker(false)}
                    chartData={chartData}
                    topProfitItems={topProfitItems}
                    summary={summary}
                    summaryComparison={summaryComparison}
                    dailyBreakdown={dailyBreakdown}
                    expenses={expenses}
                    wastageExpenses={wastageExpenses}
                    selectedPeriod={selectedPeriod}
                    displayText={displayText}
                    selectedWeek={selectedWeek}
                    selectedDate={selectedDate}
                    fixedMonthlyExpense={fixedMonthlyExpense}
                    outletBreakdown={outletBreakdown}
                    fixedShare={fixedShareFull}
                />

                <DatePickerCombined
                    isOpen={showDatePicker}
                    onClose={() => setShowDatePicker(false)}
                    onWeekSelect={handleWeekSelect}
                    onMonthSelect={handleDateSelect}
                />
            </div >

            {
                showExportModal && (
                    <div style={{
                        position: 'fixed',
                        inset: 0,
                        backgroundColor: 'rgba(0,0,0,0.5)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        zIndex: 9999,
                        padding: '10px'
                    }} onClick={() => setShowExportModal(false)}>
                        <div style={{
                            width: 'min(380px, 90vw)',
                            background: '#fff',
                            borderRadius: 12,
                            boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
                            padding: '32px 24px',
                            textAlign: 'center'
                        }} onClick={e => e.stopPropagation()}>
                            <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '16px' }}>
                                <div style={{
                                    width: '60px',
                                    height: '60px',
                                    borderRadius: '50%',
                                    border: '3px solid #22c55e',
                                    backgroundColor: 'white',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center'
                                }}>
                                    <Check size={36} color="#22c55e" strokeWidth={3} />
                                </div>
                            </div>
                            <div style={{
                                fontSize: '18px',
                                fontWeight: '600',
                                marginBottom: '16px',
                                color: '#1f2937'
                            }}>
                                Report Downloaded Successfully
                            </div>
                            <button
                                onClick={() => setShowExportModal(false)}
                                style={{
                                    padding: '10px 24px',
                                    backgroundColor: '#000000',
                                    color: '#fff',
                                    border: 'none',
                                    borderRadius: '8px',
                                    fontSize: '14px',
                                    fontWeight: '600',
                                    cursor: 'pointer'
                                }}
                            >
                                OK
                            </button>
                        </div>
                    </div>
                )
            }

            <style>
                {`
                    @import url('https://fonts.googleapis.com/css2?family=Roboto:wght@400;500;600;700&display=swap');

                    * {
                        font-family: 'Roboto', sans-serif !important;
                    }

                    body {
                        margin: 0;
                        background-color: #f0f2f5;
                        font-family: 'Roboto', sans-serif;
                    }

                    .report-container {
                        max-width: 600px;
                        width: 100%;
                        margin: 0 auto;
                        background-color: #fff;
                        min-height: 100vh;
                        /* Halve left/right padding; keep top 35px */
                        padding: 10px 18px;
                        box-sizing: border-box;
                        position: relative;
                        /* Increase bottom padding */
                        padding-bottom: 4rem;
                        font-family: 'Roboto', sans-serif;
                    }
                    @media (max-width: 768px) {
                        .report-container {
                            /* Halve left/right padding; bottom 4rem */
                            padding: 25px 12px 4rem 12px;
                        }
                    }

                    @media (max-width: 480px) {
                        .report-container {
                            /* Further reduce horizontal padding on very small screens; bottom 4rem */
                            padding: 20px 6px 4rem 6px;
                        }
                    }

                    .sales-date-picker-overlay {
                        position: fixed;
                        top: 0;
                        left: 0;
                        right: 0;
                        bottom: 0;
                        background-color: rgba(0, 0, 0, 0.5);
                        display: flex;
                        align-items: center;
                        justify-content: center;
                        z-index: 1000;
                    }

                    /* Base calendar popup: fixed 600px on larger screens */
                    .report-container .sales-date-picker-popup {
                        background: white;
                        border-radius: 12px;
                        padding: 20px;
                        box-shadow: 0 10px 25px rgba(0, 0, 0, 0.2);
                        width: 600px !important; /* enforce 600px on desktop */
                        max-width: 600px !important;
                    }
                    /* Allow shrink on medium and small screens */
                    @media (max-width: 800px) {
                        .report-container .sales-date-picker-popup {
                            width: 90%;
                            max-width: 600px;
                        }
                    }
                    @media (max-width: 480px) {
                        .report-container .sales-date-picker-popup {
                            width: 95%;
                            max-width: 95%;
                        }
                    }

                    .sales-date-picker-popup .calendar-header {
                        display: flex;
                        justify-content: space-between;
                        align-items: center;
                        margin-bottom: 20px;
                    }

                    .sales-date-picker-popup .calendar-header button {
                        background: none;
                        border: none;
                        font-size: 18px;
                        cursor: pointer;
                        padding: 5px 10px;
                        border-radius: 4px;
                    }

                    .sales-date-picker-popup .calendar-header button:hover {
                        background-color: #f0f2f5;
                    }

                    .sales-date-picker-popup .calendar-header h3 {
                        margin: 0;
                        font-size: 16px;
                        font-weight: 600;
                    }

                    .sales-date-picker-popup .calendar-weekdays {
                        display: grid;
                        grid-template-columns: repeat(7, 1fr);
                        gap: 5px;
                        margin-bottom: 10px;
                    }

                    .sales-date-picker-popup .weekday {
                        text-align: center;
                        font-size: 12px;
                        font-weight: 500;
                        color: #6c757d;
                        padding: 5px;
                    }

                    .sales-date-picker-popup .calendar-grid {
                        display: grid;
                        grid-template-columns: repeat(7, 1fr);
                        gap: 5px;
                    }

                    .sales-date-picker-popup .calendar-day {
                        aspect-ratio: 1;
                        display: flex;
                        align-items: center;
                        justify-content: center;
                        font-size: 14px;
                        cursor: pointer;
                        border-radius: 4px;
                        transition: background-color 0.2s;
                    }

                    .sales-date-picker-popup .calendar-day:hover {
                        background-color: #f0f2f5;
                    }

                    .sales-date-picker-popup .calendar-day.today {
                        background-color: #007aff;
                        color: white;
                    }

                    .sales-date-picker-popup .calendar-day.empty {
                        cursor: default;
                    }

                    .sales-date-picker-popup .calendar-day.empty:hover {
                        background-color: transparent;
                    }

                    .report-header {
                        display: flex;
                        flex-direction: column;
                        padding: 0.5rem 0;
                        
                    }

                    .header-row {
                        display: flex;
                        justify-content: flex-start;
                        align-items: center;
                        gap: 1rem;
                        margin-left: 20px;
                        margin-top: -18px;
}


                    .report-header h1 {
                        font-size: 1.2rem;
                        font-weight: 600;
                        margin: 0;
                    }

                    .date-picker {
                        display: flex;
                        align-items: center;
                        justify-content: space-between;
                        background-color: #f5f5f5;
                        padding: 0.5rem 1rem;
                        border-radius: 8px;
                        font-size: 0.9rem;
                        font-weight: 500;
                        width: 100%;
                        margin-top: 1rem;
                        position: relative;
                    }

                    /* Match stockrepo dropdown styles */
                    .period-trigger {
                        flex: 1;
                        display: flex;
                        align-items: center;
                        justify-content: space-between;
                        color: #000;
                        padding: 10px 14px;
                        cursor: pointer;
                    }
                    .period-trigger .label { font-weight: 500; font-size: 14px; }

                    .period-menu {
                        position: absolute;
                        top: calc(100% + 6px);
                        left: 46px; /* leave space for calendar icon */
                        right: 0;
                        background: #fff;
                        border: 1px solid #e5e7eb;
                        border-radius: 12px;
                        box-shadow: 0 8px 24px rgba(0, 0, 0, 0.18);
                        z-index: 1100;
                        padding: 6px; /* soften edge */
                    }
                    .period-menu .select-list {
                        max-height: 240px;
                        overflow-y: auto;
                        overflow-x: hidden;
                        -webkit-overflow-scrolling: touch;
                        border-radius: 10px;
                        background: #fff;
                        clip-path: inset(0 round 10px);
                    }
                    .period-option {
                        padding: 12px 14px;
                        display: flex;
                        align-items: center;
                        justify-content: space-between;
                        cursor: pointer;
                        font-size: 14px;
                        font-weight: 500;
                    }
                    .period-option:hover { background: #f5f5f5; }
                    .period-option.active { background: #f5f5f5; font-weight: 600; }
                    .chevron-down {
                            border: solid black;
                            border-width: 0 2px 2px 0;
                            display: inline-block;
                            padding: 3px;
                            transform: rotate(45deg);
                            -webkit-transform: rotate(45deg);
                            position: absolute;
                            right: 1rem;
                    }

                    .section-title {
                        font-size: 1.1rem;
                        font-weight: 600;
                        margin: 0rem 0 1rem 0;
                        text-align: left;
                    }

                    .percentage-up {
                        color: #34c759;
                        font-size: 0.95rem;
                        font-weight: 500;
                        display: flex;
                        align-items: center;
                        gap: 0.25rem;
                        margin: 0;
                    }

                    .stats-grid {
                        padding-top: 1rem;
                        display: grid;
                        grid-template-columns: repeat(2, 1fr);
                        gap: 1rem;
                    }

                    .sr-stat-card {
                        background-color: #fff;
                        border-radius: 0.5rem;
                        padding: 1rem 1rem 3rem 1rem;
                        box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);
                        text-align: left;
                        min-height: 100px;
                    }

                    @media (max-width: 480px) {
                        .stats-grid {
                            grid-template-columns: repeat(2, 1fr);
                            gap: 0.75rem;
                        }
                    }

                    .card-title {
                        font-size: 0.85rem;
                        color: #6c757d;
                        margin: 0 0 1.2rem 0;
                        padding-bottom: 0;
                        line-height: 1.2;
                    }

                    .card-value {
                        font-size: 1rem;
                        font-weight: 700;
                        color: #374151;
                        margin: 0;
                        padding-bottom: 4px;
                        line-height: 1.15;
                        margin-top: 15px;
                    }

                    .payment-methods {
                        display: flex;
                        flex-direction: row;
                        gap: 1rem;
                        justify-content: space-between;
                    }

                    .payment-card {
                        background-color: #fff;
                        border-radius: 12px;
                        padding: 1rem;
                        border: 2px solid transparent;
                        display: flex;
                        justify-content: space-between;
                        align-items: flex-start;
                        flex: 1;
                        box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
                    }

                    @media (max-width: 600px) {
                        .payment-methods {
                            flex-direction: row !important;
                            gap: 0.5rem;
                        }
                        .payment-card {
                            padding: 0.7rem 0.3rem;
                        }
                    }

                    .payment-left {
                        display: flex;
                        flex-direction: column;
                        align-items: flex-start;
                    }

                    .payment-left svg {
                        margin-bottom: 0.5rem;
                    }

                    .payment-title {
                        margin: 0;
                        font-weight: 500;
                    }

                    .payment-right {
                        text-align: right;
                    }

                    .payment-percent {
                        margin: 0;
                        font-weight: 700;
                        font-size: 1.2rem;
                        color: #3B82F6;
                        padding-bottom: 0.2rem;
                    }

                    .payment-value {
                        margin: 0.5rem 0 0 0;
                        color: #22C55E;
                        font-size: 0.8rem;
                    }

                    .trends-wrapper {
                        background-color: #ffffff;
                        border-radius: 12px;
                        padding: 1.3rem;
                        box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
                        margin-bottom: 1.5rem;
                    }

                    .trend-container {
                        background-color: #F9FAFB;
                        border-radius: 12px;
                        padding: 1.5rem;
                        height: 200px;
                        display: flex;
                        align-items: center;
                        justify-content: center;
                        margin-bottom: 1rem;
                        box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
                    }

                    @media (max-width: 768px) {
                        .trends-wrapper {
                            padding: 1rem;
                        }
                        
                        .trend-container {
                            padding: 1rem;
                            height: auto;
                            min-height: 180px;
                        }
                    }

                    @media (max-width: 480px) {
                        .trends-wrapper {
                            padding: 0.75rem;
                        }
                        
                        .trend-container {
                            padding: 0.75rem;
                            min-height: 160px;
                        }
                    }

                    .trend-card-main {
                        text-align: center;
                    }

                    .trend-card-main h3 {
                        margin: 0 0 0.5rem 0;
                        font-weight: 600;
                        font-size: 1.5rem;
                        padding-bottom:5px;
                        line-height: 28px;
                    }

                    .trend-peak {
                        font-size: 1.2rem;
                        font-weight: 700;
                        color: #007aff;
                        margin: 0 0 0.5rem 0;
                        padding: 10px;
                    }

                    .trend-peak.secondary {
                        color: #007aff;
                    }

                    .profit-details {
                        display: flex;
                        flex-direction: column;
                        gap: 0.5rem;
                        margin-top: 1rem;
                    }

                    .trend-days {
                        display: flex;
                        gap: 0.75rem;
                        margin-top: 1.5rem;
                        overflow-x: auto;
                        white-space: nowrap;
                        padding-bottom: 0.5rem;
                        -webkit-overflow-scrolling: touch;
                    }

                    .day-card {
                        flex: 0 0 auto;
                        background-color: #000;
                        color: #fff;
                        text-align: center;
                        padding: 0.75rem;
                        border-radius: 8px;
                        font-weight: 500;
                        font-size: 0.9rem;
                        width: 100px;
                        box-sizing: border-box;
                    }

                    @media (max-width: 480px) {
                        .trend-days {
                            gap: 0.5rem;
                        }
                        
                        .day-card {
                            padding: 0.5rem;
                            font-size: 0.8rem;
                            width: 80px;
                        }
                        
                        .day-card span {
                            font-size: 0.8rem;
                        }
                    }

                    .day-card span {
                        display: block;
                        font-weight: 600;
                        margin-top: 0.25rem;
                        color: #22C55E;
                    }

                    .section-header {
                        display: flex;
                        justify-content: space-between;
                        align-items: center;
                    }

                    .items-list {
                        display: flex;
                        flex-direction: column;
                        gap: 0;
                        max-height: 267px;
                        overflow-y: auto;
                        padding-right: 8px;
                    }

                    .item-cards {
                        display: flex;
                        align-items: center;
                        padding: 0.75rem 0;
                        border-bottom: 1px solid #e9ecef;
                        min-height: 65px;
                    }

                    @media (max-width: 480px) {
                        .section-header {
                            margin-top: 1.5rem;
                        }
                        
                        .section-title {
                            font-size: 1rem;
                        }
                        
                        .item-cards {
                            padding: 0.5rem 0;
                        }
                        
                        .item-cards img {
                            width: 40px;
                            height: 40px;
                            margin-right: 0.75rem;
                        }
                        
                        .item-name {
                            font-size: 0.9rem;
                        }
                        
                        .item-sold {
                            font-size: 0.75rem;
                        }
                        
                        .item-price {
                            font-size: 0.9rem;
                        }
                    }

                    .item-cards:last-child {
                        border-bottom: none;
                    }

                    .item-cards img {
                        width: 50px;
                        height: 50px;
                        border-radius: 8px;
                        object-fit: cover;
                        margin-right: 1rem;
                    }

                    .item-info {
                        flex-grow: 1;
                        text-align: left;
                        padding-left: 0.5rem;
                    }

                    .item-name {
                        font-weight: 600;
                        margin: 0;
                        text-align: left;
                    }

                    .item-sold {
                        font-size: 0.85rem;
                        color: #6c757d;
                        margin: 0.25rem 0 0 0;
                    }

                    .item-revenue {
                        text-align: right;
                    }

                    .item-price {
                        font-weight: 600;
                        margin: 0;
                        font-size: 14px;
                        line-height:20px;
                        color: black;
                    }

                    .item-revenue .percentage-up {
                        justify-content: flex-end;
                    }

                    .analysis-card {
                        background-color: #fff;
                        border-radius: 12px;
                        padding: 1.5rem;
                        border: 1px solid #E5E7EB;
                    }

                    .analysis-title {
                        font-weight: 600;
                        font-size: 1.1rem;
                        margin: 0 0 1rem 0;
                        text-align: left;
                        padding-bottom: 0.3rem;
                    }

                    @media (max-width: 480px) {
                        .analysis-card {
                            padding: 1rem;
                        }
                        
                        .analysis-title {
                            font-size: 1rem;
                        }
                        
                        .analysis-row {
                            font-size: 0.9rem;
                        }
                    }

                    .analysis-row {
                        display: flex;
                        justify-content: space-between;
                        padding: 0.75rem 0;
                        border-bottom: 1px solid #e9ecef;
                    }

                    .analysis-row:last-child {
                        border-bottom: none;
                        padding-bottom: 0;
                    }

                    .analysis-row:first-of-type {
                        padding-top: 0;
                    }

                    .analysis-row span:first-child {
                        color: #6c757d;
                    }

                    .analysis-row span:last-child {
                        font-weight: 600;
                    }

                    .report-footer {
                        display: flex;
                        gap: 1rem;
                        padding: 1rem 0;
                    }

                    .footer-btn {
                        flex: 1;
                        padding: 0.75rem;
                        border-radius: 8px;
                        border: none;
                        font-size: 0.875rem;
                        font-weight: 600;
                        cursor: pointer;
                        display: flex;
                        align-items: center;
                        justify-content: center;
                        gap: 0.5rem;
                    }

                    @media (max-width: 480px) {
                        .report-footer {
                            /* Keep buttons on one row for mobile */
                            flex-direction: row;
                            gap: 0.75rem;
                            padding: 1rem 0 0.75rem;
                        }
                        
                        .footer-btn {
                            padding: 0.75rem 0;
                            font-size: 0.875rem;
                        }
                    }

                    .export-btn {
                        background-color: #e9ecef;
                        color: #000;
                    }

                    .share-btn {
                        background-color: #000;
                        color: #fff;
                    }

                    .revenue-growth {
                        color: #F86C36;
                        font-size: 0.95rem;
                        font-weight: 500;
                        display: flex;
                        align-items: center;
                        gap: 0.25rem;
                        margin: 0;
                        padding: 0 0 5px 15px;
                    }

                    .gross-profit {
                        color: #34c759;
                        font-size: 0.95rem;
                        font-weight: 500;
                        display: flex;
                        align-items: center;
                        gap: 0.25rem;
                        margin: 0;
                        padding-left: 15px;
                    }

                    /* Custom scrollbar styles */
                    .items-list,
                    .trend-days {
                        scrollbar-width: thin;
                        scrollbar-color: #cfd8dc #f0f2f5;
                    }

                    .items-list::-webkit-scrollbar,
                    .trend-days::-webkit-scrollbar {
                        width: 6px;
                        height: 6px;
                        background: #f0f2f5;
                        border-radius: 8px;
                    }

                    .items-list::-webkit-scrollbar-thumb,
                    .trend-days::-webkit-scrollbar-thumb {
                        background: #cfd8dc;
                        border-radius: 8px;
                    }

                    .items-list::-webkit-scrollbar-thumb:hover,
                    .trend-days::-webkit-scrollbar-thumb:hover {
                        background: #b0bec5;
                    }
                    
                    /* Highlighted section styling */
                    .highlighted-section {
                        animation: highlight-pulse 1.5s ease-in-out;
                        border: 3px solid #3b82f6;
                        box-shadow: 0 0 15px rgba(59, 130, 246, 0.5);
                    }
                    
                    @keyframes highlight-pulse {
                        0% { box-shadow: 0 0 5px rgba(59, 130, 246, 0.3); }
                        50% { box-shadow: 0 0 20px rgba(59, 130, 246, 0.7); }
                        100% { box-shadow: 0 0 15px rgba(59, 130, 246, 0.5); }
                    }

                    /* ================= Extra Cards (Profit + Peak Hour) ================= */
                    .extra-cards {
                        display: grid;
                        grid-template-columns: repeat(2, 1fr);
                        gap: 1rem;
                        margin-top: 1.5rem;
                    }

                    .profit-card, .peak-card {
                        background: #fff;
                        border-radius: 12px;
                        padding: 1rem;
                        box-shadow: 0 2px 8px rgba(0,0,0,0.1);
                        text-align: center;
                    }

                    .profit-card h3, .peak-card h3 {
                        margin: 0 0 0.5rem;
                        font-size: 1rem;
                        font-weight: 600;
                    }

                    .profit-card p, .peak-card p {
                        margin: 0;
                        font-size: 1.3rem;
                        font-weight: 700;
                        color: #000;
                    }

                    .trend-card {
                    background: #fff;
                    border-radius: 12px;
                    padding: 1.5rem;
                    margin-bottom: 1.5rem;
                    box-shadow: 0 2px 8px rgba(0,0,0,0.08);
                    border: 1px solid #e5e7eb;
                    text-align: center;
                }

                .trend-title {
                    font-size: 1.2rem;
                    font-weight: 700;
                    margin: 0 0 0.75rem 0;
                }

                .trend-value {
                    font-size: 1.1rem;
                    font-weight: 600;
                    color: #2563eb;
                    margin-bottom: 1rem;
                }

                .gross-profit {
                    color: #34c759;
                    font-size: 0.95rem;
                    font-weight: 500;
                    margin: 0.25rem 0;
                }

                .revenue-growth {
                    color: #f86c36;
                    font-size: 0.95rem;
                    font-weight: 500;
                    margin: 0.25rem 0;
                }

                .trend-peak {
                    font-size: 1rem;
                    font-weight: 600;
                    color: #007aff;
                    margin-bottom: 1rem;
                }

                /* Hour breakdown grid */
                .hour-grid {
                    display: flex;
                    justify-content: space-around;
                    margin-top: 1rem;
                }

                .hour-item {
                    text-align: center;
                    font-size: 0.9rem;
                }

                .hour-item span {
                    display: block;
                }

                .hour-value {
                    margin-top: 0.25rem;
                    color: #22c55e;
                    font-weight: 600;
                }

                /* Center popup style for export success */
                .export-toast {
                    position: fixed;
                    top: 50%;
                    left: 50%;
                    transform: translate(-50%, -50%);
                    background: #ffffff;
                    border: 1px solid #a7f3d0;
                    color: #065f46;
                    padding: 26px 34px 22px;
                    border-radius: 22px;
                    box-shadow: 0 22px 48px -12px rgba(16, 185, 129, 0.35), 0 8px 20px -6px rgba(16,185,129,0.25);
                    z-index: 2147483647;
                    font-weight: 600;
                    font-size: 1rem;
                    display: flex;
                    align-items: center;
                    gap: 14px;
                    pointer-events: none;
                    animation: exportPop .38s cubic-bezier(.4,0,.2,1);
                }

                .export-toast .check-icon {
                    width: 42px;
                    height: 42px;
                    border-radius: 50%;
                    background: #10b981;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    color: #fff;
                    box-shadow: 0 0 0 6px #d1fae5;
                }

                @keyframes exportPop { from { opacity:0; transform: translate(-50%, -46%) scale(.9);} to { opacity:1; transform: translate(-50%, -50%) scale(1);} }

                /* Centered success modal (export) */
                .modal-overlay {
                    position: fixed;
                    inset: 0;
                    background: rgba(17,24,39,0.35);
                    backdrop-filter: blur(6px);
                    -webkit-backdrop-filter: blur(6px);
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    z-index: 2147483647;
                    padding: 0;
                    margin: 0;
                }

                .modal-card {
                    background: #fff;
                    width: min(92vw, 360px);
                    border-radius: 16px;
                    padding: 24px 16px 16px;
                    box-shadow: 0 10px 30px rgba(0,0,0,0.25);
                    text-align: center;
                }

                /* Expense Details Modal */
                .expense-modal-overlay {
                    position: fixed;
                    inset: 0;
                    background: rgba(17,24,39,0.35);
                    backdrop-filter: blur(6px);
                    -webkit-backdrop-filter: blur(6px);
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    z-index: 2000;
                    padding: 0 !important;
                    margin: 0;
                }

                .expense-modal-container {
                    border-radius: 0.5rem;
                    width: 600px !important;
                    max-width: 600px !important;
                    height: 100vh;
                    height: 100dvh;
                    max-height: 100vh;
                    max-height: 100dvh;
                    background: #fff;
                    overflow: hidden;
                    display: flex;
                    flex-direction: column;
                    margin: 0 auto !important;
                    box-sizing: border-box;
                    box-shadow: 0 20px 40px rgba(0,0,0,0.2);
                }
                
                /* Prevent global modal card styles from affecting expense modal cards */
                .expense-modal-container .expense-record-card,
                .expense-modal-container .expense-summary-card {
                    margin: 0 !important;
                }
                
                .expense-modal-container .expense-record-card {
                    margin-bottom: 12px !important;
                }

                .expense-records-scroll-area {
                    flex: 1;
                    min-height: 0;
                    overflow-y: auto;
                    overflow-x: hidden;
                    padding-right: 2px;
                    scrollbar-width: none;
                    -ms-overflow-style: none;
                }

                .expense-records-scroll-area::-webkit-scrollbar {
                    width: 0;
                    height: 0;
                }

                .expense-search-filter-row {
                    display: flex;
                    align-items: center;
                    gap: 8px;
                    margin-top: 14px;
                    margin-bottom: 2px;
                }

                .expense-search-wrapper {
                    flex: 1;
                    min-width: 0;
                }

                .expense-dropdown-filter-container {
                    position: relative;
                    display: inline-flex;
                    align-items: center;
                }

                .expense-dropdown-filter-btn {
                    border: none;
                    background: #ffffff;
                    border-radius: 8px;
                    height: 44px;
                    min-width: 108px;
                    padding: 10px 12px;
                    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
                    cursor: pointer;
                    display: inline-flex;
                    align-items: center;
                    justify-content: space-between;
                    gap: 8px;
                }

                .expense-dropdown-filter-btn:hover,
                .expense-dropdown-filter-btn:focus,
                .expense-dropdown-filter-btn:focus-visible,
                .expense-dropdown-filter-btn:active {
                    background: #ffffff !important;
                    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1) !important;
                    transform: none !important;
                    opacity: 1 !important;
                }

                .expense-dropdown-filter-value {
                    font-size: 14px;
                    font-weight: 600;
                    color: #111827;
                    line-height: 1;
                }

                .expense-dropdown-arrow {
                    font-size: 13px;
                    color: #6b7280;
                }

                .expense-dropdown-menu {
                    position: absolute;
                    top: 52px;
                    right: 0;
                    background: #ffffff;
                    border-radius: 8px;
                    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.14);
                    border: 1px solid #e5e7eb;
                    min-width: 140px;
                    padding: 6px;
                    z-index: 30;
                }

                .expense-dropdown-option {
                    width: 100%;
                    text-align: left;
                    border: none;
                    background: transparent;
                    border-radius: 6px;
                    padding: 10px 12px;
                    font-size: 14px;
                    font-weight: 500;
                    color: #111827;
                    cursor: pointer;
                }

                .expense-dropdown-option.active {
                    background: #f3f4f6;
                    font-weight: 600;
                }

                .modal-icon { margin-bottom: 8px; display:flex; justify-content:center; }

                .modal-text {
                    font-weight: 700;
                    color: #0f172a;
                    margin: 1px 0 21px;
                }

                .modal-btn {
                    background: #22c55e;
                    color: #fff;
                    border: none;
                    border-radius: 8px;
                    padding: 8px 14px;
                    font-weight: 700;
                    cursor: pointer;
                    width: auto;
                    min-width: 96px;
                    font-size: 0.9rem;
                    margin: 0 auto;
                }

                /* Responsive Styles */
                    @media (max-width: 768px) {
                    .report-container {
                        /* Sync with earlier breakpoint override: halve horizontal, bottom 4rem */
                        padding: 10px 5px 4rem 5px;
                    }
                    /* Full-bleed on mobile for all modals */
                    .bep-modal-container,
                    .expense-modal-container,
                    .modal-card {
                        width: 100vw !important;
                        max-width: 100vw !important;
                        margin: 0 !important;
                        left: 0 !important;
                        right: 0 !important;
                    }
                    .expense-modal-container { height: 100dvh; max-height: 100dvh; }
                    .bep-modal-container { height: auto; }
                    .modal-card { width: 100vw !important; }
                    
                    /* Expense Modal Mobile Responsiveness */
                    .expense-modal-header {
                        padding: 16px !important;
                    }
                    
                    .expense-modal-title {
                        font-size: 16px !important;
                    }
                    
                    .expense-modal-subtitle {
                        font-size: 13px !important;
                    }
                    
                    .expense-summary-stats {
                        gap: 10px !important;
                        margin-top: 12px !important;
                    }
                    
                    .expense-summary-card {
                        padding: 12px 14px !important;
                    }
                    
                    .expense-summary-label {
                        font-size: 12px !important;
                    }
                    
                    .expense-summary-value {
                        font-size: 1rem !important;
                        margin-top: 6px !important;
                    }

                    .expense-search-filter-row {
                        margin-top: 12px !important;
                        margin-bottom: 0 !important;
                        gap: 8px !important;
                    }

                    .expense-search-wrapper {
                        min-width: 0;
                    }

                    .expense-dropdown-filter-btn {
                        height: 44px;
                        min-width: 96px;
                        padding: 10px 10px;
                    }

                    .expense-dropdown-filter-value {
                        font-size: 13px;
                    }

                    .expense-dropdown-menu {
                        top: 46px;
                        min-width: 126px;
                    }

                    .expense-dropdown-option {
                        font-size: 13px;
                        padding: 9px 10px;
                    }
                    
                    .expense-modal-body {
                        padding: 16px !important;
                    }

                    .expense-records-scroll-area {
                        padding-right: 0;
                    }
                    
                    .expense-record-card {
                        padding: 12px !important;
                        border-radius: 8px !important;
                    }
                    
                    .expense-badges {
                        gap: 6px !important;
                    }
                    
                    .expense-price {
                        font-size: 1rem !important;
                    }
                    
                    .expense-badge {
                        font-size: 10px !important;
                        padding: 2px 6px !important;
                    }
                    
                    .expense-date {
                        font-size: 12px !important;
                    }
                    
                    .expense-items-header {
                        font-size: 14px !important;
                        margin-bottom: 6px !important;
                    }
                    
                    .expense-items-count {
                        font-size: 14px !important;
                    }
                    
                    .expense-vendor {
                        font-size: 13px !important;
                    }
                    }
                    
                    .stats-grid {
                        grid-template-columns: repeat(2, 1fr);
                        gap: 12px;
                    }
                    
                    .sr-stat-card {
                        padding: 16px;
                    }
                    
                    .trends-wrapper {
                        padding: 15px !important;
                        margin: 16px 0 !important;
                    }
                    
                    .section-title {
                        font-size: 16px !important;
                        margin-bottom: 3px !important;
                    }
                    
                    /* Bar Chart Responsive */
                    .recharts-wrapper {
                        font-size: 11px;
                    }
                    
                    /* Pie Chart Container Responsive */
                    .trends-wrapper .recharts-wrapper {
                        min-height: 200px;
                    }
                    
                    /* Analysis Section */
                    .analysis-section {
                        margin: 16px 0;
                    }
                    
                    .analysis-card {
                        padding: 15px;
                    }
                    
                    .analysis-row {
                        font-size: 13px;
                        margin-bottom: 8px;
                    }
                    
                    /* Header responsive */
                    .report-header {
                    
                    }
                    
                    .report-header h1 {
                        font-size: 1.5rem;
                    }
                    
                    .date-picker {
                        flex-direction: row; /* keep calendar + period on one line */
                        gap: 8px;
                    }
                    
                    .period-trigger {
                        font-size: 14px;
                        padding: 8px 12px;
                    }
                }
                
                @media (max-width: 480px) {
                    .stats-grid {
                        grid-template-columns: repeat(2, 1fr); /* two cards on mobile */
                        gap: 10px;
                    }
                    
                    .sr-stat-card {
                        padding: 12px;
                    }
                    
                    .trends-wrapper {
                        padding: 12px !important;
                        margin: 12px 0 !important;
                    }
                    
                    .section-title {
                        font-size: 14px !important;
                        margin-bottom: 12px !important;
                    }
                    
                    .report-header {
                        padding: 8px 12px;
                    }
                    
                    .report-header h1 {
                        font-size: 1.3rem;
                    }
                    
                    .header-row {
                        gap: 8px;
                    }
                }

                /* Online Reports parity: Header + top date picker (Sales unique classes) */
                .report-container .sr-top-header {
                    display: flex;
                    flex-direction: column;
                    padding: 0.5rem 0;
                    position: static;
                    top: auto;
                    z-index: auto;
                    background-color: #fff;
                }

                .report-container .sr-top-header-row {
                    display: flex;
                    align-items: center;
                    gap: 2px;
                    padding: 0;
                    flex-wrap: nowrap;
                }

                .report-container .sr-top-back-btn {
                    background: none;
                    border: none;
                    padding: 0.4rem;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    cursor: pointer;
                    line-height: 0;
                    box-shadow: none;
                    color: #000;
                }

                .report-container .sr-top-title {
                    margin: 0;
                    font-size: 24px;
                    font-weight: 600;
                    line-height: 1.1;
                    color: #000;
                    font-family: 'Roboto', sans-serif;
                }

                .report-container .sr-top-picker {
                    display: flex;
                    align-items: center;
                    justify-content: space-between;
                    background-color: #f5f5f5;
                    border: 1px solid #e5e7eb;
                    border-radius: 8px;
                    padding: 0.4rem 0.5rem;
                    font-size: 0.9rem;
                    font-weight: 500;
                    width: 100%;
                    position: relative;
                    gap: 0;
                    flex-direction: row;
                }

                .report-container .sr-top-calendar-btn {
                    border: none;
                    background: none;
                    color: #000;
                    padding: 0.35rem 0.45rem;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    cursor: pointer;
                }

                .report-container .sr-top-period-trigger {
                    flex: 1;
                    display: flex;
                    align-items: center;
                    justify-content: space-between;
                    color: #000;
                    background: none;
                    border: none;
                    padding: 10px 14px;
                    cursor: pointer;
                    text-align: left;
                    font-size: inherit;
                }

                .report-container .sr-top-period-label {
                    font-weight: 500;
                    font-size: 14px;
                    color: #000;
                }

                .report-container .sr-top-chevron {
                    border: solid #000;
                    border-width: 0 2px 2px 0;
                    display: inline-block;
                    padding: 3px;
                    transform: rotate(45deg);
                    transition: transform 0.15s ease;
                    margin-left: 0.625rem;
                    position: static;
                    right: auto;
                }

                .report-container .sr-top-chevron.open {
                    transform: rotate(-135deg);
                }

                .report-container .sr-top-period-menu {
                    position: absolute;
                    top: calc(100% + 6px);
                    left: 46px;
                    right: 0;
                    background: #fff;
                    border: 1px solid #e5e7eb;
                    border-radius: 12px;
                    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.18);
                    z-index: 1100;
                    padding: 6px;
                }

                .report-container .sr-top-select-list {
                    max-height: 240px;
                    overflow-y: auto;
                    overflow-x: hidden;
                    -webkit-overflow-scrolling: touch;
                    border-radius: 10px;
                    background: #fff;
                    clip-path: inset(0 round 10px);
                }

                .report-container .sr-top-period-option {
                    width: 100%;
                    padding: 12px 14px;
                    display: flex;
                    align-items: center;
                    justify-content: space-between;
                    cursor: pointer;
                    font-size: 14px;
                    font-weight: 500;
                    border: none;
                    background: transparent;
                    color: #000;
                    text-align: left;
                }

                .report-container .sr-top-period-option:hover {
                    background: #f5f5f5;
                }

                .report-container .sr-top-period-option.active {
                    background: #f5f5f5;
                    font-weight: 600;
                }

                .report-container .sales-date-picker-overlay {
                    position: fixed;
                    inset: 0;
                    background: rgba(17, 24, 39, 0.35);
                    backdrop-filter: blur(4px);
                    -webkit-backdrop-filter: blur(4px);
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    z-index: 1600;
                    padding: 1rem;
                }

                .report-container .sales-date-picker-popup {
                    width: min(600px, 92vw) !important;
                    max-width: none !important;
                    max-height: 90vh;
                    overflow: auto;
                    background: #fff;
                    border-radius: 12px;
                    padding: 16px;
                    box-shadow: 0 20px 40px rgba(0, 0, 0, 0.22);
                }

                @media (max-width: 600px) {
                    .report-container .sr-top-header {
                        padding: 0.3rem 0;
                    }

                    .report-container .sr-top-back-btn {
                        padding: 0.35rem;
                    }

                    .report-container .sr-top-title {
                        font-size: 20px;
                    }

                    .report-container .sr-top-picker {
                        margin-bottom: 0.85rem;
                        padding: 0.35rem 0.4rem;
                    }

                    .report-container .sr-top-period-trigger {
                        padding: 8px 10px;
                    }

                    .report-container .sr-top-period-label {
                        font-size: 0.95rem;
                    }

                    .report-container .sr-top-period-menu {
                        left: 40px;
                    }

                    .report-container .sales-date-picker-overlay {
                        padding: 12px;
                        align-items: center;
                        justify-content: center;
                    }

                    .report-container .sales-date-picker-popup {
                        width: min(420px, 94vw) !important;
                        max-width: 94vw !important;
                        max-height: 88vh;
                        border-radius: 12px;
                        padding: 14px;
                    }
                }

                `}
            </style>

            {/* Share / Export Toast */}
            {exportToast.visible && exportToast.message && exportToast.message.includes('Preparing') ? (
                <div style={{
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    backgroundColor: 'rgba(0, 0, 0, 0.5)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    zIndex: 999999,
                    backdropFilter: 'blur(4px)',
                    animation: 'fadeIn 0.2s ease-out'
                }} onClick={() => setExportToast({ visible: false, message: '', type: 'success' })}>
                    <div style={{
                        width: 'min(360px, 90vw)',
                        background: '#fff',
                        borderRadius: '12px',
                        boxShadow: '0 20px 60px rgba(0,0,0,0.18)',
                        padding: '30px 24px',
                        textAlign: 'center',
                        position: 'relative',
                        animation: 'scaleIn 0.3s cubic-bezier(0.34, 1.56, 0.64, 1)'
                    }} onClick={e => e.stopPropagation()}>
                        
                        {/* Close button */}
                        <button 
                            onClick={() => setExportToast({ visible: false, message: '', type: 'success' })} 
                            style={{
                                position: 'absolute',
                                top: '14px',
                                right: '14px',
                                background: 'none',
                                border: 'none',
                                fontSize: '20px',
                                cursor: 'pointer',
                                color: '#9ca3af',
                                lineHeight: 1,
                                padding: '4px'
                            }}
                        >
                            ✕
                        </button>

                        {/* Spinner */}
                        <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '20px' }}>
                            <div className="processing-spinner" style={{ margin: 0, width: '40px', height: '40px', border: '3px solid #f3f3f3', borderTop: '3px solid #000' }}></div>
                        </div>

                        {/* Text Content */}
                        <h3 style={{ fontSize: '17px', fontWeight: 700, color: '#111827', margin: '0 0 8px' }}>
                            Preparing Report
                        </h3>
                        <p style={{ color: '#4b5563', fontSize: '14px', margin: 0, lineHeight: 1.5 }}>
                            Generating your report PDF. Please wait...
                        </p>
                    </div>
                </div>
            ) : (
                exportToast.visible && (
                    <Toast 
                        message={exportToast.message} 
                        type={exportToast.type} 
                        duration={6000} 
                        onClose={() => setExportToast({ visible: false, message: '', type: 'success' })} 
                    />
                )
            )}

            {/* Revert Toast */}
            {revertToast.visible && (
                <div 
                    style={{
                        position: 'fixed',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        backgroundColor: 'rgba(0, 0, 0, 0.5)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        zIndex: 999999,
                        backdropFilter: 'blur(4px)',
                        animation: 'fadeIn 0.2s ease-out'
                    }}
                    onClick={() => setRevertToast(prev => ({ ...prev, visible: false }))}
                >
                    <div
                        style={{
                            width: 'min(380px, 90vw)',
                            background: '#fff',
                            borderRadius: '12px',
                            boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
                            padding: '24px',
                            textAlign: 'center'
                        }}
                        onClick={(e) => e.stopPropagation()}
                    >
                        {/* Icon — a failure must not wear the success mark. This was a green
                            tick for every outcome, so a refused revert looked, at a glance,
                            exactly like a completed one. */}
                        <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '16px' }}>
                            <div style={{
                                width: '60px',
                                height: '60px',
                                borderRadius: '50%',
                                border: `3px solid ${revertToast.type === 'error' ? '#ef4444' : '#22c55e'}`,
                                backgroundColor: 'white',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center'
                            }}>
                                {revertToast.type === 'error'
                                    ? <X size={36} color="#ef4444" strokeWidth={3} />
                                    : <Check size={36} color="#22c55e" strokeWidth={3} />}
                            </div>
                        </div>
                        
                        {/* Message — the server's own words. Long refusals ("part of this batch
                            has already been used...") need room and must not be clipped. */}
                        <div style={{
                            fontSize: revertToast.type === 'error' ? '15px' : '18px',
                            fontWeight: '600',
                            marginBottom: '16px',
                            color: '#1f2937',
                            lineHeight: 1.5,
                            whiteSpace: 'pre-wrap',
                            wordBreak: 'break-word'
                        }}>
                            {revertToast.message?.replace('✅ ', '').replace('❌ ', '') || (revertToast.type === 'success' ? 'Stock-in reverted successfully!' : 'Failed to revert stock-in')}
                        </div>
                        
                        {/* Close Button */}
                        <button
                            onClick={() => setRevertToast(prev => ({ ...prev, visible: false }))}
                            style={{
                                padding: '10px 24px',
                                backgroundColor: '#000000',
                                color: '#fff',
                                border: 'none',
                                borderRadius: '8px',
                                fontSize: '14px',
                                fontWeight: '600',
                                cursor: 'pointer',
                                transition: 'all 0.2s'
                            }}
                            onMouseEnter={(e) => {
                                e.target.style.transform = 'scale(1.05)';
                                e.target.style.backgroundColor = '#000';
                            }}
                            onMouseLeave={(e) => {
                                e.target.style.transform = 'scale(1)';
                                e.target.style.backgroundColor = '#000000';
                            }}
                        >
                            OK
                        </button>
                    </div>
                </div>
            )}

            {/* Revert Confirmation Modal */}
            {revertConfirm.visible && (
                <div 
                    style={{
                        position: 'fixed',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        backgroundColor: 'rgba(0, 0, 0, 0.6)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        zIndex: 999999,
                        backdropFilter: 'blur(4px)',
                        animation: 'fadeIn 0.2s ease-out'
                    }}
                    onClick={() => {
                        if (revertLoading) return;
                        setRevertConfirm({ visible: false, stockInId: null, record: null });
                        setRevertSelectAll(false);
                        setRevertSelected({});
                    }}
                >
                    <div
                        className="revert-confirm-modal-box"
                        style={{
                            maxWidth: '560px',
                            maxHeight: '90vh',
                            overflow: 'auto'
                        }}
                        onClick={(e) => e.stopPropagation()}
                    >
                        {/* Warning Icon */}
                        <div style={{
                            fontSize: '48px',
                            textAlign: 'center',
                            marginBottom: '12px'
                        }}>
                            ⚠️
                        </div>
                        
                        {/* Title */}
                        <h3 style={{
                            margin: '0 0 16px 0',
                            fontSize: '20px',
                            fontWeight: '700',
                            color: '#111',
                            textAlign: 'center'
                        }}>
                            Are you sure you want to revert this {revertConfirm.record?.is_negative_fix ? 'negative stock fix' : 'stock-in'}?
                        </h3>
                        
                        {/* Items Table */}
                        {revertConfirm.record && (
                            <div style={{
                                border: '1px solid #e5e7eb',
                                borderRadius: '12px',
                                overflow: 'auto',
                                maxHeight: '50vh',
                                marginBottom: '24px'
                            }}>
                                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
                                    <thead>
                                        <tr style={{ background: '#ffffff', borderBottom: '1px solid #e5e7eb' }}>
                                            <th style={{ padding: '10px 12px', textAlign: 'left', fontWeight: 600, color: '#6b7280', fontSize: '11px', width: '120px' }}>
                                                <label style={{ display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer', whiteSpace: 'nowrap' }}>
                                                    <input
                                                        type="checkbox"
                                                        disabled={revertLoading}
                                                        checked={revertSelectAll}
                                                        onChange={(e) => {
                                                            if (revertLoading) return;
                                                            const checked = e.target.checked;
                                                            const items = revertConfirm.record?.line_items || [];
                                                            const nextSelected = {};
                                                            if (checked) {
                                                                items.forEach((_, idx) => {
                                                                    nextSelected[idx] = true;
                                                                });
                                                            }
                                                            setRevertSelectAll(checked);
                                                            setRevertSelected(nextSelected);
                                                        }}
                                                        style={{
                                                            width: '16px',
                                                            height: '16px',
                                                            cursor: revertLoading ? 'not-allowed' : 'pointer',
                                                            accentColor: '#000000'
                                                        }}
                                                    />
                                                    <span>Select All</span>
                                                </label>
                                            </th>
                                            <th style={{ padding: '10px 12px', textAlign: 'left', fontWeight: 600, color: '#6b7280', fontSize: '11px' }}>ITEM NAME</th>
                                            <th style={{ padding: '10px 12px', textAlign: 'left', fontWeight: 600, color: '#6b7280', fontSize: '11px', width: '70px' }}>QTY</th>
                                            <th style={{ padding: '10px 12px', textAlign: 'left', fontWeight: 600, color: '#6b7280', fontSize: '11px', width: '90px' }}>TOTAL</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {(revertConfirm.record.line_items || []).map((item, itemIndex) => {
                                            const isEditingThis = editingLineItemId === item.line_item_id;
                                            const isRowEditable = !revertConfirm.record?.is_negative_fix && (!isStockInTsrLocked(revertConfirm.record) || !!unlockedRecords[revertConfirm.stockInId]);
                                            
                                            if (isEditingThis) {
                                                return (
                                                    <tr key={itemIndex} style={{ borderBottom: '1px solid #f3f4f6', background: '#f9fafb' }}>
                                                        <td style={{ padding: '10px 12px' }}></td>
                                                        <td style={{ padding: '10px 12px', color: '#111', fontWeight: 500, textAlign: 'left', textTransform: 'capitalize' }}>
                                                            {item.mapped_item_name || item.item_name}
                                                            <div style={{ fontSize: '11px', color: '#6b7280' }}>
                                                                Unit: {item.unit_symbol || item.unit_name || ''}
                                                            </div>
                                                        </td>
                                                        <td style={{ padding: '10px 12px' }}>
                                                            <input
                                                                type="number"
                                                                step="any"
                                                                value={editQuantity}
                                                                onChange={(e) => setEditQuantity(e.target.value)}
                                                                style={{
                                                                    width: '70px',
                                                                    padding: '4px 6px',
                                                                    border: '1px solid #ddd',
                                                                    borderRadius: '4px',
                                                                    fontSize: '13px'
                                                                }}
                                                                placeholder="Qty"
                                                            />
                                                        </td>
                                                        <td style={{ padding: '10px 12px' }}>
                                                            <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                                                                <div style={{ display: 'flex', alignItems: 'center', gap: '4px', fontSize: '11px', color: '#666' }}>
                                                                    <span>Rate:</span>
                                                                    <input
                                                                        type="number"
                                                                        step="any"
                                                                        value={editUnitPrice}
                                                                        onChange={(e) => setEditUnitPrice(e.target.value)}
                                                                        style={{
                                                                            width: '70px',
                                                                            padding: '4px 6px',
                                                                            border: '1px solid #ddd',
                                                                            borderRadius: '4px',
                                                                            fontSize: '13px'
                                                                        }}
                                                                        placeholder="Rate"
                                                                    />
                                                                </div>
                                                                <span style={{ fontWeight: 600, color: '#111' }}>
                                                                    ₹{(parseFloat(editQuantity || 0) * parseFloat(editUnitPrice || 0)).toFixed(2)}
                                                                </span>
                                                            </div>
                                                        </td>
                                                        <td style={{ padding: '10px 12px', textAlign: 'center' }}>
                                                            <div style={{ display: 'flex', gap: '6px', justifyContent: 'center' }}>
                                                                <button
                                                                    onClick={() => handleSaveLineItem(item)}
                                                                    disabled={editSaving}
                                                                    style={{
                                                                        padding: '4px 8px',
                                                                        backgroundColor: '#10b981',
                                                                        color: '#fff',
                                                                        border: 'none',
                                                                        borderRadius: '4px',
                                                                        fontSize: '11px',
                                                                        fontWeight: 600,
                                                                        cursor: 'pointer'
                                                                    }}
                                                                >
                                                                    {editSaving ? '...' : 'Save'}
                                                                </button>
                                                                <button
                                                                    onClick={() => {
                                                                        setEditingLineItemId(null);
                                                                        setEditQuantity('');
                                                                        setEditUnitPrice('');
                                                                    }}
                                                                    disabled={editSaving}
                                                                    style={{
                                                                        padding: '4px 8px',
                                                                        backgroundColor: '#ef4444',
                                                                        color: '#fff',
                                                                        border: 'none',
                                                                        borderRadius: '4px',
                                                                        fontSize: '11px',
                                                                        fontWeight: 600,
                                                                        cursor: 'pointer'
                                                                    }}
                                                                >
                                                                    Cancel
                                                                </button>
                                                            </div>
                                                        </td>
                                                    </tr>
                                                );
                                            }

                                            return (
                                                <tr key={itemIndex} style={{ borderBottom: '1px solid #f3f4f6' }}>
                                                    <td style={{ padding: '10px 12px', textAlign: 'left' }}>
                                                        <input
                                                            type="checkbox"
                                                            disabled={revertLoading}
                                                            checked={!!revertSelected[itemIndex]}
                                                            onChange={(e) => {
                                                                if (revertLoading) return;
                                                                const checked = e.target.checked;
                                                                setRevertSelected(prev => {
                                                                    const next = { ...prev, [itemIndex]: checked };
                                                                    const items = revertConfirm.record?.line_items || [];
                                                                    const allChecked = items.length > 0 && items.every((_, idx) => next[idx]);
                                                                    setRevertSelectAll(allChecked);
                                                                    return next;
                                                                });
                                                            }}
                                                            style={{
                                                                width: '16px',
                                                                height: '16px',
                                                                cursor: revertLoading ? 'not-allowed' : 'pointer',
                                                                accentColor: '#000000'
                                                            }}
                                                        />
                                                    </td>
                                                    <td style={{ padding: '10px 12px', color: '#111', fontWeight: 500, textAlign: 'left', textTransform: 'capitalize' }}>
                                                        {item.mapped_item_name || item.item_name}
                                                    </td>
                                                    <td style={{ padding: '10px 12px', color: '#111', textAlign: 'left' }}>
                                                        {formatQuantity(item.quantity, item.unit_symbol || item.unit_name)}
                                                    </td>
                                                    <td style={{ padding: '10px 12px', color: '#111', textAlign: 'left', fontWeight: 600 }}>
                                                        ₹{getLineItemDisplayTotal(item).toFixed(2)}
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                        {(!revertConfirm.record.line_items || revertConfirm.record.line_items.length === 0) && (
                                            <tr>
                                                <td colSpan={4} style={{ padding: '16px', textAlign: 'center', color: '#9ca3af', fontSize: '13px' }}>
                                                    No items found
                                                </td>
                                            </tr>
                                        )}
                                    </tbody>
                                </table>
                            </div>
                        )}
                        
                        {/* Buttons */}
                        <div style={{
                            display: 'flex',
                            gap: '12px',
                            justifyContent: 'center'
                        }}>
                            <button
                                disabled={revertLoading}
                                onClick={() => {
                                    if (revertLoading) return;
                                    setRevertConfirm({ visible: false, stockInId: null, record: null });
                                    setRevertSelectAll(false);
                                    setRevertSelected({});
                                }}
                                style={{
                                    backgroundColor: '#e5e7eb',
                                    color: '#374151',
                                    border: 'none',
                                    padding: '14px 32px',
                                    borderRadius: '10px',
                                    fontSize: '16px',
                                    fontWeight: '600',
                                    cursor: revertLoading ? 'not-allowed' : 'pointer',
                                    opacity: revertLoading ? 0.6 : 1,
                                    transition: 'all 0.2s',
                                    minWidth: '120px',
                                    maxWidth: '50%',
                                    width: '50%'
                                }}
                                onMouseEnter={(e) => {
                                    if (!revertLoading) {
                                        e.target.style.transform = 'scale(1.05)';
                                        e.target.style.backgroundColor = '#d1d5db';
                                    }
                                }}
                                onMouseLeave={(e) => {
                                    if (!revertLoading) {
                                        e.target.style.transform = 'scale(1)';
                                        e.target.style.backgroundColor = '#e5e7eb';
                                    }
                                }}
                            >
                                Cancel
                            </button>
                            <button
                                disabled={revertLoading}
                                onClick={async () => {
                                    if (revertLoading) return;
                                    try {
                                        // Get selected line_item_ids
                                        const selectedLineItemIds = [];
                                        const items = revertConfirm.record?.line_items || [];
                                        
                                        items.forEach((item, idx) => {
                                            if (revertSelected[idx]) {
                                                selectedLineItemIds.push(item.line_item_id);
                                            }
                                        });
                                        
                                        if (selectedLineItemIds.length === 0) {
                                            setRevertToast({ visible: true, message: 'Please select at least one item to revert', type: 'error' });
                                            return;
                                        }
                                        
                                        setRevertLoading(true);

                                        // Call revert with selected line items
                                        const pin = unlockedRecords[revertConfirm.stockInId] || null;
                                        const result = await StockInService.revertStockIn(revertConfirm.stockInId, selectedLineItemIds, pin);

                                        // services/httpClient.js RESOLVES on 403 instead of throwing, so every
                                        // refusal the server makes with that status — a wrong admin PIN, a TSR
                                        // lock, a business mismatch —
                                        // arrived here as an ordinary value. The code below then announced
                                        // "Stock-in reverted successfully!" for a revert that never happened,
                                        // which is worse than the error it was hiding: the stock is still there
                                        // and the owner has been told it is gone.
                                        if (result && result.success === false) {
                                            throw Object.assign(new Error(revertErrorMessage(result)), { data: result });
                                        }

                                        setRevertConfirm({ visible: false, stockInId: null, record: null });
                                        setRevertSelectAll(false);
                                        setRevertSelected({});
                                        
                                        const message = selectedLineItemIds.length === items.length
                                            ? 'Stock-in reverted successfully!'
                                            : `${selectedLineItemIds.length} item(s) reverted successfully!`;
                                        
                                        setRevertToast({ visible: true, message, type: 'success' });
                                        fetchExpenseDetails();
                                        setRefreshTrigger(prev => prev + 1);
                                    } catch (err) {
                                        // `err.response.data.error` is the AXIOS shape. This call goes through
                                        // services/httpClient.js, a fetch wrapper that attaches the body flat as
                                        // `err.data` and never sets `.response` — so that read was undefined on
                                        // every single failure and the generic fallback below was the only thing
                                        // the user ever saw, whatever the server actually said. revertErrorMessage
                                        // understands both shapes and surfaces `details` for a generic 500.
                                        const errorMsg = revertErrorMessage(err);
                                        setRevertConfirm({ visible: false, stockInId: null, record: null });
                                        setRevertSelectAll(false);
                                        setRevertSelected({});
                                        setRevertToast({ visible: true, message: errorMsg, type: 'error' });
                                        console.error('Revert error:', err);
                                        // A 409 means the list is stale — the bill or some of its items were
                                        // already reverted (another tab, a double click), or a closing count
                                        // started. Reload so the list and the Expense card match the database.
                                        if (err && err.status === 409) {
                                            fetchExpenseDetails();
                                            setRefreshTrigger(prev => prev + 1);
                                        }
                                    } finally {
                                        setRevertLoading(false);
                                    }
                                }}
                                style={{
                                    backgroundColor: '#111111',
                                    color: '#ffffff',
                                    border: 'none',
                                    padding: '14px 32px',
                                    borderRadius: '10px',
                                    fontSize: '16px',
                                    fontWeight: '600',
                                    cursor: revertLoading ? 'not-allowed' : 'pointer',
                                    opacity: revertLoading ? 0.7 : 1,
                                    transition: 'all 0.2s',
                                    minWidth: '120px',
                                    maxWidth: '50%',
                                    width: '50%',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    gap: '8px'
                                }}
                                onMouseEnter={(e) => {
                                    if (!revertLoading) {
                                        e.target.style.transform = 'scale(1.05)';
                                        e.target.style.backgroundColor = '#000000';
                                    }
                                }}
                                onMouseLeave={(e) => {
                                    if (!revertLoading) {
                                        e.target.style.transform = 'scale(1)';
                                        e.target.style.backgroundColor = '#111111';
                                    }
                                }}
                            >
                                {revertLoading ? (
                                    <>
                                        <FontAwesomeIcon icon={faSpinner} spin style={{ fontSize: '16px' }} />
                                        <span>Reverting...</span>
                                    </>
                                ) : (
                                    'OK'
                                )}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Wastage Revert Confirmation Modal */}
            {wastageRevertConfirm.visible && (
                <div 
                    style={{
                        position: 'fixed',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        backgroundColor: 'rgba(0, 0, 0, 0.6)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        zIndex: 999999,
                        backdropFilter: 'blur(4px)',
                        animation: 'fadeIn 0.2s ease-out'
                    }}
                    onClick={() => {
                        setWastageRevertConfirm({ visible: false, stockOutId: null, itemName: '' });
                    }}
                >
                    <div
                        className="revert-confirm-modal-box"
                        style={{
                            maxWidth: '480px',
                            textAlign: 'center'
                        }}
                        onClick={(e) => e.stopPropagation()}
                    >
                        {/* Warning Icon */}
                        <div style={{
                            fontSize: '48px',
                            marginBottom: '16px'
                        }}>
                            ⚠️
                        </div>
                        
                        {/* Title */}
                        <h3 style={{
                            margin: '0 0 16px 0',
                            fontSize: '20px',
                            fontWeight: '700',
                            color: '#111',
                            lineHeight: '1.4'
                        }}>
                            Are you sure you want to revert the wastage record for "{wastageRevertConfirm.itemName}"?
                        </h3>
                        
                        {/* Subtext */}
                        <p style={{
                            margin: '0 0 24px 0',
                            fontSize: '14px',
                            color: '#6b7280',
                            lineHeight: '1.5'
                        }}>
                            This will restore the deducted inventory.
                        </p>

                        {wastageRevertConfirm.banquet && (
                            <p style={{
                                margin: '-12px 0 24px 0',
                                fontSize: '14px',
                                color: '#92400e',
                                background: '#fef3c7',
                                borderRadius: '8px',
                                padding: '10px 12px',
                                lineHeight: '1.5'
                            }}>
                                {banquetRevertNote(wastageRevertConfirm.banquet)}
                            </p>
                        )}

                        {/* Action Buttons */}
                        <div style={{
                            display: 'flex',
                            gap: '12px',
                            justifyContent: 'center'
                        }}>
                            <button
                                onClick={() => {
                                    setWastageRevertConfirm({ visible: false, stockOutId: null, itemName: '' });
                                }}
                                style={{
                                    backgroundColor: '#e5e7eb',
                                    color: '#1f2937',
                                    border: 'none',
                                    padding: '14px 24px',
                                    borderRadius: '10px',
                                    fontSize: '16px',
                                    fontWeight: '600',
                                    cursor: 'pointer',
                                    transition: 'all 0.2s',
                                    flex: 1
                                }}
                                onMouseEnter={(e) => {
                                    e.target.style.backgroundColor = '#d1d5db';
                                }}
                                onMouseLeave={(e) => {
                                    e.target.style.backgroundColor = '#e5e7eb';
                                }}
                            >
                                Cancel
                            </button>
                            <button
                                onClick={executeWastageRevert}
                                style={{
                                    backgroundColor: '#111111',
                                    color: '#ffffff',
                                    border: 'none',
                                    padding: '14px 24px',
                                    borderRadius: '10px',
                                    fontSize: '16px',
                                    fontWeight: '600',
                                    cursor: 'pointer',
                                    transition: 'all 0.2s',
                                    flex: 1
                                }}
                                onMouseEnter={(e) => {
                                    e.target.style.backgroundColor = '#000000';
                                }}
                                onMouseLeave={(e) => {
                                    e.target.style.backgroundColor = '#111111';
                                }}
                            >
                                Confirm
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Admin PIN Verification Modal */}
            {showPinModal && (
                <div style={{
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    backgroundColor: 'rgba(0,0,0,0.5)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    zIndex: 10000
                }}>
                    <div style={{
                        backgroundColor: '#fff',
                        padding: '24px',
                        borderRadius: '12px',
                        width: '320px',
                        boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
                        textAlign: 'center'
                    }}>
                        <h3 style={{ margin: '0 0 12px 0', fontSize: '18px', fontWeight: 700, color: '#333' }}>Enter Admin PIN</h3>
                        <p style={{ margin: '0 0 20px 0', fontSize: '13px', color: '#666', lineHeight: 1.4 }}>
                            Sales for this date are already submitted. Enter Admin PIN to unlock editing and reverting.
                        </p>
                        <input
                            type="password"
                            value={enteredPin}
                            onChange={(e) => {
                                setEnteredPin(e.target.value);
                                setPinError('');
                            }}
                            placeholder="PIN"
                            maxLength={8}
                            style={{
                                width: '100%',
                                padding: '10px 12px',
                                fontSize: '18px',
                                letterSpacing: '4px',
                                textAlign: 'center',
                                border: '1px solid #ddd',
                                borderRadius: '8px',
                                marginBottom: '12px',
                                outline: 'none'
                            }}
                            autoFocus
                            onKeyDown={(e) => {
                                if (e.key === 'Enter') {
                                    handleVerifyPin();
                                }
                            }}
                        />
                        {pinError && (
                            <div style={{ color: '#dc2626', fontSize: '12px', marginBottom: '12px', fontWeight: 500 }}>
                                {pinError}
                            </div>
                        )}
                        <div style={{ display: 'flex', gap: '8px', justifyContent: 'center' }}>
                            <button
                                onClick={() => {
                                    setShowPinModal(false);
                                    setPinModalRecord(null);
                                    setEnteredPin('');
                                    setPinError('');
                                }}
                                style={{
                                    padding: '8px 16px',
                                    border: '1px solid #ddd',
                                    borderRadius: '6px',
                                    background: '#fff',
                                    cursor: 'pointer',
                                    fontSize: '14px',
                                    color: '#333'
                                }}
                            >
                                Cancel
                            </button>
                            <button
                                onClick={handleVerifyPin}
                                disabled={pinVerifying}
                                style={{
                                    padding: '8px 16px',
                                    border: 'none',
                                    borderRadius: '6px',
                                    background: '#000',
                                    color: '#fff',
                                    cursor: 'pointer',
                                    fontSize: '14px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '6px'
                                }}
                            >
                                {pinVerifying && <FontAwesomeIcon icon={faSpinner} spin />}
                                Verify
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/*
              PIECE GUARD — the edit door's popup.
              It proposes; a human confirms; the corrected number goes back through the
              SAME server-side ladder as fresh input. Nothing here applies a correction
              on its own, for the same reason nothing on the submit form does: a silent
              ×4 that guesses wrong creates stock that does not physically exist.
            */}
            {/* ── M6 — THE EDIT DOOR CAN DEFINE A PIECE ────────────────────────────
                The backend edit route can and does emit `piece_profile_required`: an
                edited pcs line on an item that has never had a profile hits §6.1 like any
                other. This screen rendered PiecePopupFor and never PieceProfileModal, and
                PiecePopupFor has no case for that code — so the person was told the piece
                must be defined, given a "Back to the form" button, and the line could
                NEVER BE SAVED FROM THIS SCREEN AT ALL.

                It reuses the same modal and the same shared apply-result function as the
                submit door (P3.4), so the "already pieces / packets" question behaves
                identically in all three places. */}
            {piecePopup?.blocker?.code === 'piece_profile_required' && (
                <PieceProfileModal
                    isOpen
                    itemId={piecePopup.itemId}
                    itemName={piecePopup.itemName || 'this item'}
                    category={piecePopup.category}
                    enteredQuantity={piecePopup.quantity}
                    enteredTotal={piecePopup.quantity * piecePopup.unitCost}
                    suggestedPiecesPerPack={piecePopup.blocker.suggested_pieces_per_pack}
                    rawItemName={piecePopup.itemName}
                    saving={editSaving}
                    error={pieceProfileError}
                    onSave={async (data) => {
                        setEditSaving(true);
                        setPieceProfileError(null);
                        try {
                            const businessId = localStorage.getItem('businessId');
                            const saved = await http.post(
                                `/stock-in/v2/piece-profiles?businessId=${businessId}`,
                                { ...data, item_id: piecePopup.itemId, businessId }
                            );
                            if (!saved?.success) throw new Error(saved?.error || 'Could not save the piece definition');

                            // P3.4 — the SAME rule the submit door uses. The line total is
                            // what the bill says was paid and never moves; a corrected
                            // COUNT moves the unit cost, not the money.
                            const lineTotal = piecePopup.quantity * piecePopup.unitCost;
                            const applied = applyPieceProfileResult(data, {
                                quantity: piecePopup.quantity,
                                total_price: lineTotal,
                            });

                            const pin = unlockedRecords[piecePopup.stockInId] || null;
                            const res = await http.put(
                                `/stock-in/v2/${piecePopup.stockInId}/line-items/${piecePopup.lineItemId}`,
                                {
                                    quantity: applied.quantity,
                                    unit_cost: applied.unitPrice != null ? applied.unitPrice : piecePopup.unitCost,
                                    ...(pin ? { pin } : {})
                                }
                            );
                            if (res.success) {
                                setPiecePopup(null);
                                setEditingLineItemId(null);
                                setRevertToast({ visible: true, message: 'Line item updated successfully!', type: 'success' });
                                fetchExpenseDetails();
                                setRefreshTrigger(prev => prev + 1);
                            } else {
                                setRevertToast({ visible: true, message: res.error || 'Failed to update line item', type: 'error' });
                            }
                        } catch (e) {
                            // The definition may be saved and the line may still trip the
                            // ladder — keep the conversation open rather than dropping the
                            // edit and losing the definition the person just typed.
                            if (e.data?.piece_blocker) {
                                setPiecePopup(prev => prev && { ...prev, blocker: { ...e.data.piece_blocker, index: 0 } });
                            } else {
                                setPieceProfileError(e.data?.error || e.message || 'Could not save the piece definition');
                            }
                        } finally {
                            setEditSaving(false);
                        }
                    }}
                    onCancel={() => { setPiecePopup(null); setPieceProfileError(null); }}
                />
            )}

            {piecePopup && piecePopup.blocker?.code !== 'piece_profile_required' && (
                <PiecePopupFor
                    blocker={piecePopup.blocker}
                    items={[{ item_name: piecePopup.itemName, quantity: piecePopup.quantity, total_price: piecePopup.quantity * piecePopup.unitCost }]}
                    onResolve={async (token) => {
                        try {
                            setEditSaving(true);
                            // "Yes — it was a packet" changes the COUNT. The line total was
                            // never in dispute, so the unit cost has to move with it or the
                            // edit would silently multiply what the bill said was paid.
                            const corrected = token.type === 'piece_pack_corrected' && Number(token.quantity) > 0;
                            const newQty = corrected ? Number(token.quantity) : piecePopup.quantity;
                            const lineTotal = piecePopup.quantity * piecePopup.unitCost;
                            const newCost = corrected ? lineTotal / newQty : piecePopup.unitCost;

                            const pin = unlockedRecords[piecePopup.stockInId] || null;
                            const res = await http.put(
                                `/stock-in/v2/${piecePopup.stockInId}/line-items/${piecePopup.lineItemId}`,
                                {
                                    quantity: newQty,
                                    unit_cost: newCost,
                                    piece_confirmation: token,
                                    ...(pin ? { pin } : {})
                                }
                            );
                            if (res.success) {
                                setPiecePopup(null);
                                setEditingLineItemId(null);
                                setRevertToast({ visible: true, message: 'Line item updated successfully!', type: 'success' });
                                fetchExpenseDetails();
                                setRefreshTrigger(prev => prev + 1);
                            } else {
                                setRevertToast({ visible: true, message: res.error || 'Failed to update line item', type: 'error' });
                            }
                        } catch (e) {
                            // A corrected count can itself trip the ladder — keep the
                            // conversation open rather than dropping the edit.
                            if (e.data?.piece_blocker) {
                                setPiecePopup(prev => prev && { ...prev, blocker: { ...e.data.piece_blocker, index: 0 } });
                            } else {
                                setPiecePopup(null);
                                setRevertToast({ visible: true, message: e.data?.error || e.message || 'Failed to save changes', type: 'error' });
                            }
                        } finally {
                            setEditSaving(false);
                        }
                    }}
                    onCancel={() => setPiecePopup(null)}
                />
            )}

        </>
    );
};

export default SalesReport;